import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  User as UserIcon,
  Palette,
  Plug,
  Bot,
  Bell,
  Database,
  ShieldCheck,
  KeyRound,
  Cog,
  Layers,
  Mic,
  Info,
  Cloud,
  HardDrive,
  Sparkles,
  ArrowRight,
  ListChecks,
  GitMerge,
  TrendingUp,
  Megaphone,
  Bug,
  Shield,
  Zap,
  Wand2,
  Code,
  BookOpen,
  Rocket,
  MessageCircle,
  Mail as MailIcon,
  Users as UsersIcon,
  RefreshCw,
  FileText,
  Eye,
  Plus,
  Trash2,
  Copy,
  Smartphone,
  AlertTriangle,
  Inbox,
  Ban,
  type LucideIcon,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";
import { SuppressedSendersSection } from "../components/SuppressedSendersSection";
import { useAuth, refreshAuth, getCsrfToken } from "../lib/auth";
import { WorkConnectorsPanel } from "../anchors/settings/connections/WorkConnectorsPanel";
import MobileSection from "../anchors/settings/mobile/MobileSection";
import { ConnectorCatalog } from "../components/ConnectorCatalog";
import ConnectorsManager from "../components/ConnectorsManager";
import { ThemePickerPanel } from "../components/ThemePicker";
import { Markdown } from "../components/Markdown";
import { formatAbsolute, formatRelative } from "../lib/formatRelative";
import { api, setAgentOverride, type VersionPayload, type AgentSummary, type ConnectorsResponse, type ViewDef, type ScopeDef, type GmailTriageStatus, type GmailTriageSettings, type GmailTriageAction, type GmailTriageFingerprint, type AdminResetResult, type AdminSnapshot } from "../lib/api";
import { TeachingBanner } from "../components/TeachingBanner";
import { L2RailShell, type L2NavItem } from "../components/L2Nav";
import { Breadcrumb } from "../components/Breadcrumb";
import { useView } from "../lib/viewContext";
import { BUILT_VERSION } from "../lib/version";
import { useChangelog, setLastSeen, type ChangelogEntry } from "../lib/changelog";

interface SettingsSection {
  id: string;
  label: string;
  Icon: LucideIcon;
  /** One-line context for the mobile-list view. Shown under the
   *  label so the user can scan the list without tapping into each
   *  section. Skipped on desktop where the sidebar is always
   *  collapsed-list-of-labels. */
  hint: string;
  ready: boolean;
}

const SECTIONS: SettingsSection[] = [
  { id: "account", label: "Account", Icon: UserIcon, hint: "Profile, sign-out, restart onboarding", ready: true },
  { id: "appearance", label: "Appearance", Icon: Palette, hint: "Visual theme, light / dark mode", ready: true },
  { id: "connectors", label: "Connectors", Icon: Plug, hint: "Gmail, Calendar, Outlook, Teams", ready: true },
  { id: "gmail-triage", label: "Gmail triage", Icon: Megaphone, hint: "Auto-archive bulk promo mail, safely + reversibly", ready: true },
  { id: "inbox-cleanup", label: "Inbox Cleanup", Icon: Inbox, hint: "Archive + label promos, receipts; review candidates before approving", ready: true },
  { id: "mobile", label: "Mobile", Icon: Smartphone, hint: "Pair the phone app: location, battery, network", ready: true },
  { id: "agents", label: "Agents", Icon: Bot, hint: "Per-agent model overrides + cost ledger", ready: true },
  { id: "notifications", label: "Notifications", Icon: Bell, hint: "Push: morning brief, monitor matches, digests", ready: true },
  { id: "data", label: "Data & vault", Icon: Database, hint: "Backup, vault path, export", ready: true },
  { id: "privacy", label: "Privacy & scopes", Icon: ShieldCheck, hint: "Scope mappings + privacy gate audit", ready: true },
  { id: "views", label: "Views", Icon: Eye, hint: "Macro privacy views: bundle scopes into one selector", ready: true },
  { id: "suppressed-senders", label: "Suppressed senders", Icon: Ban, hint: "Senders you hid or kept skipping; reverse any of them", ready: true },
  { id: "secrets", label: "API tokens", Icon: KeyRound, hint: "Issue + revoke per-user bearer tokens", ready: true },
  { id: "mcp", label: "MCP clients", Icon: KeyRound, hint: "Pair Claude / ChatGPT / agents; per-client resource grants", ready: true },
  { id: "ai-providers", label: "AI", Icon: Sparkles, hint: "Provider keys + per-feature model bindings", ready: true },
  { id: "pipeline", label: "Pipeline queue", Icon: Cog, hint: "Capture-pipeline backlog + recovery", ready: true },
  { id: "logs", label: "Logs", Icon: Layers, hint: "Agent runs, connector runs, monitor matches", ready: true },
  { id: "whatsnew", label: "What's new", Icon: Megaphone, hint: "Recent deploys + changelog", ready: true },
  { id: "about", label: "About", Icon: Info, hint: "Version + build info", ready: true },
  { id: "admin", label: "Admin", Icon: AlertTriangle, hint: "Reset all user data + snapshot restore", ready: true },
  { id: "workers", label: "Workers", Icon: Cog, hint: "Worker fleet health: status, last tick, errors, pause / run-now", ready: true },
];

/** Resolve the active section from URL state. Priority:
 *    1. /settings/:section path param (canonical)
 *    2. ?section=<id> query param (legacy from the WhatsNew toast
 *       deep-link in earlier commits + external bookmarks)
 *    3. null on mobile (shows the section list); first ready section
 *       on desktop (always has content in the right pane). */
function resolveSectionFromUrl(
  pathSection: string | undefined,
  search: string,
): string | null {
  if (pathSection && SECTIONS.some((s) => s.id === pathSection)) return pathSection;
  const params = new URLSearchParams(search);
  const qs = params.get("section");
  if (qs && SECTIONS.some((s) => s.id === qs)) return qs;
  return null;
}

export function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const { section: rawSection } = useParams<{ section?: string }>();
  // The connectors sub-page owns deep-link children
  // (/settings/connectors/<subsection>); those routes do not carry a
  // :section param, so resolve the section from the path directly.
  const pathSection =
    rawSection ??
    (/\/settings\/connectors(\/|$)/.test(location.pathname)
      ? "connectors"
      : undefined);
  // Layout-mode detection: the same component renders mobile + desktop.
  // On mobile (`< md`), an unset section shows the list; on desktop
  // we auto-select the first ready section so the right pane always
  // has content. Window media-query check picks up the right side
  // synchronously at mount so there's no flicker between list + pane.
  const isDesktop = useMediaQueryMatches("(min-width: 768px)");
  const initialSection = resolveSectionFromUrl(pathSection, location.search);
  const [active, setActive] = useState<string | null>(
    initialSection ?? (isDesktop ? "appearance" : null),
  );
  // Re-resolve when the URL changes (browser back, deep link from
  // toast, etc.). Honors the path param + legacy query string.
  useEffect(() => {
    const next = resolveSectionFromUrl(pathSection, location.search);
    if (next !== active) {
      setActive(next ?? (isDesktop ? "appearance" : null));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathSection, location.search]);

  // When the user clicks a section, advance the URL to the canonical
  // /settings/<id> path. PUSH (not replace) so the system back
  // button + edge-swipe gesture on Android Chrome PWA navigate to
  // the parent /settings list, matching native iOS Settings
  // behaviour. The in-app back chevron calls navigate(-1) so both
  // gestures behave identically.
  const navigateToSection = (id: string | null) => {
    setActive(id);
    const base = location.pathname.startsWith("/u/")
      ? location.pathname.replace(/\/settings(\/.*)?$/, "/settings")
      : "/settings";
    if (id) {
      // Drill-in: push so back returns to the list.
      navigate(`${base}/${id}`);
    } else {
      // Drill-out via the in-app chevron: history.back() so the
      // browser stack stays consistent with system back. If there's
      // no history (direct visit to /settings/foo then tap back),
      // navigate to the bare /settings as a fallback.
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate(base, { replace: true });
      }
    }
  };

  const currentSection = active ? SECTIONS.find((s) => s.id === active) ?? null : null;

  return (
    <div className="w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 py-6">
      <div className="mb-4">
        <Breadcrumb
          segments={
            currentSection
              ? [
                  { label: "Settings", href: "/settings" },
                  { label: currentSection.label },
                ]
              : [{ label: "Settings" }]
          }
        />
      </div>
      {/* Mobile header: when no section is active, just "Settings".
          When a section is active, render a back chevron + the
          section's label so the user can navigate back to the list
          one-tap. Desktop has the persistent sidebar so the back
          affordance is unnecessary there. The section title carries
          a brief "arrival flash" so the user gets visual confirmation
          their tap routed to the right place. */}
      <header className="mb-6 flex items-center gap-3">
        {!isDesktop && currentSection && (
          <button
            type="button"
            onClick={() => navigateToSection(null)}
            aria-label="Back to settings"
            className="flex-shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 active:bg-orange-500/10 dark:active:bg-orange-400/15 active:scale-[0.95] text-slate-700 dark:text-slate-200 transition-all duration-150 motion-reduce:transition-none"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}
        <h1
          key={currentSection ? `title-${currentSection.id}` : "title-root"}
          className={`text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100 truncate ${
            currentSection ? "animate-arrivalFlash motion-reduce:animate-none" : ""
          }`}
        >
          {currentSection ? currentSection.label : "Settings"}
        </h1>
      </header>

      <L2RailShell
        items={SECTIONS.map<L2NavItem>((s) => ({
          id: s.id,
          label: s.label,
          Icon: s.Icon,
          hint: s.hint,
          disabled: !s.ready,
        }))}
        activeId={currentSection ? currentSection.id : null}
        onPick={(id) => navigateToSection(id)}
        railAriaLabel="Settings sections"
        listAriaLabel="Settings sections"
        tint="slate"
        railTrailer={<SettingsModes />}
        mobileHeader={<TeachingBanner id="settings_overview" />}
      >
        <SectionRenderer active={(currentSection?.id ?? active) ?? "appearance"} />
      </L2RailShell>
    </div>
  );
}

/** "Modes" footer rendered below the Settings rail / mobile list.
 *  Lists the side-quest surfaces (Backlog, Reconciliation, Evolution)
 *  that historically lived next to the settings list but are not
 *  sections themselves. Same content + ordering on both viewports. */
function SettingsModes() {
  return (
    <div className="glass rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur px-2 py-3">
      <p className="px-3 pb-1 text-[10px] uppercase tracking-wider font-bold text-slate-400">
        Modes
      </p>
      <ul className="space-y-0.5">
        <ModeLink to="/backlog" Icon={ListChecks} label="Backlog" />
        <ModeLink to="/reconcile" Icon={GitMerge} label="Reconciliation" />
        <ModeLink to="/evolution" Icon={TrendingUp} label="Evolution" />
      </ul>
    </div>
  );
}

/** Tiny matchMedia hook that re-renders on viewport flip. Used to
 *  pick the right initial state without a 1-frame flicker between
 *  mobile-list and desktop-split. */
function useMediaQueryMatches(query: string): boolean {
  const [matches, setMatches] = useState<boolean>(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/** Render the section panel for a given id. Pulled out of the main
 *  component so both the mobile + desktop layouts share one
 *  switch-table. The big chain stays here because each section has
 *  its own component; switching to a registry would obscure the
 *  ready/Phase-2 fallback. */
function SectionRenderer({ active }: { active: string }) {
  switch (active) {
    case "appearance":
      return <AppearanceSection />;
    case "agents":
      return <AgentsSection />;
    case "connectors":
      return <ConnectorsSection />;
    case "gmail-triage":
      return <GmailTriageSection />;
    case "inbox-cleanup":
      return <InboxCleanupSection />;
    case "mobile":
      return <MobileSection />;
    case "mail":
      return <MailSettingsRedirect />;
    case "whatsnew":
      return <WhatsNewSection />;
    case "logs":
      return <LogsSection />;
    case "privacy":
      return <PrivacySection />;
    case "views":
      return <ViewsSection />;
    case "suppressed-senders":
      return <SuppressedSendersSection />;
    case "pipeline":
      return <PipelineSection />;
    case "secrets":
      return <ApiTokensSection />;
    case "mcp":
      return <McpSection />;
    case "data":
      return <DataAndVaultSection />;
    case "account":
      return <AccountSection />;
    case "about":
      return <AboutSection />;
    case "notifications":
      return <NotificationsSection />;
    case "ai-providers":
      return <AiProvidersSection />;
    case "admin":
      return <AdminDataSection />;
    case "workers":
      return <WorkersSection />;
    default:
      return <UnknownSection label={SECTIONS.find((s) => s.id === active)?.label ?? ""} />;
  }
}

type WorkerFleetRow = Awaited<
  ReturnType<typeof api.adminWorkersFleet>
>["workers"][number];

function workerStatusClass(status: string): string {
  switch (status) {
    case "running":
      return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
    case "idle":
      return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
    case "paused":
      return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
    case "errored":
      return "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300";
    case "gated_off":
      return "bg-slate-100 text-slate-400 dark:bg-slate-800 dark:text-slate-500";
    default:
      return "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400";
  }
}

function fmtWhen(s: string | null): string {
  if (!s) return "never";
  const t = Date.parse(s);
  if (!Number.isFinite(t)) return s;
  const ageMs = Date.now() - t;
  if (ageMs < 0) return new Date(t).toLocaleString();
  const m = Math.floor(ageMs / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

/** Step 29 follow-up: external bridge/sidecar health. Green = up,
 *  yellow = down but not configured on this box (so not a real
 *  degradation), red = configured and unreachable. */
function BridgesPanel() {
  const [bridges, setBridges] = useState<
    Awaited<ReturnType<typeof api.workConnectorsBridgeHealth>>["bridges"]
  >([]);
  const [err, setErr] = useState<string | null>(null);
  const [recycling, setRecycling] = useState(false);
  const [recycleMsg, setRecycleMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.workConnectorsBridgeHealth();
      setBridges(r.bridges);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  const recycleCom = useCallback(async () => {
    setRecycling(true);
    setRecycleMsg(null);
    try {
      const r = await api.workConnectorsRecycleCom();
      if (r.ok && r.recycle) {
        const b = r.recycle.before?.handle_count;
        const a = r.recycle.after?.handle_count;
        setRecycleMsg(
          a != null && b != null
            ? `Recycled. Handles ${b.toLocaleString()} to ${a.toLocaleString()}.`
            : "Recycled COM session.",
        );
      } else {
        setRecycleMsg(r.error || "Recycle failed.");
      }
    } catch (e) {
      setRecycleMsg((e as Error).message);
    } finally {
      setRecycling(false);
      void load();
    }
  }, [load]);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 60000);
    return () => clearInterval(t);
  }, [load]);

  if (err) {
    return (
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3 text-sm text-slate-500">
        Bridges: {err}
      </div>
    );
  }
  if (bridges.length === 0) return null;

  const dotClass = (b: (typeof bridges)[number]) =>
    b.ok
      ? "bg-emerald-500"
      : b.expected
        ? "bg-rose-500"
        : "bg-amber-400";

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-3 space-y-2">
      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
        Bridges
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        External bridges and sidecars these connectors depend on. Probed
        daemon-side, cached 60s.
      </p>
      <div className="space-y-1.5">
        {bridges.map((b) => (
          <div
            key={b.kind}
            className="flex items-center gap-2.5 text-sm flex-wrap"
          >
            <span
              className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${dotClass(b)}`}
              aria-hidden
            />
            <span className="font-medium text-slate-800 dark:text-slate-200">
              {b.label}
            </span>
            <span className="text-xs text-slate-400">
              {b.ok
                ? "up"
                : b.expected
                  ? "down"
                  : "not configured"}
            </span>
            {b.version ? (
              <span className="text-[11px] text-slate-400">
                v{b.version}
              </span>
            ) : null}
            <span className="text-[11px] text-slate-400">
              last seen{" "}
              {b.last_seen_at
                ? new Date(b.last_seen_at).toLocaleString()
                : "never"}
            </span>
            {!b.ok && b.error ? (
              <span className="text-[11px] text-rose-500 dark:text-rose-400">
                {b.error}
              </span>
            ) : null}
            {b.kind === "outlook" && b.handle_count != null ? (
              <span
                className={`text-[11px] ${
                  b.handle_health === "crit"
                    ? "text-rose-500 dark:text-rose-400"
                    : b.handle_health === "warn"
                      ? "text-amber-500 dark:text-amber-400"
                      : "text-slate-400"
                }`}
                title="COM handle count (warn 25k, crit 60k). High + long uptime means the bridge is heading for a wedge."
              >
                handles {b.handle_count.toLocaleString()}
                {b.uptime_seconds != null
                  ? ` · up ${Math.floor(b.uptime_seconds / 3600)}h`
                  : ""}
              </span>
            ) : null}
            {b.kind === "outlook" ? (
              <button
                type="button"
                onClick={() => void recycleCom()}
                disabled={recycling}
                className="text-[11px] rounded border border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
                title="Recycle the bridge COM session without closing Outlook"
              >
                {recycling ? "Recycling..." : "Recycle COM"}
              </button>
            ) : null}
          </div>
        ))}
        {recycleMsg ? (
          <div className="text-[11px] text-slate-500 dark:text-slate-400 pt-1">
            {recycleMsg}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function WorkersSection() {
  const [fleet, setFleet] = useState<WorkerFleetRow[]>([]);
  const [summary, setSummary] = useState<Record<string, number>>({});
  const [passive, setPassive] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openRuns, setOpenRuns] = useState<string | null>(null);
  const [runs, setRuns] = useState<
    Awaited<ReturnType<typeof api.adminWorkerRuns>>["runs"]
  >([]);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.adminWorkersFleet();
      setFleet(r.workers);
      setSummary(r.summary);
      setPassive(r.passive_mode);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 15000);
    return () => clearInterval(t);
  }, [load]);

  async function act(
    id: string,
    fn: () => Promise<{ ok: boolean; error?: string | null }>,
    label: string,
  ) {
    setBusyId(id);
    setToast(null);
    try {
      const r = await fn();
      setToast(
        r.ok ? `${label}: ${id} ok` : `${label}: ${id} - ${r.error ?? "failed"}`,
      );
      await load();
    } catch (e) {
      setToast(`${label}: ${id} - ${(e as Error).message}`);
    } finally {
      setBusyId(null);
    }
  }

  async function toggleRuns(id: string) {
    if (openRuns === id) {
      setOpenRuns(null);
      return;
    }
    setOpenRuns(id);
    try {
      const r = await api.adminWorkerRuns(id);
      setRuns(r.runs);
    } catch {
      setRuns([]);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          Workers
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Daemon worker fleet. The supervisor auto-clears a worker stuck in
          running with a stale heartbeat and the next tick re-runs it on
          cadence; pause stops a worker until you resume it.
          {passive ? " Passive mode is ON: gated workers are off." : ""}
        </p>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 p-3 text-sm text-rose-700 dark:text-rose-300">
          {err}
        </div>
      )}
      {toast && (
        <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 p-3 text-sm text-slate-700 dark:text-slate-300">
          {toast}
        </div>
      )}

      <div className="flex flex-wrap gap-2 text-xs">
        {Object.entries(summary).map(([k, v]) => (
          <span
            key={k}
            className="rounded-full bg-slate-100 dark:bg-slate-800 px-2.5 py-1 text-slate-600 dark:text-slate-300"
          >
            {k.replace(/_/g, " ")}: <strong>{v}</strong>
          </span>
        ))}
      </div>

      <BridgesPanel />

      <div className="space-y-2">
        {fleet.map((w) => (
          <div
            key={w.id}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/40 p-3"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-semibold ${workerStatusClass(
                  w.status,
                )}`}
              >
                {w.status}
              </span>
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {w.label}
              </span>
              <span className="text-xs text-slate-400">{w.id}</span>
              <span className="text-xs text-slate-400">
                {w.tier} · readiness {w.readiness_score}
              </span>
              {w.heartbeat_stale && (
                <span className="text-xs font-semibold text-rose-600 dark:text-rose-400">
                  heartbeat stale
                </span>
              )}
              <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">
                last tick {fmtWhen(w.last_tick_at)} · {w.total_runs} runs
                {w.error_count > 0 ? ` · ${w.error_count} fails` : ""}
              </span>
            </div>
            {w.last_error && (
              <p className="mt-1.5 text-xs text-rose-600 dark:text-rose-400 break-words">
                last error ({fmtWhen(w.last_error_at)}): {w.last_error}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {w.paused ? (
                <button
                  disabled={busyId === w.id}
                  onClick={() =>
                    void act(w.id, () => api.adminWorkerResume(w.id), "resume")
                  }
                  className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2.5 py-1 disabled:opacity-50"
                >
                  Resume
                </button>
              ) : (
                <button
                  disabled={busyId === w.id || w.status === "gated_off"}
                  onClick={() =>
                    void act(w.id, () => api.adminWorkerPause(w.id), "pause")
                  }
                  className="rounded-md border border-slate-300 dark:border-slate-600 text-xs px-2.5 py-1 text-slate-700 dark:text-slate-200 disabled:opacity-50"
                >
                  Pause
                </button>
              )}
              <button
                disabled={
                  busyId === w.id ||
                  !w.runnable_on_demand ||
                  w.paused ||
                  w.status === "gated_off"
                }
                title={
                  w.runnable_on_demand
                    ? "Run one tick now"
                    : "Not runnable on demand"
                }
                onClick={() =>
                  void act(w.id, () => api.adminWorkerRunNow(w.id), "run-now")
                }
                className="rounded-md border border-slate-300 dark:border-slate-600 text-xs px-2.5 py-1 text-slate-700 dark:text-slate-200 disabled:opacity-50"
              >
                Run now
              </button>
              <button
                onClick={() => void toggleRuns(w.id)}
                className="rounded-md border border-slate-300 dark:border-slate-600 text-xs px-2.5 py-1 text-slate-700 dark:text-slate-200"
              >
                {openRuns === w.id ? "Hide log" : "Run log"}
              </button>
            </div>
            {openRuns === w.id && (
              <div className="mt-2 max-h-56 overflow-auto rounded-lg bg-slate-50 dark:bg-slate-800/50 p-2 text-xs font-mono">
                {runs.length === 0 ? (
                  <p className="text-slate-400">no recorded runs</p>
                ) : (
                  runs.map((run) => (
                    <div
                      key={run.id}
                      className="flex gap-2 border-b border-slate-200/60 dark:border-slate-700/60 py-1 last:border-0"
                    >
                      <span
                        className={
                          run.status === "ok"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-rose-600 dark:text-rose-400"
                        }
                      >
                        {run.status}
                      </span>
                      <span className="text-slate-500">
                        {fmtWhen(run.started_at)}
                      </span>
                      <span className="text-slate-500">
                        {run.items_processed} items · {run.trigger}
                      </span>
                      {run.error_message && (
                        <span className="text-rose-500 break-all">
                          {run.error_message}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        ))}
        {fleet.length === 0 && !err && (
          <p className="text-sm text-slate-400">Loading fleet...</p>
        )}
      </div>
    </div>
  );
}

function AdminDataSection() {
  const [plan, setPlan] = useState<AdminResetResult | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<AdminResetResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<AdminSnapshot[]>([]);
  const [restoreErr, setRestoreErr] = useState<string | null>(null);
  const [restoreMsg, setRestoreMsg] = useState<string | null>(null);
  const [delConfirm, setDelConfirm] = useState("");
  const [delBusy, setDelBusy] = useState(false);
  const [delMsg, setDelMsg] = useState<string | null>(null);
  const [delErr, setDelErr] = useState<string | null>(null);

  const loadPlan = useCallback(async () => {
    try {
      setPlan(await api.adminResetUserData(true));
    } catch (e) {
      setErr((e as Error).message);
    }
  }, []);

  const loadSnapshots = useCallback(async () => {
    try {
      const r = await api.adminListSnapshots();
      setSnapshots(r.snapshots);
    } catch {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    void loadPlan();
    void loadSnapshots();
  }, [loadPlan, loadSnapshots]);

  const armed = confirmText.trim() === "RESET";

  async function doReset() {
    if (!armed || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api.adminResetUserData(false);
      if (!r.ok) {
        setErr(r.error ?? "reset failed");
      } else {
        setResult(r);
        setConfirmText("");
        await loadSnapshots();
        await loadPlan();
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const delArmed = delConfirm.trim() === "DELETE_MY_ACCOUNT";

  async function doDeleteAccount() {
    if (!delArmed || delBusy) return;
    setDelBusy(true);
    setDelErr(null);
    setDelMsg(null);
    try {
      const r = await api.adminDeleteAccount();
      if (!r.ok) {
        setDelErr(r.error ?? "delete failed");
        return;
      }
      setDelConfirm("");
      setDelMsg(
        `Account deleted. Snapshot: ${r.snapshot_path}. Signing you out...`,
      );
      // The account + its sessions/tokens are gone server-side and the
      // delete response already cleared the cookies. Defensively hit
      // logout, purge ALL client storage so no stale "logged in" shell
      // survives, then hard-redirect to the sign-in screen.
      // The real logout route is /auth/logout (NOT /api/auth/logout -
      // that path 401s at the /api requireAuth gate, so handleLogout
      // never ran and the portable cookie was never cleared). This is
      // the route that calls clearPortableSessionCookie on the parent
      // brand domain with the exact attrs the cookie was set with.
      try {
        await fetch("/auth/logout", {
          method: "POST",
          credentials: "include",
        });
      } catch {
        /* cookies also cleared by the delete response */
      }
      try {
        localStorage.clear();
        sessionStorage.clear();
      } catch {
        /* storage may be unavailable; redirect still clears the UI */
      }
      // replace() not href: a full reload with no history entry, so
      // the back button cannot restore the stale signed-in shell.
      window.location.replace("/login?reason=account_deleted");
    } catch (e) {
      setDelErr((e as Error).message);
    } finally {
      setDelBusy(false);
    }
  }

  async function doRestore(path: string) {
    if (busy) return;
    if (
      !window.confirm(
        `Restore the database from this snapshot? The daemon will restart.\n\n${path}`,
      )
    )
      return;
    setBusy(true);
    setRestoreErr(null);
    setRestoreMsg(null);
    try {
      const isAccount = path.split("/").pop()?.startsWith("delete_account_");
      const r = isAccount
        ? await api.adminRestoreAccountSnapshot(path)
        : await api.adminRestoreSnapshot(path);
      setRestoreMsg(
        r.ok
          ? `Restored from ${r.restored_from}. Daemon restarting, reload in a few seconds.`
          : "restore failed",
      );
    } catch (e) {
      setRestoreErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const totalRows = plan
    ? Object.values(plan.row_counts_before).reduce((a, b) => a + Math.max(b, 0), 0)
    : 0;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Admin</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Destructive pre-onboarding tooling. Every reset takes a full database
          snapshot first so it can be rolled back.
        </p>
      </div>

      <div className="rounded-xl border-2 border-rose-300 dark:border-rose-800/70 bg-rose-50/70 dark:bg-rose-950/30 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-bold text-rose-800 dark:text-rose-200">
              Reset all user data
            </h3>
            <p className="text-sm text-rose-700/90 dark:text-rose-300/80 mt-0.5">
              Clears every table holding your captured content, inferences and
              connector links so you re-onboard into a clean instance. Account,
              auth, AI keys, scope and view definitions, and the connector
              catalog are kept.
            </p>
          </div>
        </div>

        {plan && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div className="rounded-lg bg-white/70 dark:bg-slate-900/40 p-3">
              <p className="font-bold text-rose-700 dark:text-rose-300 mb-1">
                Will clear ({plan.tables_to_clear?.length ?? plan.tables_cleared.length} tables, ~{totalRows.toLocaleString()} rows)
              </p>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                {(plan.tables_to_clear ?? []).slice(0, 30).join(", ")}
                {(plan.tables_to_clear?.length ?? 0) > 30 ? ", ..." : ""}
              </p>
            </div>
            <div className="rounded-lg bg-white/70 dark:bg-slate-900/40 p-3">
              <p className="font-bold text-emerald-700 dark:text-emerald-400 mb-1">
                Will keep ({plan.tables_kept.length} tables)
              </p>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                {plan.tables_kept.join(", ")}
              </p>
              {plan.tables_unclassified.length > 0 && (
                <p className="text-amber-600 dark:text-amber-400 mt-2">
                  Not touched (unclassified): {plan.tables_unclassified.join(", ")}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-sm font-medium text-rose-800 dark:text-rose-200">
            Type <span className="font-mono font-bold">RESET</span> to confirm
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder="RESET"
            className="w-40 rounded-lg border border-rose-300 dark:border-rose-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-400"
          />
          <div>
            <button
              type="button"
              disabled={!armed || busy}
              onClick={doReset}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-bold text-white enabled:hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              {busy ? "Working..." : "Reset all user data"}
            </button>
          </div>
        </div>

        {err && (
          <p className="text-sm text-rose-700 dark:text-rose-300 font-medium">
            {err}
          </p>
        )}

        {result && result.ok && !result.dry_run && (
          <div className="rounded-lg bg-white/80 dark:bg-slate-900/50 p-3 text-xs space-y-1">
            <p className="font-bold text-emerald-700 dark:text-emerald-400">
              Reset complete.
            </p>
            <p className="text-slate-600 dark:text-slate-400">
              Snapshot: <span className="font-mono">{result.snapshot_path}</span>
            </p>
            <p className="text-slate-600 dark:text-slate-400">
              Cleared {result.tables_cleared.length} tables. Rows before:{" "}
              {Object.values(result.row_counts_before)
                .reduce((a, b) => a + Math.max(b, 0), 0)
                .toLocaleString()}{" "}
              and after:{" "}
              {Object.values(result.row_counts_after)
                .reduce((a, b) => a + Math.max(b, 0), 0)
                .toLocaleString()}
              .
            </p>
          </div>
        )}
      </div>

      <div className="rounded-xl border-2 border-rose-400 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/40 p-5 space-y-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-rose-600 dark:text-rose-400 mt-0.5 shrink-0" />
          <div>
            <h3 className="font-bold text-rose-800 dark:text-rose-200">
              Delete my account
            </h3>
            <p className="text-sm text-rose-700/90 dark:text-rose-300/80 mt-0.5">
              This permanently wipes your Memso account: all data plus your
              user record, sessions, tokens, and settings. After this, you
              will go through the new-user signup flow. A snapshot is saved
              first.
            </p>
          </div>
        </div>
        <div className="space-y-2">
          <label className="block text-sm font-medium text-rose-800 dark:text-rose-200">
            Type{" "}
            <span className="font-mono font-bold">DELETE_MY_ACCOUNT</span> to
            confirm
          </label>
          <input
            type="text"
            value={delConfirm}
            onChange={(e) => setDelConfirm(e.target.value)}
            placeholder="DELETE_MY_ACCOUNT"
            className="w-64 rounded-lg border border-rose-300 dark:border-rose-700 bg-white dark:bg-slate-900 px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-rose-400"
          />
          <div>
            <button
              type="button"
              disabled={!delArmed || delBusy}
              onClick={doDeleteAccount}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-700 px-4 py-2 text-sm font-bold text-white enabled:hover:bg-rose-800 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-4 h-4" />
              {delBusy ? "Deleting..." : "Delete my account"}
            </button>
          </div>
        </div>
        {delErr && (
          <p className="text-sm text-rose-700 dark:text-rose-300 font-medium">
            {delErr}
          </p>
        )}
        {delMsg && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400 font-medium">
            {delMsg}
          </p>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 p-5 space-y-3">
        <div className="flex items-center gap-2">
          <RefreshCw className="w-4 h-4 text-slate-500" />
          <h3 className="font-bold text-slate-800 dark:text-slate-200">
            Restore from snapshot
          </h3>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Pick a snapshot to roll back to. A safety snapshot of the current
          database is taken first, then the daemon restarts.
        </p>
        {snapshots.length === 0 ? (
          <p className="text-sm text-slate-400 dark:text-slate-500">
            No snapshots yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {snapshots.map((s) => (
              <li
                key={s.path}
                className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700/50 px-3 py-2 text-xs"
              >
                <div className="min-w-0">
                  <p className="font-mono truncate text-slate-700 dark:text-slate-300">
                    {s.name}
                  </p>
                  <p className="text-slate-400">
                    <span className="font-semibold text-slate-500 dark:text-slate-400">
                      {s.name.startsWith("delete_account_")
                        ? "Account delete"
                        : s.name.startsWith("pre-restore_")
                          ? "Pre-restore safety"
                          : "Data reset"}
                    </span>{" "}
                    · {new Date(s.created_at).toLocaleString()} ·{" "}
                    {(s.size_bytes / 1_000_000).toFixed(1)} MB
                  </p>
                </div>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => doRestore(s.path)}
                  className="shrink-0 rounded-lg border border-slate-300 dark:border-slate-600 px-3 py-1 font-medium text-slate-700 dark:text-slate-300 enabled:hover:bg-slate-100 dark:enabled:hover:bg-slate-800 disabled:opacity-40"
                >
                  Restore
                </button>
              </li>
            ))}
          </ul>
        )}
        {restoreMsg && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400">
            {restoreMsg}
          </p>
        )}
        {restoreErr && (
          <p className="text-sm text-rose-700 dark:text-rose-300">{restoreErr}</p>
        )}
      </div>
    </div>
  );
}

function AppearanceSection() {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Appearance</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Pick a theme and how Memso follows light or dark.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white/60 dark:bg-slate-900/40 overflow-hidden max-w-sm">
        <ThemePickerPanel />
      </div>
    </div>
  );
}

/** User profile + account-level admin actions. Restart onboarding is
 *  the headline affordance: it clears users.onboarded_at so the next
 *  navigation routes back through the wizard. Useful for re-picking
 *  theme / vault / scopes / re-grant-Gmail / re-grant-Calendar
 *  without juggling localStorage. */
function AccountSection() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const restart = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const csrf = getCsrfToken();
      const res = await fetch("/api/onboarding/restart", {
        method: "POST",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      await refreshAuth();
      navigate("/onboarding", { replace: true });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const u = auth.user;
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Account</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Your Memso identity and workspace handle. Sign-out and onboarding controls.
        </p>
      </div>
      <section className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-4 space-y-2">
        <div className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          Signed in as
        </div>
        <div className="flex items-center gap-3">
          {u?.picture ? (
            <img
              src={u.picture}
              alt=""
              referrerPolicy="no-referrer"
              className="w-10 h-10 rounded-full ring-1 ring-slate-300 dark:ring-slate-600 object-cover"
            />
          ) : (
            <span className="w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center text-white font-bold">
              {(u?.name || u?.email || "?").charAt(0).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
              {u?.name || "(unknown)"}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {u?.email || ""}
            </div>
          </div>
        </div>
        <dl className="grid grid-cols-[100px_1fr] gap-y-1 gap-x-3 text-xs pt-2">
          <dt className="text-slate-500 dark:text-slate-400">Handle</dt>
          <dd className="font-mono text-slate-700 dark:text-slate-200">
            {u?.handle ? `memso.ai/u/${u.handle}` : "(not set)"}
          </dd>
          <dt className="text-slate-500 dark:text-slate-400">Onboarded</dt>
          <dd className="text-slate-700 dark:text-slate-200">
            {u?.onboardedAt ? new Date(u.onboardedAt).toLocaleString() : "no (wizard pending)"}
          </dd>
        </dl>
      </section>

      <section className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-4 space-y-3">
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Restart onboarding</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Re-run the welcome wizard to re-pick your theme, vault path, privacy scopes, or
            reconnect Gmail / Calendar. Your existing data, captures, and connector grants stay
            put; only the wizard flag resets.
          </p>
        </div>
        {error && <div className="text-xs text-rose-700 dark:text-rose-300">{error}</div>}
        {confirming ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={restart}
              disabled={busy}
              className="rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
            >
              {busy ? "Restarting…" : "Confirm: restart onboarding"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5"
          >
            Restart onboarding
          </button>
        )}
      </section>

      <section className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-4 space-y-2">
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Sign out</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Ends this session on this device. Your account and data remain intact.
        </p>
        <a
          href="/auth/logout"
          onClick={(e) => {
            // Match Sidebar.tsx logout: abort in-flight bridge fetches
            // + clear cache before navigating so logout is felt
            // instantly even when the mail page had many pending
            // pageSize=1 count fetches in flight.
            e.preventDefault();
            void import("../lib/bridgeFetch").then((m) => {
              try { m.abortAllBridgeFetches(); } catch { /* noop */ }
              try { m.clearBridgeCache(); } catch { /* noop */ }
              window.location.assign("/auth/logout");
            });
          }}
          className="inline-flex items-center gap-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-1.5 transition-colors"
        >
          Sign out
        </a>
      </section>
    </div>
  );
}

function AboutSection() {
  const [serverVersion, setServerVersion] = useState<VersionPayload | null>(null);
  useEffect(() => {
    api.version().then(setServerVersion).catch(() => undefined);
  }, []);
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">About</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Build metadata. Useful when troubleshooting an apparent stale bundle.
        </p>
      </div>
      <dl className="text-sm grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-y-2 gap-x-4">
        <Term label="Bundle">
          <code className="text-xs">{BUILT_VERSION}</code>
        </Term>
        <Term label="Server">
          <code className="text-xs">{serverVersion?.version ?? "-"}</code>
        </Term>
        <Term label="Git SHA">
          <code className="text-xs">{serverVersion?.gitSha ?? "-"}</code>
        </Term>
        <Term label="Built at">
          <code className="text-xs">{serverVersion?.buildTime ?? "-"}</code>
        </Term>
      </dl>
    </div>
  );
}

function Term({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 self-center">
        {label}
      </dt>
      <dd className="text-slate-900 dark:text-slate-100">{children}</dd>
    </>
  );
}

function Row({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</div>
        <div className="text-xs text-slate-500 dark:text-slate-400">{hint}</div>
      </div>
      <div className="flex-shrink-0">{children}</div>
    </div>
  );
}

function AgentsSection() {
  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .agents()
      .then((d) => setAgents(d.agents))
      .catch((e: Error) => setError(e.message));
  }, []);

  if (error) {
    return (
      <div className="text-sm text-rose-600 dark:text-rose-300">
        Could not load agents: {error}
      </div>
    );
  }
  if (!agents) {
    return <div className="text-sm text-slate-500 dark:text-slate-400">Loading agents...</div>;
  }

  const totals = agents.reduce(
    (acc, a) => {
      acc.local += a.todaySpend.local.tokens;
      acc.anthropic += a.todaySpend.anthropic.tokens;
      acc.claudeSession += a.todaySpend["claude-session"].tokens;
      acc.runs += a.todaySpend.local.runs + a.todaySpend.anthropic.runs + a.todaySpend["claude-session"].runs;
      return acc;
    },
    { local: 0, anthropic: 0, claudeSession: 0, runs: 0 },
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Agents</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          What each pipeline stage uses + today's token spend per route.
          Privacy gate forces local for any private-scope content,
          regardless of defaults.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <SpendCard
          Icon={HardDrive}
          label="Local"
          tokens={totals.local}
          tint="emerald"
          hint="WakiAIService -> Ollama"
        />
        <SpendCard
          Icon={Cloud}
          label="Anthropic API"
          tokens={totals.anthropic}
          tint="sky"
          hint="Sonnet, Opus"
        />
        <SpendCard
          Icon={Sparkles}
          label="Claude session"
          tokens={totals.claudeSession}
          tint="violet"
          hint="Max plan, free"
        />
      </div>

      <div className="space-y-2">
        {agents.map((agent) => (
          <AgentRow key={agent.name} agent={agent} reload={() => api.agents().then((d) => setAgents(d.agents))} />
        ))}
      </div>
      <p className="text-[11px] text-slate-400">
        Total runs today: {totals.runs}. Spend numbers are token counts;
        local + claude-session are free, anthropic is billed.
      </p>
    </div>
  );
}

function ModeLink({ to, Icon, label }: { to: string; Icon: LucideIcon; label: string }) {
  return (
    <li>
      <Link
        to={to}
        className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800/40 transition-colors"
      >
        <Icon className="w-4 h-4 flex-shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        <ArrowRight className="w-3.5 h-3.5 text-slate-400" />
      </Link>
    </li>
  );
}

function AgentRow({ agent, reload }: { agent: AgentSummary; reload: () => void }) {
  const [draft, setDraft] = useState(agent.modelOverride ?? "");
  const [busy, setBusy] = useState(false);

  const apply = async (
    value: string | null | undefined,
    opusToggle?: boolean,
  ) => {
    if (busy) return;
    setBusy(true);
    try {
      const patch: { modelOverride?: string | null; opusEscalateAlwaysOn?: boolean } = {};
      if (value !== undefined) patch.modelOverride = value;
      if (opusToggle !== undefined) patch.opusEscalateAlwaysOn = opusToggle;
      await setAgentOverride(agent.name, patch);
      reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="glass rounded-xl p-3 sm:p-4 border border-slate-200/70 dark:border-slate-700/50 backdrop-blur">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-900 dark:text-slate-100 capitalize">
            {agent.name}
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{agent.description}</div>
        </div>
        <RouteBadge model={agent.effective} fallback={agent.localFallback} />
      </header>
      <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
        <SpendCell tint="emerald" label="local" runs={agent.todaySpend.local.runs} tokens={agent.todaySpend.local.tokens} />
        <SpendCell tint="sky" label="api" runs={agent.todaySpend.anthropic.runs} tokens={agent.todaySpend.anthropic.tokens} />
        <SpendCell tint="violet" label="session" runs={agent.todaySpend["claude-session"].runs} tokens={agent.todaySpend["claude-session"].tokens} />
      </div>
      <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2 text-xs">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={`override (e.g. ${agent.localFallback})`}
          className="flex-1 rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs text-slate-900 dark:text-slate-100 font-mono"
        />
        <button
          type="button"
          onClick={() => apply(draft.trim() || null)}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-2.5 py-1.5 disabled:opacity-50"
        >
          Set
        </button>
        {agent.modelOverride && (
          <button
            type="button"
            onClick={() => {
              setDraft("");
              apply(null);
            }}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1.5 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50"
          >
            Clear
          </button>
        )}
        {agent.opusEscalateAvailable && (
          <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 dark:text-slate-200 ml-auto">
            <input
              type="checkbox"
              checked={agent.opusEscalateAlwaysOn}
              onChange={(e) => apply(undefined, e.target.checked)}
              disabled={busy}
            />
            Opus always
          </label>
        )}
      </div>
      {agent.prefersClaudeSession && (
        <div className="mt-2 text-[10px] uppercase tracking-wider font-bold">
          <span className="px-1.5 py-0.5 rounded-full bg-sky-200 dark:bg-sky-700/40 text-sky-800 dark:text-sky-200">
            Claude session preferred
          </span>
        </div>
      )}
    </article>
  );
}

interface AgentRunRow { id: string; agent: string; status: string; model: string | null; route: string | null; input_tokens: number | null; output_tokens: number | null; latency_ms: number | null; started_at: string; finished_at: string | null; error: string | null }
interface ConnectorRunRow { id: string; connector_id: string; started_at: string; finished_at: string | null; status: string; items_seen: number; items_new: number; error: string | null }
interface MonitorMatchRow { id: string; monitor_kind: string | null; matched_at: string; headline: string | null; scope: string | null }
interface PipelineJobRow { id: string; capture_id: string; status: string; enqueued_at: string; started_at: string | null; finished_at: string | null; error: string | null; attempts: number }
interface ClaudeSessionRow { id: string; agent: string; started_at: string; finished_at: string | null; status: string; error: string | null }

function LogsSection() {
  const [tab, setTab] = useState<"agents" | "pipeline" | "connectors" | "monitors" | "claude">("agents");
  const [agentRuns, setAgentRuns] = useState<AgentRunRow[]>([]);
  const [connectorRuns, setConnectorRuns] = useState<ConnectorRunRow[]>([]);
  const [monitorMatches, setMonitorMatches] = useState<MonitorMatchRow[]>([]);
  const [pipelineJobs, setPipelineJobs] = useState<PipelineJobRow[]>([]);
  const [claudeRuns, setClaudeRuns] = useState<ClaudeSessionRow[]>([]);

  useEffect(() => {
    fetch("/api/logs/agent-runs", { credentials: "same-origin" }).then((r) => r.json()).then((d) => setAgentRuns(d.runs || [])).catch(() => undefined);
    fetch("/api/logs/connector-runs", { credentials: "same-origin" }).then((r) => r.json()).then((d) => setConnectorRuns(d.runs || [])).catch(() => undefined);
    fetch("/api/logs/monitor-matches", { credentials: "same-origin" }).then((r) => r.json()).then((d) => setMonitorMatches(d.matches || [])).catch(() => undefined);
    fetch("/api/logs/pipeline-jobs", { credentials: "same-origin" }).then((r) => r.json()).then((d) => setPipelineJobs(d.jobs || [])).catch(() => undefined);
    fetch("/api/logs/claude-session", { credentials: "same-origin" }).then((r) => r.json()).then((d) => setClaudeRuns(d.requests || [])).catch(() => undefined);
  }, []);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Logs</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Recent telemetry across the daemon's workers. Use this when something looks
          off and you want to see why.
        </p>
      </div>
      <nav className="flex gap-1.5 flex-wrap">
        {([
          ["agents", `Agents (${agentRuns.length})`],
          ["pipeline", `Pipeline (${pipelineJobs.length})`],
          ["connectors", `Connectors (${connectorRuns.length})`],
          ["monitors", `Monitors (${monitorMatches.length})`],
          ["claude", `Claude session (${claudeRuns.length})`],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            aria-pressed={tab === id}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${
              tab === id
                ? "bg-sky-600 text-white"
                : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            {label}
          </button>
        ))}
      </nav>

      {tab === "agents" && (
        <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {agentRuns.length === 0 ? <Empty /> : agentRuns.map((r) => (
            <li key={r.id} className="rounded-lg bg-slate-100 dark:bg-slate-800/40 px-3 py-2 text-xs flex flex-wrap items-center gap-2">
              <span className="font-bold text-slate-900 dark:text-slate-100">{r.agent}</span>
              <StatusChip status={r.status} />
              {r.route && <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">{r.route}</span>}
              {r.model && <code className="text-[10px] text-slate-500">{r.model}</code>}
              <span className="ml-auto text-[10px] text-slate-500">{r.input_tokens ?? 0}/{r.output_tokens ?? 0} tok · {r.latency_ms ?? 0}ms</span>
              <span className="text-[10px] text-slate-400 w-full sm:w-auto">{new Date(r.started_at).toLocaleString()}</span>
              {r.error && <div className="text-[10px] text-rose-600 dark:text-rose-300 w-full">{r.error.slice(0, 200)}</div>}
            </li>
          ))}
        </ul>
      )}
      {tab === "pipeline" && (
        <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {pipelineJobs.length === 0 ? <Empty /> : pipelineJobs.map((j) => (
            <li key={j.id} className="rounded-lg bg-slate-100 dark:bg-slate-800/40 px-3 py-2 text-xs flex flex-wrap items-center gap-2">
              <code className="text-slate-500">{j.capture_id}</code>
              <StatusChip status={j.status} />
              <span className="text-[10px] text-slate-500">attempts: {j.attempts}</span>
              <span className="ml-auto text-[10px] text-slate-400">{new Date(j.enqueued_at).toLocaleString()}</span>
              {j.error && <div className="text-[10px] text-rose-600 dark:text-rose-300 w-full">{j.error.slice(0, 200)}</div>}
            </li>
          ))}
        </ul>
      )}
      {tab === "connectors" && (
        <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {connectorRuns.length === 0 ? <Empty /> : connectorRuns.map((r) => (
            <li key={r.id} className="rounded-lg bg-slate-100 dark:bg-slate-800/40 px-3 py-2 text-xs flex flex-wrap items-center gap-2">
              <code className="text-slate-500">{r.connector_id}</code>
              <StatusChip status={r.status} />
              <span className="text-[10px] text-slate-500">{r.items_new}/{r.items_seen} new</span>
              <span className="ml-auto text-[10px] text-slate-400">{new Date(r.started_at).toLocaleString()}</span>
              {r.error && <div className="text-[10px] text-rose-600 dark:text-rose-300 w-full">{r.error.slice(0, 200)}</div>}
            </li>
          ))}
        </ul>
      )}
      {tab === "monitors" && (
        <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {monitorMatches.length === 0 ? <Empty /> : monitorMatches.map((m) => (
            <li key={m.id} className="rounded-lg bg-slate-100 dark:bg-slate-800/40 px-3 py-2 text-xs flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">{m.monitor_kind || "?"}</span>
              <span className="flex-1 text-slate-900 dark:text-slate-100 truncate">{m.headline}</span>
              <span className="text-[10px] text-slate-400">{new Date(m.matched_at).toLocaleString()}</span>
            </li>
          ))}
        </ul>
      )}
      {tab === "claude" && (
        <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {claudeRuns.length === 0 ? <Empty /> : claudeRuns.map((c) => (
            <li key={c.id} className="rounded-lg bg-slate-100 dark:bg-slate-800/40 px-3 py-2 text-xs flex items-center gap-2">
              <span className="font-bold text-slate-900 dark:text-slate-100">{c.agent}</span>
              <StatusChip status={c.status} />
              <span className="ml-auto text-[10px] text-slate-400">{new Date(c.started_at).toLocaleString()}</span>
              {c.error && <div className="text-[10px] text-rose-600 dark:text-rose-300 w-full">{c.error.slice(0, 200)}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function StatusChip({ status }: { status: string }) {
  const tints: Record<string, string> = {
    ok: "bg-emerald-200 dark:bg-emerald-700/40 text-emerald-800 dark:text-emerald-200",
    done: "bg-emerald-200 dark:bg-emerald-700/40 text-emerald-800 dark:text-emerald-200",
    running: "bg-sky-200 dark:bg-sky-700/40 text-sky-800 dark:text-sky-200",
    pending: "bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200",
    error: "bg-rose-200 dark:bg-rose-700/40 text-rose-800 dark:text-rose-200",
    skipped: "bg-amber-200 dark:bg-amber-700/40 text-amber-800 dark:text-amber-200",
  };
  return (
    <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${tints[status] || tints.pending}`}>
      {status}
    </span>
  );
}

function EmptyLog() {
  return <p className="text-sm text-slate-500 dark:text-slate-400 italic">No rows yet.</p>;
}

function Empty() {
  return <EmptyLog />;
}

const WHATSNEW_CATEGORY_ORDER = [
  "all",
  "feature",
  "bug-fix",
  "security",
  "polish",
  "performance",
  "infrastructure",
  "refactor",
  "docs",
] as const;

type WhatsNewCategoryFilter = typeof WHATSNEW_CATEGORY_ORDER[number];

function WhatsNewSection() {
  const { entries, lastSeen } = useChangelog();
  const [shown, setShown] = useState(20);
  const [categoryFilter, setCategoryFilter] = useState<WhatsNewCategoryFilter>("all");
  const [search, setSearch] = useState("");

  // Mark all as seen the moment the user lands on this panel; they're
  // explicitly reading the feed.
  useEffect(() => {
    if (entries.length > 0) setLastSeen(entries[0].sha);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries.length]);

  if (entries.length === 0) {
    return (
      <p className="text-sm text-slate-500 dark:text-slate-400 italic">
        No changelog entries yet. As deploys land, they'll show up here within a minute.
      </p>
    );
  }

  const needle = search.trim().toLowerCase();
  const filtered = entries.filter((e) => {
    if (categoryFilter !== "all" && e.category !== categoryFilter) return false;
    if (!needle) return true;
    const hay = [
      e.ref_id,
      e.kind,
      e.category,
      e.summary,
      e.bullets.join(" "),
      e.sha,
    ]
      .filter((v): v is string => typeof v === "string")
      .join(" ")
      .toLowerCase();
    return hay.includes(needle);
  });
  const visible = filtered.slice(0, shown);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">What's new</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Plain-English summaries of every deploy. Tap a card to expand the full
          description.
        </p>
      </div>

      <WhatsNewToolbar
        categoryFilter={categoryFilter}
        onCategory={setCategoryFilter}
        search={search}
        onSearch={setSearch}
        totalCount={entries.length}
        filteredCount={filtered.length}
      />

      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 italic">
          No entries match the current filter.
        </p>
      ) : (
        <ol className="flex flex-col gap-2.5">
          {visible.map((e) => (
            <ChangelogEntryCard key={e.sha} entry={e} unseen={!lastSeen || isNewer(e.sha, lastSeen, entries)} />
          ))}
        </ol>
      )}

      {filtered.length > shown && (
        <button
          type="button"
          onClick={() => setShown((n) => n + 20)}
          className="text-xs text-sky-600 dark:text-sky-300 hover:underline"
        >
          Load more ({filtered.length - shown} older)
        </button>
      )}
    </div>
  );
}

function WhatsNewToolbar({
  categoryFilter,
  onCategory,
  search,
  onSearch,
  totalCount,
  filteredCount,
}: {
  categoryFilter: WhatsNewCategoryFilter;
  onCategory: (c: WhatsNewCategoryFilter) => void;
  search: string;
  onSearch: (s: string) => void;
  totalCount: number;
  filteredCount: number;
}) {
  return (
    <div className="space-y-2">
      <input
        type="search"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        placeholder="Search changelog by label, sha, or content"
        aria-label="Search What's new"
        className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/40 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-violet-400/40"
      />
      <div className="flex flex-wrap items-center gap-1.5">
        {WHATSNEW_CATEGORY_ORDER.map((cat) => {
          const active = categoryFilter === cat;
          return (
            <button
              key={cat}
              type="button"
              onClick={() => onCategory(cat)}
              aria-pressed={active}
              className={`inline-flex items-center text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? "bg-violet-600 text-white border-violet-600"
                  : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700"
              }`}
            >
              {cat === "all" ? "All" : cat}
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        Showing {filteredCount} of {totalCount} entries.
      </p>
    </div>
  );
}

function ChangelogEntryCard({ entry, unseen }: { entry: ChangelogEntry; unseen: boolean }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <li
      className={`glass rounded-xl border backdrop-blur transition-colors ${
        unseen
          ? "border-violet-300/60 dark:border-violet-500/40 bg-violet-50/60 dark:bg-violet-900/30"
          : "border-slate-200/70 dark:border-slate-700/50"
      }`}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full text-left px-4 py-3.5 flex items-center gap-3 group active:bg-orange-500/10 dark:active:bg-orange-400/15 active:scale-[0.99] transition-all duration-150 motion-reduce:transition-none rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-400/60"
      >
        {/* Card body: full-width feed-row layout. Title is the
            prominent first line; meta + badges sit as a single
            secondary line below. Chevron sits on the right
            vertically centered with the whole row. */}
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-base font-semibold text-slate-900 dark:text-slate-100 line-clamp-2 leading-snug">
            {entry.bullets[0] || "(no description)"}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap text-[11px] text-slate-500 dark:text-slate-400">
            {entry.ref_id && <RefIdBadge refId={entry.ref_id} />}
            {entry.category && <CategoryBadge category={entry.category} />}
            {entry.kind && <KindBadge kind={entry.kind} />}
            <span title={formatAbsolute(entry.ts)}>{formatRelative(entry.ts)}</span>
            {!entry.sha.startsWith("legacy_") && (
              <span className="text-[10px] text-slate-400 font-mono">
                {entry.sha.slice(0, 7)}
              </span>
            )}
          </div>
        </div>
        <ChevronDown
          className={`flex-shrink-0 w-5 h-5 text-slate-400 dark:text-slate-500 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-transform ${
            expanded ? "rotate-180" : ""
          }`}
        />
      </button>

      {expanded && (
        <div className="px-3 sm:px-3.5 pb-3 sm:pb-3.5 -mt-1 space-y-2 border-t border-slate-200/70 dark:border-slate-700/50 pt-3">
          <ul className="space-y-1.5 text-sm text-slate-900 dark:text-slate-100">
            {entry.bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-violet-500 dark:text-violet-300 mt-1">-</span>
                <div className="flex-1 min-w-0">
                  <Markdown>{b}</Markdown>
                </div>
              </li>
            ))}
          </ul>
          <div className="flex items-center gap-3 flex-wrap text-[11px] pt-1">
            <span className="text-slate-500 dark:text-slate-400" title={formatAbsolute(entry.ts)}>
              Deployed {formatAbsolute(entry.ts)}
            </span>
            {!entry.sha.startsWith("legacy_") && (
              <a
                href={`https://github.com/wwahmed/brain-v2/commit/${entry.sha}`}
                target="_blank"
                rel="noreferrer"
                className="text-slate-400 hover:text-sky-600 dark:hover:text-sky-300 font-mono"
              >
                View commit {entry.sha.slice(0, 7)}
              </a>
            )}
            {entry.ref_id && (
              <a
                href={`https://wakilabs.dev/pipeline/${encodeURIComponent(entry.ref_id)}`}
                target="_blank"
                rel="noreferrer"
                className="text-slate-400 hover:text-sky-600 dark:hover:text-sky-300"
              >
                View on /pipeline
              </a>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function KindBadge({ kind }: { kind: "major" | "minor" | "patch" }) {
  const styles: Record<typeof kind, string> = {
    major: "bg-orange-100 dark:bg-orange-900/40 text-orange-800 dark:text-orange-200 border-orange-300/70 dark:border-orange-700/40",
    minor: "bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200 border-sky-300/70 dark:border-sky-700/40",
    patch: "bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 border-slate-300/70 dark:border-slate-700/40",
  };
  const labels: Record<typeof kind, string> = {
    major: "Major",
    minor: "Minor",
    patch: "Patch",
  };
  return (
    <span
      className={`inline-flex items-center text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${styles[kind]}`}
    >
      {labels[kind]}
    </span>
  );
}

/** Pipeline-tracker reference badge. Clickable; routes to the
 *  wakilabs.dev/pipeline detail page for the given ref. Visual:
 *  monospace pill with subtle slate background so the id reads as a
 *  ticket id (matches the GitHub / Linear convention). */
function RefIdBadge({ refId }: { refId: string }) {
  return (
    <a
      href={`https://wakilabs.dev/pipeline/${encodeURIComponent(refId)}`}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center gap-1 text-[10px] font-mono font-bold px-1.5 py-0.5 rounded border bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 border-slate-300/70 dark:border-slate-700/40 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
      title={`Open ${refId} in pipeline tracker`}
    >
      {refId}
    </a>
  );
}

/** Category badge with brand-colour + lucide icon. The colour set is
 *  intentionally distinct from the kind badges (orange/sky/slate) so
 *  the two stripes read as separate axes when stacked. */
function CategoryBadge({ category }: {
  category: "feature" | "bug-fix" | "security" | "polish" | "performance" | "infrastructure" | "refactor" | "docs";
}) {
  const styles: Record<typeof category, string> = {
    feature: "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-200 border-emerald-300/70 dark:border-emerald-700/40",
    "bug-fix": "bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200 border-rose-300/70 dark:border-rose-700/40",
    security: "bg-violet-100 dark:bg-violet-900/40 text-violet-800 dark:text-violet-200 border-violet-300/70 dark:border-violet-700/40",
    polish: "bg-sky-100 dark:bg-sky-900/40 text-sky-800 dark:text-sky-200 border-sky-300/70 dark:border-sky-700/40",
    performance: "bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 border-amber-300/70 dark:border-amber-700/40",
    infrastructure: "bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 border-slate-300/70 dark:border-slate-700/40",
    refactor: "bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 border-slate-300/70 dark:border-slate-700/40",
    docs: "bg-slate-100 dark:bg-slate-800/60 text-slate-700 dark:text-slate-300 border-slate-300/70 dark:border-slate-700/40",
  };
  const Icons: Record<typeof category, LucideIcon> = {
    feature: Rocket,
    "bug-fix": Bug,
    security: Shield,
    polish: Wand2,
    performance: Zap,
    infrastructure: Cog,
    refactor: Code,
    docs: BookOpen,
  };
  const Icon = Icons[category];
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${styles[category]}`}
      title={category}
    >
      <Icon className="w-3 h-3" strokeWidth={2.5} />
      {category}
    </span>
  );
}

function isNewer(sha: string, lastSeen: string, entries: ChangelogEntry[]): boolean {
  const target = entries.findIndex((e) => e.sha === sha);
  const seen = entries.findIndex((e) => e.sha === lastSeen);
  if (target < 0 || seen < 0) return false;
  return target < seen;
}

/** Stub that redirects to the dedicated /settings/mail page. WOV's
 *  full settings UI is too rich for inlining here; the dedicated page
 *  hosts Display, AI, Custom Folders, and placeholders for Rules /
 *  Team / Retention / Background Jobs. */
function MailSettingsRedirect() {
  useEffect(() => {
    window.location.href = "/settings/mail";
  }, []);
  return (
    <div className="text-sm text-slate-500 dark:text-slate-400">
      Opening Mail settings...
    </div>
  );
}

type ConnectorsTab = "connected" | "available" | "all";

/** Privacy scope picker constants. The canonical scope set is the
 *  packets table CHECK constraint
 *    ('private','work','business','personal','family','social')
 *  so we don't introduce values that the rest of the system cannot
 *  filter on. The display labels make the set human-friendly. */
const PRIVACY_SCOPE_OPTIONS: Array<{
  value:
    | "private"
    | "work"
    | "business"
    | "personal"
    | "family"
    | "social"
    | "financial"
    | "side_project";
  label: string;
  description: string;
}> = [
  { value: "personal", label: "Personal", description: "My private personal life — not shared." },
  { value: "work",     label: "Work",     description: "My job, employer, work projects." },
  { value: "family",   label: "Family",   description: "Spouse, kids, household — shared family scope." },
  { value: "private",  label: "Private (local-only)", description: "Sensitive — local-models only, never cloud." },
  { value: "financial", label: "Financial", description: "Money, accounts, sensitive financial context." },
  { value: "business", label: "Business / Shared", description: "Side business, collaborative shared scope." },
  { value: "side_project", label: "Side project", description: "Hobby and side projects." },
  { value: "social",   label: "Public / Social",   description: "Newsletters, social, public-facing." },
];

const PRIVACY_BADGE_CLASSES: Record<string, string> = {
  work:     "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
  personal: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  family:   "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200",
  business: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  social:   "bg-slate-200 text-slate-800 dark:bg-slate-700 dark:text-slate-200",
  private:  "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
  financial: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-200",
  side_project: "bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/40 dark:text-fuchsia-200",
};

function privacyBadgeClass(scope: string | null | undefined): string {
  return PRIVACY_BADGE_CLASSES[scope ?? "social"] ?? PRIVACY_BADGE_CLASSES.social!;
}

function privacyScopeLabel(scope: string | null | undefined): string {
  const opt = PRIVACY_SCOPE_OPTIONS.find((p) => p.value === scope);
  return opt?.label ?? "Untagged";
}

function GmailTriageSection() {
  const [status, setStatus] = useState<GmailTriageStatus | null>(null);
  const [actions, setActions] = useState<GmailTriageAction[]>([]);
  const [fingerprints, setFingerprints] = useState<GmailTriageFingerprint[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const load = useCallback(() => {
    api.gmailTriageStatus().then(setStatus).catch(() => undefined);
    api.gmailTriageActions(100).then((d) => setActions(d.actions)).catch(() => undefined);
    api.gmailTriageFingerprints().then((d) => setFingerprints(d.fingerprints)).catch(() => undefined);
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (p: Partial<GmailTriageSettings>) => {
    try {
      const r = await api.gmailTriageSaveSettings(p);
      setStatus((s) => (s ? { ...s, settings: r.settings } : s));
    } catch (err) {
      setMsg((err as Error).message);
    }
  };

  const run = async (mode: "dry_run" | "live") => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.gmailTriageRun(mode === "live" ? 50 : 100, mode);
      const x = r.result;
      setMsg(
        `${x.mode === "live" ? "Live" : "Dry-run"}: scanned ${x.scanned}, promo ${x.classifiedPromo}, guarded ${x.guarded}, ${x.mode === "live" ? `archived ${x.archived}` : `would archive ${x.classifiedPromo - x.guarded}`}, errors ${x.errors}. Stages: fp ${x.fromFingerprint} / regex ${x.fromRegex} / llm ${x.fromLlm}.`,
      );
      load();
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revert = async (id: string) => {
    try {
      await api.gmailTriageRevert(id);
      load();
    } catch (err) {
      setMsg((err as Error).message);
    }
  };
  const forget = async (id: string) => {
    try {
      await api.gmailTriageForget(id);
      setFingerprints((f) => f.filter((x) => x.id !== id));
    } catch (err) {
      setMsg((err as Error).message);
    }
  };

  if (!status) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading Gmail triage…</div>;
  const s = status.settings;
  const t = status.tally;

  const toggle = (label: string, hint: string, value: boolean, on: () => void) => (
    <button
      type="button"
      onClick={on}
      className="flex w-full items-start justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2.5 text-left"
    >
      <span>
        <span className="block text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</span>
        <span className="block text-xs text-slate-500 dark:text-slate-400 mt-0.5">{hint}</span>
      </span>
      <span
        className={`mt-0.5 inline-flex h-5 w-9 shrink-0 items-center rounded-full transition ${value ? "bg-sky-600" : "bg-slate-300 dark:bg-slate-600"}`}
      >
        <span className={`h-4 w-4 rounded-full bg-white transition ${value ? "translate-x-4" : "translate-x-1"}`} />
      </span>
    </button>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Gmail triage</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Memso reads bulk promotional mail out of your inbox and files it
          under the label Memso/Triaged-Promo. Nothing is ever deleted. A
          two-stage classifier plus a hard safety guard keeps receipts,
          shipping, payments, and personal mail untouched. Start in dry-run,
          review what it would do, then unlock live archiving.
        </p>
      </div>

      {!status.scope.connected && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200">
          Connect a Gmail account first in Connectors.
        </div>
      )}
      {status.scope.connected && status.scope.needsReconsent && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800 px-3 py-2.5 text-sm text-amber-800 dark:text-amber-200 flex items-center justify-between gap-3">
          <span>
            Triage needs permission to move messages. Re-connect {status.scope.account} to grant it. Nothing changes until you approve the Google consent screen.
          </span>
          <a
            href={status.scope.reconsentUrl}
            className="shrink-0 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-3 py-1.5"
          >
            Re-connect Gmail
          </a>
        </div>
      )}

      <div className="space-y-2">
        {toggle("Enabled", "Master switch for promo triage.", s.enabled, () => patch({ enabled: !s.enabled }))}
        {toggle("Pause", "Hard stop. Overrides everything; no sweeps run while paused.", s.paused, () => patch({ paused: !s.paused }))}
        {toggle("Dry-run mode", "Audit only. Records what it would do without touching Gmail. Turn off to allow live archiving.", s.dry_run, () => patch({ dry_run: !s.dry_run }))}
        {toggle("Mark archived as read", "Off by default so unread stays unread. On clears the unread flag when archiving.", s.mark_read, () => patch({ mark_read: !s.mark_read }))}
        {!s.live_unlocked && (
          <button
            type="button"
            onClick={() => patch({ live_unlocked: true })}
            className="text-xs text-sky-600 hover:underline px-1"
          >
            Unlock live archiving (after reviewing a dry-run batch)
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => run("dry_run")}
          disabled={busy || s.paused}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50"
        >
          Run dry-run (100)
        </button>
        <button
          type="button"
          onClick={() => run("live")}
          disabled={busy || s.paused || s.dry_run || !s.live_unlocked || status.scope.needsReconsent}
          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50"
        >
          Run live batch (50)
        </button>
      </div>

      {msg && <div className="text-sm text-slate-600 dark:text-slate-300">{msg}</div>}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          ["Dry-run would archive", t.byMode.dry_run.archive],
          ["Dry-run guarded", t.byMode.dry_run.guarded],
          ["Live archived", t.byMode.live.archive],
          ["Fingerprints learned", t.fingerprints],
        ].map(([k, v]) => (
          <div key={k as string} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
            <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{v as number}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{k as string}</div>
          </div>
        ))}
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">Recent decisions</h3>
        <div className="space-y-1.5 max-h-96 overflow-auto">
          {actions.length === 0 && <div className="text-sm text-slate-500">No triage runs yet.</div>}
          {actions.map((a) => (
            <div key={a.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs">
              <div className="min-w-0">
                <div className="truncate text-slate-900 dark:text-slate-100">{a.subject || "(no subject)"}</div>
                <div className="truncate text-slate-500">
                  {a.sender_domain} · {a.classification} via {a.classifier_stage} ·{" "}
                  <span className={a.decision === "archive" ? "text-rose-600" : a.decision === "guarded" ? "text-emerald-600" : "text-slate-500"}>
                    {a.decision}{a.guard_reason ? ` (${a.guard_reason})` : ""}
                  </span>
                  {a.mode === "dry_run" ? " · dry-run" : a.executed ? " · executed" : ""}
                  {a.reverted ? " · reverted" : ""}
                </div>
              </div>
              {a.executed === 1 && a.reverted === 0 && (
                <button type="button" onClick={() => revert(a.id)} className="shrink-0 text-sky-600 hover:underline">
                  Revert
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">Learned fingerprints</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          A sender plus subject-shape that has been classified once is re-triaged with zero AI cost. Forget one to force a fresh look next time.
        </p>
        <div className="space-y-1.5 max-h-72 overflow-auto">
          {fingerprints.length === 0 && <div className="text-sm text-slate-500">None yet.</div>}
          {fingerprints.map((f) => (
            <div key={f.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs">
              <div className="min-w-0">
                <div className="truncate text-slate-900 dark:text-slate-100">{f.sender_domain}</div>
                <div className="truncate text-slate-500">
                  {f.classification} via {f.source_stage} · {f.hits} hit{f.hits === 1 ? "" : "s"} · "{f.subject_shape}"
                </div>
              </div>
              <button type="button" onClick={() => forget(f.id)} className="shrink-0 text-sky-600 hover:underline">
                Forget
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function InboxCleanupSection() {
  const [summary, setSummary] = useState<import("../lib/api").InboxCleanupSummary | null>(null);
  const [bucket, setBucket] = useState<"all" | "transactions" | "recycle_bin">("recycle_bin");
  const [candidates, setCandidates] = useState<import("../lib/api").InboxCleanupCandidate[]>([]);
  const [candTotal, setCandTotal] = useState(0);
  const [allowlist, setAllowlist] = useState<string>("");
  const [unsubCands, setUnsubCands] = useState<import("../lib/api").UnsubscribeCandidateView[]>([]);
  const [unsubLog, setUnsubLog] = useState<import("../lib/api").UnsubscribeLogEntry[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const loadSummary = useCallback(() => {
    api.inboxCleanupSummary().then(setSummary).catch((e) => setMsg((e as Error).message));
  }, []);
  const loadAllowlist = useCallback(() => {
    api.inboxCleanupAllowlist().then((r) => setAllowlist(r.allowlist.preserve_senders.join("\n"))).catch(() => undefined);
  }, []);
  const loadUnsub = useCallback(() => {
    api.inboxCleanupUnsubCandidates(100).then((r) => setUnsubCands(r.items)).catch(() => undefined);
    api.inboxCleanupUnsubLog().then((r) => setUnsubLog(r.entries)).catch(() => undefined);
  }, []);
  useEffect(() => { loadSummary(); loadAllowlist(); loadUnsub(); }, [loadSummary, loadAllowlist, loadUnsub]);

  const approveUnsub = async (dryRun: boolean) => {
    if (busy) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.inboxCleanupUnsubApprove({ all: true, limit: 100, dryRun });
      if (dryRun) {
        setMsg(`Dry-run: would unsubscribe ${r.count ?? 0} senders.`);
      } else {
        setMsg(`Unsubscribed ${r.unsubscribed ?? 0} / ${r.executed ?? 0} (${r.failed ?? 0} failed).`);
        loadUnsub(); loadSummary();
      }
    } catch (e) { setMsg((e as Error).message); }
    finally { setBusy(false); }
  };

  const resubscribe = async (logId: string) => {
    setBusy(true); setMsg(null);
    try { await api.inboxCleanupResubscribe(logId); loadUnsub(); }
    catch (e) { setMsg((e as Error).message); }
    finally { setBusy(false); }
  };

  const showCandidates = async () => {
    setBusy(true); setMsg(null);
    try {
      const r = await api.inboxCleanupCandidates(bucket, 100);
      setCandidates(r.items);
      setCandTotal(r.total_in_bucket);
    } catch (e) { setMsg((e as Error).message); }
    finally { setBusy(false); }
  };

  const approve = async (b: "transactions" | "recycle_bin", dryRun: boolean) => {
    if (busy) return;
    setBusy(true); setMsg(null);
    try {
      const r = await api.inboxCleanupApproveBatch(b, 100, dryRun);
      if (dryRun) {
        setMsg(`Dry-run (${b}): would archive ${r.would_archive ?? 0}, label-only ${r.would_label_only ?? 0}.`);
      } else {
        setMsg(`Approved (${b}): archived ${r.archived ?? 0}, labeled ${r.labeled ?? 0}.${r.errors && r.errors.length ? " Errors: " + r.errors.join("; ") : ""}`);
        loadSummary();
        setCandidates([]);
      }
    } catch (e) { setMsg((e as Error).message); }
    finally { setBusy(false); }
  };

  const saveAllowlist = async () => {
    setBusy(true); setMsg(null);
    try {
      const senders = allowlist.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
      const r = await api.inboxCleanupSaveAllowlist(senders);
      setAllowlist(r.allowlist.preserve_senders.join("\n"));
      setMsg(`Allowlist saved (${r.allowlist.preserve_senders.length} senders).`);
      loadSummary();
    } catch (e) { setMsg((e as Error).message); }
    finally { setBusy(false); }
  };

  if (!summary) {
    return <div className="text-sm text-slate-500 dark:text-slate-400">{msg ?? "Loading inbox cleanup…"}</div>;
  }

  const stat = (label: string, value: number) => (
    <div key={label} className="rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
      <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{value}</div>
      <div className="text-xs text-slate-500 dark:text-slate-400">{label}</div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Inbox Cleanup</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Triages your Gmail into keep / transactions / recycle bin. Approve archives + labels mail.
          Nothing is ever deleted and read state is never changed; archived mail is labeled and stays
          in All Mail so you can recover it or delete from Gmail when ready.
        </p>
      </div>

      {msg && (
        <div className="rounded-lg border border-sky-300 bg-sky-50 dark:bg-sky-950/30 dark:border-sky-800 px-3 py-2.5 text-sm text-sky-800 dark:text-sky-200">
          {msg}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {stat("Gmail packets total", summary.total_gmail_packets)}
        {stat("In inbox, uncleaned", summary.in_inbox_uncleaned)}
        {stat("Already cleaned", summary.already_cleaned)}
        {stat("Allowlist senders", summary.allowlist_count)}
        {stat("Recycle Bin (archive now)", summary.archive_candidates.recycle_bin_now)}
        {stat("Transactions (archive ≥30d)", summary.archive_candidates.transactions_now)}
        {stat("Transactions (label only)", summary.archive_candidates.transactions_label_only)}
        {stat("Total archive candidates", summary.archive_candidates.total_now)}
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">Distribution by triage kind</h3>
        <div className="flex flex-wrap gap-2">
          {Object.entries(summary.by_triage_kind).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
            <span key={k} className="text-xs rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 px-2 py-0.5">
              {k}: <b>{v}</b>
            </span>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">Review candidates</h3>
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <select
            value={bucket}
            onChange={(e) => setBucket(e.target.value as typeof bucket)}
            className="rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm px-2 py-1.5"
          >
            <option value="recycle_bin">Recycle Bin</option>
            <option value="transactions">Transactions</option>
            <option value="all">All non-keep</option>
          </select>
          <button type="button" onClick={showCandidates} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold px-3 py-1.5 disabled:opacity-50">
            Show 100 archive candidates
          </button>
          {bucket !== "all" && (
            <>
              <button type="button" onClick={() => approve(bucket, true)} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-sm font-semibold px-3 py-1.5 disabled:opacity-50">
                Dry-run approve
              </button>
              <button type="button" onClick={() => approve(bucket, false)} disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-1.5 disabled:opacity-50">
                Approve batch (100)
              </button>
            </>
          )}
        </div>
        {candidates.length > 0 && (
          <div className="text-xs text-slate-500 dark:text-slate-400 mb-1">
            Showing {candidates.length} of {candTotal} in “{bucket}”.
          </div>
        )}
        <div className="space-y-1.5 max-h-[28rem] overflow-auto">
          {candidates.length === 0 && (
            <div className="text-sm text-slate-500 dark:text-slate-400">No candidates loaded. Pick a bucket and Show.</div>
          )}
          {candidates.map((c) => (
            <div key={c.packet_id} className="rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-slate-900 dark:text-slate-100">{c.subject || "(no subject)"}</div>
                  <div className="truncate text-slate-500">{c.sender || c.sender_domain} · {c.triage_kind}</div>
                </div>
                <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                  {c.action === "recycle_bin" ? "Recycle" : "Transactions"}{c.archive_eligible_now ? " · archive" : " · label"}
                </span>
              </div>
              {c.reasons.length > 0 && (
                <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">why: {c.reasons.join(" · ")}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-3 mb-2">
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Unsubscribe queue ({unsubCands.length})</h3>
          <div className="flex gap-2">
            <button type="button" onClick={() => approveUnsub(true)} disabled={busy || unsubCands.length === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50">
              Dry-run
            </button>
            <button type="button" onClick={() => approveUnsub(false)} disabled={busy || unsubCands.length === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50">
              Approve all
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          Ranked by ≥5 unread or ≥20 messages in 6 months, multi-signal. https unsubscribes fire a
          one-click request; mailto unsubscribes stage a draft you send (never auto-sent). All get
          archived + logged below.
        </p>
        <div className="space-y-1.5 max-h-80 overflow-auto">
          {unsubCands.length === 0 && <div className="text-sm text-slate-500 dark:text-slate-400">No unsubscribe candidates.</div>}
          {unsubCands.map((c) => (
            <div key={c.sender_domain} className="rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-slate-900 dark:text-slate-100">{c.sender_domain}</div>
                  <div className="truncate text-slate-500">{c.unread_count} unread · {c.message_count} msgs · {c.method}</div>
                </div>
                <span className="shrink-0 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-amber-200 dark:bg-amber-700/40 text-amber-800 dark:text-amber-200">
                  {c.method === "https_oneclick" ? "1-click" : c.method === "mailto_draft" ? "draft" : "archive"}
                </span>
              </div>
              {c.reasons.length > 0 && (
                <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5 truncate">why: {c.reasons.join(" · ")}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      {unsubLog.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">Unsubscribe log ({unsubLog.length})</h3>
          <div className="space-y-1.5 max-h-72 overflow-auto">
            {unsubLog.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs">
                <div className="min-w-0">
                  <div className="truncate text-slate-900 dark:text-slate-100">
                    {e.sender_domain}
                    {e.resubscription_detected_at && <span className="ml-1 text-rose-600">· re-subscribed?</span>}
                  </div>
                  <div className="truncate text-slate-500">{e.status} · {e.method} · {new Date(e.unsubscribed_at).toLocaleDateString()}</div>
                </div>
                {e.status !== "resubscribed" && (
                  <button type="button" onClick={() => resubscribe(e.id)} disabled={busy} className="shrink-0 text-sky-600 hover:underline">
                    Re-subscribe
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-1">Allowlist (always keep these senders)</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
          One sender domain per line. Mail from these is never archived (news, dev, social content you read).
        </p>
        <textarea
          value={allowlist}
          onChange={(e) => setAllowlist(e.target.value)}
          rows={6}
          className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-xs font-mono px-2.5 py-2"
        />
        <button type="button" onClick={saveAllowlist} disabled={busy}
          className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-1.5 disabled:opacity-50">
          Save allowlist
        </button>
      </div>
    </div>
  );
}

function ConnectorsSection() {
  return <ConnectorsManager />;
}

function ConnectorsSectionLegacy() {
  const [data, setData] = useState<ConnectorsResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [tab, setTab] = useState<"configured" | "available">("configured");
  const load = () => {
    api.connectors().then((d) => setData(d)).catch(() => undefined);
  };
  useEffect(() => {
    load();
  }, []);
  const runAll = async () => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.runConnectors();
      const total = Object.values(r.summary).reduce((acc, s) => acc + s.new, 0);
      setMsg(`Synced. ${total} new captures across ${Object.keys(r.summary).length} connectors.`);
      load();
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (!data) return <div className="text-sm text-slate-500 dark:text-slate-400">Loading connectors…</div>;

  // Group instances by kind so the Configured tab can render
  // "Gmail (2 accounts)" with both nested.
  const byKind: Record<string, typeof data.connectors> =
    data.instances_by_kind ??
    data.connectors.reduce<Record<string, typeof data.connectors>>((acc, c) => {
      (acc[c.kind] ||= []).push(c);
      return acc;
    }, {});
  const connectedKinds = Object.keys(byKind);
  const totalInstances = data.connectors.length;

  // "Action required" — connected OAuth instances whose last status
  // reads like an auth failure. Shown across BOTH tabs at the top.
  const needsAttention = data.connectors.filter((c) => {
    const s = (c.last_status || "").toLowerCase();
    return (
      s.includes("invalid_grant") ||
      s.includes("expired") ||
      s.includes("revoked") ||
      s.includes("unauthorized") ||
      s.includes("401") ||
      s.startsWith("error:")
    );
  });

  // One-tap recovery straight from the banner: route each failing
  // instance to the same connect/grant handshake its kind uses for the
  // initial connect. outlook-bridge in OAuth-failure mode re-runs
  // WOV's grant flow; in bridge-only (HMAC) mode there is no grant to
  // re-run — the banner returns null and the user falls back to the
  // per-card "Sync now" + Configure flow.
  const bannerReconnectHref = (
    c: ConnectorsResponse["connectors"][number],
  ): string | null => {
    if (c.kind === "gmail" || c.kind === "calendar")
      return `/api/connectors/google/start?provider=${encodeURIComponent(c.kind)}&scope=${encodeURIComponent(c.scope)}&returnTo=${encodeURIComponent("/settings/connectors")}`;
    if (c.kind === "github")
      return `/api/connectors/github/start?scope=${encodeURIComponent(c.scope)}&returnTo=${encodeURIComponent("/settings/connectors")}`;
    if (c.kind === "outlook-bridge" || c.kind === "mail-viewer") {
      // Only offer the WOV grant when the failure looks like an
      // OAuth failure (replay, refresh failed, 401). A bridge-only
      // failure (bridge_unreachable, missing config) is not fixed
      // by an OAuth handshake; surface no banner CTA.
      const s = (c.last_status || "").toLowerCase();
      const isOAuthFailure =
        s.includes("needs_reauth") ||
        s.includes("replay") ||
        s.includes("invalid_token") ||
        s.includes("refresh failed") ||
        s.includes("401");
      if (!isOAuthFailure) return null;
      return `/api/connectors/outlook-bridge/start?connectorId=${encodeURIComponent(c.id)}&returnTo=${encodeURIComponent("/settings/connectors")}`;
    }
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Connectors</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Every account, app, and entity Memso can pull from. Configured
            shows what is connected; Available is the full catalog. Connect
            more than one Google account per service from either tab.
          </p>
        </div>
        <button
          type="button"
          onClick={runAll}
          disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50"
        >
          {busy ? "Syncing…" : "Sync all"}
        </button>
      </div>
      {msg && <div className="text-xs text-slate-600 dark:text-slate-300">{msg}</div>}

      {needsAttention.length > 0 && (
        <div className="rounded-2xl border border-amber-400/40 bg-amber-500/10 p-4">
          <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
            <ChevronRight className="w-4 h-4" />
            <span className="text-sm font-bold">Action required</span>
          </div>
          <ul className="mt-2 space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
            {needsAttention.map((c) => {
              const href = bannerReconnectHref(c);
              return (
                <li
                  key={c.id}
                  className="flex items-center justify-between gap-3"
                >
                  <span>
                    <span className="font-semibold capitalize">{c.kind}</span> ·{" "}
                    {c.account} needs attention: {c.last_status}
                  </span>
                  {href && (
                    <a
                      href={href}
                      className="inline-flex flex-shrink-0 items-center gap-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5"
                      title={`Re-run the connect handshake for ${c.kind} (${c.account}). Instance id is preserved; only the auth refreshes.`}
                    >
                      Reconnect
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="flex items-center gap-1 border-b border-slate-200/70 dark:border-slate-700/50">
        <ConnectorsTabButton active={tab === "configured"} onClick={() => setTab("configured")}>
          Configured ({totalInstances})
        </ConnectorsTabButton>
        <ConnectorsTabButton active={tab === "available"} onClick={() => setTab("available")}>
          Available
        </ConnectorsTabButton>
      </div>

      {tab === "configured" ? (
        <div className="space-y-6">
          <section className="space-y-4">
            {connectedKinds.length === 0 ? (
              <p className="text-sm text-slate-500 dark:text-slate-400">
                No accounts connected yet. Open the Available tab and use a
                Connect button to wire your first source.
              </p>
            ) : (
              connectedKinds.map((kind) => {
                const instances = byKind[kind]!;
                const reg = data.registered.find((r) => r.kind === kind);
                return (
                  <div key={kind} className="space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 capitalize">
                        {kind.replace(/_/g, " ")}
                      </h3>
                      <span className="text-[11px] text-slate-500 dark:text-slate-400">
                        {instances.length} account{instances.length === 1 ? "" : "s"}
                      </span>
                      {reg?.supportsMultipleInstances && (
                        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200">
                          multi-account
                        </span>
                      )}
                      {reg?.supportsMultipleInstances && (reg?.oauthFlow === "google" || reg?.oauthFlow === "github") && (
                        <ConnectAnotherButton
                          kind={kind}
                          defaultScope={(reg.defaultPrivacyScope as any) || "personal"}
                          oauthFlow={reg.oauthFlow}
                        />
                      )}
                    </div>
                    <ul className="space-y-2">
                      {instances.map((c) => (
                        <ConnectorCard key={c.id} connector={c} onChanged={load} />
                      ))}
                    </ul>
                  </div>
                );
              })
            )}
          </section>
          <div className="space-y-3 pt-4 border-t border-slate-200/70 dark:border-slate-700/50">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              Service connectors
            </h3>
            <ServiceConnectorsPanel />
          </div>
          <div className="space-y-3 pt-4 border-t border-slate-200/70 dark:border-slate-700/50">
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              Cowork captures
            </h3>
            <WorkConnectorsPanel />
          </div>
        </div>
      ) : (
        <ConnectorCatalog mode="settings" />
      )}
    </div>
  );
}

function ConnectorsTabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? "text-sm font-semibold px-3 py-2 border-b-2 border-sky-600 text-sky-700 dark:text-sky-300 -mb-px"
          : "text-sm font-medium px-3 py-2 border-b-2 border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
      }
    >
      {children}
    </button>
  );
}

/** Per-connector card: kind badge, account email, last sync time +
 *  status, and per-row "Sync now" + "Disconnect" buttons. Disconnect
 *  is two-click confirm. For Google connectors (gmail / calendar) it
 *  also revokes the user_connections row so the OAuth grant is no
 *  longer usable; the user's Google-side permission still needs a
 *  separate revoke at myaccount.google.com/permissions. */
/** "Service connectors" panel: live integrations Memso talks to over
 *  the network or a local sidecar. WhatsApp pairing surfaces here so
 *  the QR + Mint flow lives both at /whatsapp and at /settings/connectors,
 *  matching the surface duplication WOV had for its operational toggles.
 *  Each card polls its own status endpoint independently — one offline
 *  service doesn't block the others from rendering.
 *
 *  All three cards reuse the same Card shell pattern so adding Slack /
 *  Gmail / Calendar service-style cards in the future is a copy-paste
 *  + new fetch URL away. */
/** Per-connector scope state, fetched once + passed down to each card.
 *  Source: GET /api/work-connectors → { connectors: [{ kind, privacy_scope, ... }] }.
 *  privacy_scope is null until the user picks via the scope picker —
 *  ServiceConnectorsPanel surfaces an "Action needed" banner whenever
 *  any card has a null scope. */
type WorkConnectorScopeRow = {
  kind: string;
  displayName: string;
  enabled: boolean;
  privacy_scope: string | null;
};

function useWorkConnectorScopes() {
  const [rows, setRows] = useState<WorkConnectorScopeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    try {
      const r = await fetch("/api/work-connectors", { credentials: "same-origin" });
      const j = await r.json();
      const list = (j?.connectors ?? []) as Array<Record<string, unknown>>;
      setRows(list.map((row) => ({
        kind: String(row.kind ?? ""),
        displayName: String(row.displayName ?? row.kind ?? ""),
        enabled: !!row.enabled,
        privacy_scope: (row.privacy_scope as string | null) ?? null,
      })));
    } catch { /* best-effort */ }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);
  return { rows, loading, reload };
}

function ServiceConnectorsPanel() {
  const { rows: scopes, reload: reloadScopes } = useWorkConnectorScopes();
  const scopeFor = useCallback((kind: string) => scopes.find((r) => r.kind === kind)?.privacy_scope ?? null, [scopes]);
  // Setup-incomplete check: any of the kinds we surface as cards has
  // a null privacy_scope. We deliberately consider a NULL as
  // "incomplete" rather than a kind-based default — that's the whole
  // point of this work.
  const incompleteKinds = scopes.filter((r) => r.privacy_scope == null).map((r) => r.kind);
  const haveIncomplete = incompleteKinds.length > 0;
  return (
    <>
      {haveIncomplete && (
        <div className="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200 mb-3 flex items-start gap-2">
          <span className="mt-px">⚠</span>
          <div>
            <strong>Action needed:</strong> {incompleteKinds.length} connector{incompleteKinds.length === 1 ? "" : "s"} {incompleteKinds.length === 1 ? "has" : "have"} no privacy scope chosen yet ({incompleteKinds.join(", ")}). Pick a scope inline on each card to complete setup. Captures from a connector without a scope route through the local privacy gate as a fallback, but explicit scope is better.
          </div>
        </div>
      )}
      <ul className="space-y-3">
        <li><WhatsAppConnectorCard scope={scopeFor("whatsapp")} onScopeChange={reloadScopes} /></li>
        <li><ObsidianConnectorCard scope={scopeFor("obsidian")} onScopeChange={reloadScopes} /></li>
        <li><OutlookBridgeCard scope={scopeFor("outlook")} onScopeChange={reloadScopes} /></li>
        <li><TeamsSidecarCard scope={scopeFor("teams")} onScopeChange={reloadScopes} /></li>
      </ul>
    </>
  );
}

/** Reusable scope picker. Six radio options matching VALID_CONNECTOR_SCOPES
 *  on the backend (private / personal / family / social / work / business).
 *  Each option carries a one-line hint so the user can choose without
 *  bouncing to docs. Save is disabled until selection. POSTs to
 *  /api/work-connectors/<kind>/scope which the backend's runner.ts
 *  validates against VALID_CONNECTOR_SCOPES. */
const SCOPE_OPTIONS: Array<{ id: string; label: string; hint: string }> = [
  { id: "personal", label: "Personal", hint: "Day-to-day life. Routes through cloud AI; not for sensitive subjects." },
  { id: "work",     label: "Work",     hint: "Job context (OpenText). Cloud AI eligible; goes through your work bindings." },
  { id: "business", label: "Business", hint: "Side business / consulting / clients. Cloud AI eligible." },
  { id: "family",   label: "Family",   hint: "Spouse, kids, parents. Sensitive — Memso prefers local AI by default." },
  { id: "social",   label: "Social",   hint: "Friends + groups not in family or work." },
  { id: "private",  label: "Private",  hint: "Strictly local AI. Captures never leave the Mac. Use for medical / legal / etc." },
];

function ConnectorScopeSetup({
  kind,
  current,
  defaultHint,
  onSaved,
  compact,
}: {
  kind: string;
  /** Current saved scope, or null when unset. */
  current: string | null;
  /** Pre-selected option (visual only) when current is null. The user
   *  must still click Save to confirm. */
  defaultHint?: string;
  onSaved: (scope: string) => void;
  /** When true, render in a tight inline strip suitable for a card
   *  body. When false (the default), render with a heading + full
   *  vertical layout — used when the picker is the primary action. */
  compact?: boolean;
}) {
  const [chosen, setChosen] = useState<string | null>(current ?? defaultHint ?? null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const save = async () => {
    if (!chosen || busy) return;
    setBusy(true); setErr(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch(`/api/work-connectors/${encodeURIComponent(kind)}/scope`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
        body: JSON.stringify({ scope: chosen }),
      });
      const j = await r.json();
      if (!r.ok || j?.error) throw new Error(j?.message || j?.error || `failed (${r.status})`);
      onSaved(chosen);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  const containerCls = compact
    ? "rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/20 p-3 space-y-2"
    : "space-y-3";
  return (
    <div className={containerCls}>
      {!compact && (
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
          Pick the privacy scope for this connector
        </div>
      )}
      {compact && (
        <div className="text-[11px] font-semibold text-amber-900 dark:text-amber-100">
          Setup incomplete — pick a scope to finish.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {SCOPE_OPTIONS.map((o) => {
          const sel = chosen === o.id;
          return (
            <label
              key={o.id}
              className={
                "flex items-start gap-2 rounded-md border px-2.5 py-1.5 cursor-pointer text-[11px] transition-colors " +
                (sel
                  ? "border-sky-500 bg-sky-50 dark:bg-sky-950/40 text-slate-900 dark:text-slate-100"
                  : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300")
              }
            >
              <input
                type="radio"
                name={`scope-${kind}`}
                value={o.id}
                checked={sel}
                onChange={() => setChosen(o.id)}
                className="mt-0.5"
              />
              <div className="min-w-0">
                <div className="font-semibold">{o.label}</div>
                <div className="text-slate-500 dark:text-slate-400 leading-tight">{o.hint}</div>
              </div>
            </label>
          );
        })}
      </div>
      {err && <div className="text-[11px] text-rose-600 dark:text-rose-400">{err}</div>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={save}
          disabled={!chosen || busy || chosen === current}
          className="text-[11px] font-semibold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 disabled:opacity-50"
        >
          {busy ? "Saving…" : current ? "Update scope" : "Save scope"}
        </button>
        {current && (
          <span className="text-[11px] text-slate-500 dark:text-slate-400">
            Currently: <span className="font-semibold">{current}</span>
          </span>
        )}
      </div>
    </div>
  );
}

type StatusTone = "ok" | "warn" | "err" | "unknown";

function StatusDot({ tone }: { tone: StatusTone }) {
  const cls =
    tone === "ok" ? "bg-emerald-500"
    : tone === "warn" ? "bg-amber-500"
    : tone === "err" ? "bg-rose-500"
    : "bg-slate-400";
  return <span className={`inline-block w-2 h-2 rounded-full ${cls} flex-shrink-0`} aria-hidden />;
}

function ServiceCardShell({
  icon, name, scope, tone, statusLabel, stats, primary, advanced, children,
}: {
  icon: React.ReactNode;
  name: string;
  scope: "work" | "personal";
  tone: StatusTone;
  statusLabel: string;
  stats?: React.ReactNode;
  primary?: React.ReactNode;
  advanced?: React.ReactNode;
  children?: React.ReactNode;
}) {
  return (
    <div className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-700 dark:text-slate-200 flex-shrink-0">
          {icon}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-slate-900 dark:text-slate-100">{name}</span>
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
              {scope}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <StatusDot tone={tone} />
              <span className="text-[11px] text-slate-600 dark:text-slate-300">{statusLabel}</span>
            </span>
          </div>
          {stats && <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{stats}</div>}
        </div>
        {primary && <div className="flex-shrink-0">{primary}</div>}
      </div>
      {children}
      {advanced && (
        <details className="border-t border-slate-200 dark:border-slate-700 pt-2">
          <summary className="text-[11px] text-sky-600 dark:text-sky-400 cursor-pointer">Advanced</summary>
          <div className="mt-2">{advanced}</div>
        </details>
      )}
    </div>
  );
}

/** WhatsApp pairing + status. Hits the same endpoints the /whatsapp tab
 *  uses (frontend/src/pages/WhatsApp.tsx) so the two surfaces share
 *  state — pair on either page, see the change reflected on the other.
 *  Polls /pair/status every 3s; QR PNG cache-busts via a tick on the
 *  same cadence. */
function WhatsAppConnectorCard({ scope, onScopeChange }: { scope: string | null; onScopeChange: () => void }) {
  type PairStatus = {
    paired?: boolean;
    awaiting_qr?: boolean;
    phone?: string;
    last_message_at?: string;
    error?: string;
  };
  const [status, setStatus] = useState<PairStatus | null>(null);
  const [statErr, setStatErr] = useState<string | null>(null);
  const [qrTick, setQrTick] = useState(0);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch("/api/whatsapp/v1/pair/status", { credentials: "same-origin" });
        const j = await r.json();
        if (cancelled) return;
        if (r.ok) {
          setStatus(j as PairStatus);
          setStatErr(null);
        } else {
          setStatErr(j?.error?.message || `bridge ${r.status}`);
        }
      } catch (e) {
        if (cancelled) return;
        setStatErr(e instanceof Error ? e.message : String(e));
      }
      if (!cancelled) setQrTick((t) => t + 1);
    };
    void poll();
    const id = window.setInterval(poll, 3000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  const mintRun = async () => {
    if (busy) return;
    setBusy(true); setMsg(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch("/api/whatsapp/bootstrap", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
      });
      const j = await r.json();
      if (!r.ok || j?.error) throw new Error(j?.error?.message || `failed (${r.status})`);
      setMsg(`Run minted: ${(j.run_id ?? "").slice(0, 12)}…`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (statErr) {
    return (
      <ServiceCardShell
        icon={<MessageCircle className="w-4 h-4" />}
        name="WhatsApp"
        scope="personal"
        tone="err"
        statusLabel="Sidecar unreachable"
      >
        <div className="rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-200">
          <code>{statErr}</code>. Make sure the <code>waki-whatsapp-connector</code> launchd job is running on this Mac.
        </div>
      </ServiceCardShell>
    );
  }

  // WhatsApp's primary card. We always render the same shell so the
  // scope picker has a stable place to live; the body switches on
  // pair status (paired summary vs QR + Mint).
  const cardScope = (scope as "personal" | "work" | undefined) === "work" ? "work" : "personal";
  const advanced = scope ? (
    <ConnectorScopeSetup kind="whatsapp" current={scope} onSaved={onScopeChange} compact={false} />
  ) : undefined;
  const scopeBlock = scope == null ? (
    <ConnectorScopeSetup kind="whatsapp" current={null} defaultHint="personal" onSaved={onScopeChange} compact />
  ) : null;

  if (status?.paired) {
    return (
      <ServiceCardShell
        icon={<MessageCircle className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
        name="WhatsApp"
        scope={cardScope}
        tone="ok"
        statusLabel={status.phone ? `Connected as ${status.phone}` : "Paired"}
        stats={
          <>
            {status.last_message_at && <>Last message {formatRelative(status.last_message_at)}</>}
            {scope && <> · scope <span className="font-semibold">{scope}</span></>}
          </>
        }
        advanced={advanced}
      >
        {scopeBlock}
      </ServiceCardShell>
    );
  }

  return (
    <ServiceCardShell
      icon={<MessageCircle className="w-4 h-4 text-amber-600 dark:text-amber-400" />}
      name="WhatsApp"
      scope={cardScope}
      tone="warn"
      statusLabel="Awaiting QR scan"
      primary={
        <button
          type="button"
          onClick={mintRun}
          disabled={busy}
          className="text-[11px] font-semibold rounded-md bg-sky-600 hover:bg-sky-700 text-white px-2.5 py-1 disabled:opacity-50"
        >
          {busy ? "Minting…" : "Mint pairing run"}
        </button>
      }
      advanced={advanced}
    >
      {scopeBlock}
      <div className="grid grid-cols-[160px_1fr] gap-3 items-start">
        <img
          src={`/api/whatsapp/v1/pair/qr.png?t=${qrTick}`}
          alt="WhatsApp pairing QR"
          className="w-40 h-40 rounded-md bg-white p-1 border border-slate-200 dark:border-slate-700"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.opacity = "0.3"; }}
        />
        <div className="text-[11px] text-slate-600 dark:text-slate-300 space-y-1.5">
          <p>
            On your phone open <span className="font-semibold">WhatsApp → Settings → Linked Devices → Link a Device</span> and scan the QR.
          </p>
          <p className="text-slate-500 dark:text-slate-400">
            Auth state persists at <code className="text-[10px] bg-slate-100 dark:bg-slate-800 px-1 rounded">~/.waki-whatsapp-connector/auth-state/</code>; subsequent restarts won't re-prompt.
          </p>
          {msg && <p className="text-emerald-700 dark:text-emerald-300 mt-1">{msg}</p>}
        </div>
      </div>
    </ServiceCardShell>
  );
}

/** Obsidian sidecar status + vault path picker + backfill trigger.
 *  Polls /api/obsidian/v1/status every 5s, surfaces the current vault
 *  path, lets the user repoint it, and exposes a Run-backfill button
 *  whose result includes the count of newly ingested notes. */
function ObsidianConnectorCard({ scope, onScopeChange }: { scope: string | null; onScopeChange: () => void }) {
  type ObsidianStatus = {
    paired?: boolean;
    user_id?: string | null;
    run_id?: string | null;
    base_url?: string | null;
    privacy_scope?: string | null;
    vault_path?: string | null;
    vault_set_at?: string | null;
    notes_ingested?: number;
    notes_deleted?: number;
  };
  type Summary = {
    scanned: number;
    ingested: number;
    unchanged: number;
    failed: number;
    uploaded: { accepted: number; skipped: number };
  };
  const [status, setStatus] = useState<ObsidianStatus | null>(null);
  const [statErr, setStatErr] = useState<string | null>(null);
  const [vaultDraft, setVaultDraft] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);

  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch("/api/obsidian/v1/status", { credentials: "same-origin" });
        const j = await r.json();
        if (cancelled) return;
        if (r.ok) {
          setStatus(j as ObsidianStatus);
          setStatErr(null);
        } else {
          setStatErr(j?.error?.message || j?.error || `bridge ${r.status}`);
        }
      } catch (e) {
        if (cancelled) return;
        setStatErr(e instanceof Error ? e.message : String(e));
      }
    };
    void poll();
    const id = window.setInterval(poll, 5000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  const saveVault = async () => {
    const path = vaultDraft.trim();
    if (!path) return;
    setBusy(true); setMsg(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch("/api/obsidian/connector/vault", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
        body: JSON.stringify({ vault_path: path }),
      });
      const j = await r.json();
      if (!r.ok || j?.error) throw new Error(j?.error?.message || j?.error || `failed (${r.status})`);
      setMsg(`Vault path saved: ${path}`);
      setVaultDraft("");
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const mintRun = async () => {
    setBusy(true); setMsg(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch("/api/obsidian/bootstrap", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
      });
      const j = await r.json();
      if (!r.ok || j?.error) throw new Error(j?.error?.message || j?.error || `failed (${r.status})`);
      setMsg(`Run minted: ${(j.run_id ?? "").slice(0, 12)}…`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  const runBackfill = async () => {
    setBusy(true); setMsg(null); setSummary(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch("/api/obsidian/backfill", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
      });
      const j = await r.json();
      if (!r.ok || j?.error) throw new Error(j?.error?.message || j?.error || `failed (${r.status})`);
      const s = j?.summary as Summary | undefined;
      if (s) setSummary(s);
      setMsg(`Backfill complete: scanned ${s?.scanned ?? "?"}, ingested ${s?.ingested ?? "?"} notes.`);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  };

  if (statErr) {
    return (
      <ServiceCardShell
        icon={<FileText className="w-4 h-4" />}
        name="Obsidian"
        scope="personal"
        tone="err"
        statusLabel="Sidecar unreachable"
      >
        <div className="rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-200">
          <code>{statErr}</code>. Make sure the <code>waki-obsidian-connector</code> launchd job is running on this Mac.
        </div>
      </ServiceCardShell>
    );
  }

  const cardScope = (scope as "personal" | "work" | undefined) === "work" ? "work" : "personal";
  const advanced = scope ? (
    <ConnectorScopeSetup kind="obsidian" current={scope} onSaved={onScopeChange} compact={false} />
  ) : undefined;
  const scopeBlock = scope == null ? (
    <ConnectorScopeSetup kind="obsidian" current={null} defaultHint="personal" onSaved={onScopeChange} compact />
  ) : null;

  const vaultLine = status?.vault_path
    ? <>Watching <code className="text-[10px] bg-slate-100 dark:bg-slate-800 px-1 rounded">{status.vault_path}</code></>
    : <span className="text-amber-600 dark:text-amber-400">No vault path set yet</span>;

  const stats = (
    <>
      {vaultLine}
      {status?.notes_ingested != null && <> · {status.notes_ingested} notes indexed</>}
      {status?.notes_deleted ? <> · {status.notes_deleted} flagged deleted</> : null}
      {scope && <> · scope <span className="font-semibold">{scope}</span></>}
    </>
  );

  const tone: StatusTone = status?.paired && status?.vault_path ? "ok" : "warn";
  const statusLabel = status?.paired
    ? status?.vault_path ? "Connected" : "Paired — set a vault path"
    : "Awaiting pairing";

  return (
    <ServiceCardShell
      icon={<FileText className="w-4 h-4 text-violet-600 dark:text-violet-400" />}
      name="Obsidian"
      scope={cardScope}
      tone={tone}
      statusLabel={statusLabel}
      stats={stats}
      primary={
        !status?.paired ? (
          <button
            type="button"
            onClick={mintRun}
            disabled={busy}
            className="text-[11px] font-semibold rounded-md bg-sky-600 hover:bg-sky-700 text-white px-2.5 py-1 disabled:opacity-50"
          >
            {busy ? "Minting…" : "Mint pairing run"}
          </button>
        ) : (
          <button
            type="button"
            onClick={runBackfill}
            disabled={busy || !status?.vault_path}
            className="text-[11px] font-semibold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white px-2.5 py-1 disabled:opacity-50"
          >
            {busy ? "Running…" : "Run backfill"}
          </button>
        )
      }
      advanced={advanced}
    >
      {scopeBlock}
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2 items-end">
        <label className="flex flex-col gap-1 text-[11px] text-slate-600 dark:text-slate-300">
          Vault path
          <input
            type="text"
            value={vaultDraft}
            onChange={(e) => setVaultDraft(e.target.value)}
            placeholder={status?.vault_path ?? "/Users/you/Documents/Obsidian/MainVault"}
            className="px-2 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 font-mono text-[11px]"
          />
        </label>
        <button
          type="button"
          onClick={saveVault}
          disabled={busy || !vaultDraft.trim()}
          className="text-[11px] font-semibold rounded-md border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 px-2.5 py-1.5 disabled:opacity-50"
        >
          Save vault
        </button>
      </div>
      {summary && (
        <div className="text-[11px] grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1">
          <ObsidianStat label="Scanned" value={summary.scanned} />
          <ObsidianStat label="Ingested" value={summary.ingested} />
          <ObsidianStat label="Unchanged" value={summary.unchanged} />
          <ObsidianStat label="Accepted" value={summary.uploaded.accepted} />
        </div>
      )}
      {msg && <p className="text-emerald-700 dark:text-emerald-300 text-[11px] mt-1">{msg}</p>}
    </ServiceCardShell>
  );
}

function ObsidianStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 px-2 py-1.5 bg-slate-50 dark:bg-slate-900/40">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</div>
      <div className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">{value}</div>
    </div>
  );
}

/** Outlook bridge status + classifier + Memso backlog ingest. Three
 *  concerns share this card:
 *
 *  1. Heartbeat from /api/bridge/helloworld — bridge online + current
 *     SMTP + inbox/calendar counts.
 *  2. Classifier status from /api/bridge/v1/llm/classifier-status —
 *     auto-classify on/off, per-folder progress, ETA.
 *  3. Backlog ingestion via /api/connectors/outlook-bridge/backfill/*
 *     — pulls 1y past + 30d forward of calendar events plus 30d of
 *     email. Two-step UX: Estimate → Confirm → run with live progress.
 *
 *  Plus the connector-level scope picker from the Connectors panel.
 */
function OutlookBridgeCard({ scope, onScopeChange }: { scope: string | null; onScopeChange: () => void }) {
  type Hello = {
    current_user_smtp?: string;
    current_user_display_name?: string;
    mail_count_inbox?: number;
    calendar_count_30d?: number;
    outlook_version?: string;
    timestamp_utc?: string;
  };
  type ClassifierPerFolder = { total: number; processed: number; skipped?: number; lastClassifiedAt?: string };
  type ClassifierStatus = {
    autoClassify: boolean;
    backfillEnabled: boolean;
    inboxTotal: number;
    estimatedUnclassified: number;
    eta_seconds?: number;
    processedThisSession: number;
    failuresThisSession: number;
    lastTickAt?: string | null;
    perFolder?: Record<string, ClassifierPerFolder>;
  };
  type BackfillRun = {
    id: string;
    kind: "outlook-calendar" | "outlook-mail";
    window: { start: string; end: string };
    estimatedCount: number;
    estimatedCostUsd: number;
    actualCostUsd: number;
    provider: string | null;
    model: string | null;
    progress: {
      pagesProcessed: number;
      pagesTotal: number;
      eventsProcessed: number;
      eventsTotal: number;
      packetsInserted: number;
      skippedDuplicates: number;
      errored: number;
    };
    status: "queued" | "running" | "finished" | "errored" | "aborted";
    errorMessage: string | null;
    startedAt: string;
    finishedAt: string | null;
  };

  type BridgeEndpointSnapshot = {
    endpoint: string;
    total: number;
    success: number;
    s4xx: number;
    s5xx: number;
    timeout: number;
    abort: number;
    errorRate: number;
    avgRetries: number;
    p50: number;
    p95: number;
    p99: number;
  };
  type BridgeMetrics = {
    windowMs: number;
    generatedAt: string;
    totals: {
      total: number;
      success: number;
      s4xx: number;
      s5xx: number;
      timeout: number;
      abort: number;
      errorRate: number;
      p50: number;
      p95: number;
      p99: number;
    };
    endpoints: BridgeEndpointSnapshot[];
    health: "healthy" | "degraded" | "unstable" | "idle";
  };

  const [hello, setHello] = useState<Hello | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [classifier, setClassifier] = useState<ClassifierStatus | null>(null);
  const [activeRuns, setActiveRuns] = useState<BackfillRun[]>([]);
  const [recentRuns, setRecentRuns] = useState<BackfillRun[]>([]);
  const [metrics, setMetrics] = useState<BridgeMetrics | null>(null);

  // Estimate + run state — keyed by kind so calendar + mail can show in parallel.
  type Estimate = {
    estimatedCount: number;
    estimatedCostUsd: number;
    provider: string;
    model: string;
    window: { start: string; end: string };
  };
  const [calEst, setCalEst] = useState<Estimate | null>(null);
  const [mailEst, setMailEst] = useState<Estimate | null>(null);
  const [calBusy, setCalBusy] = useState(false);
  const [mailBusy, setMailBusy] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);

  // Heartbeat poll (10s).
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch("/api/bridge/helloworld", { credentials: "same-origin" });
        const j = await r.json();
        if (cancelled) return;
        if (r.ok && j) { setHello(j as Hello); setErr(null); }
        else setErr(`bridge ${r.status}`);
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e.message : String(e));
      }
    };
    void poll();
    const id = window.setInterval(poll, 10_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  // Classifier status poll (15s).
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch("/api/bridge/v1/llm/classifier-status", { credentials: "same-origin" });
        const j = await r.json();
        if (cancelled) return;
        if (r.ok && j?.data) setClassifier(j.data as ClassifierStatus);
      } catch { /* bridge offline; heartbeat poll already surfaces err */ }
    };
    void poll();
    const id = window.setInterval(poll, 15_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  // Bridge proxy metrics poll (15s). Note: hits our own daemon, not
  // the bridge VM, so it stays cheap even when the bridge is offline.
  useEffect(() => {
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await fetch("/api/bridge/metrics", { credentials: "same-origin" });
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        setMetrics(j as BridgeMetrics);
      } catch { /* daemon issue, not bridge — silent */ }
    };
    void poll();
    const id = window.setInterval(poll, 15_000);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  // Active + recent backfill runs poll (5s while any is running, else 30s).
  useEffect(() => {
    let cancelled = false;
    const reload = async () => {
      try {
        const [activeR, recentR] = await Promise.all([
          fetch("/api/connectors/outlook-bridge/backfill/active", { credentials: "same-origin" }),
          fetch("/api/connectors/outlook-bridge/backfill/runs?limit=5", { credentials: "same-origin" }),
        ]);
        if (cancelled) return;
        const aJ = activeR.ok ? await activeR.json() : { runs: [] };
        const rJ = recentR.ok ? await recentR.json() : { runs: [] };
        setActiveRuns((aJ.runs ?? []) as BackfillRun[]);
        setRecentRuns((rJ.runs ?? []) as BackfillRun[]);
      } catch { /* ignore; will retry on next tick */ }
    };
    void reload();
    const ms = activeRuns.some((r) => r.status === "running" || r.status === "queued") ? 5_000 : 30_000;
    const id = window.setInterval(reload, ms);
    return () => { cancelled = true; window.clearInterval(id); };
  }, [activeRuns.some((r) => r.status === "running" || r.status === "queued")]);

  const fetchEstimate = async (kind: "outlook-calendar" | "outlook-mail") => {
    setRunMsg(null);
    if (kind === "outlook-calendar") setCalBusy(true); else setMailBusy(true);
    try {
      const params = kind === "outlook-calendar"
        ? "kind=outlook-calendar&days_back=365&days_forward=30"
        : "kind=outlook-mail&days_back=30";
      const r = await fetch(`/api/connectors/outlook-bridge/backfill/estimate?${params}`, { credentials: "same-origin" });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.message || j?.error || `HTTP ${r.status}`);
      const est: Estimate = {
        estimatedCount: j.estimatedCount,
        estimatedCostUsd: j.estimatedCostUsd,
        provider: j.provider,
        model: j.model,
        window: j.window,
      };
      if (kind === "outlook-calendar") setCalEst(est); else setMailEst(est);
    } catch (e) {
      setRunMsg(e instanceof Error ? e.message : String(e));
    } finally {
      if (kind === "outlook-calendar") setCalBusy(false); else setMailBusy(false);
    }
  };

  const startBackfill = async (kind: "outlook-calendar" | "outlook-mail") => {
    const est = kind === "outlook-calendar" ? calEst : mailEst;
    if (!est) { setRunMsg("Click 'Estimate' first."); return; }
    if (kind === "outlook-calendar") setCalBusy(true); else setMailBusy(true);
    setRunMsg(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch("/api/connectors/outlook-bridge/backfill/start", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
        body: JSON.stringify({
          kind,
          days_back: kind === "outlook-calendar" ? 365 : 30,
          days_forward: kind === "outlook-calendar" ? 30 : 0,
          confirmedCostUsd: est.estimatedCostUsd,
        }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.message || j?.error || `HTTP ${r.status}`);
      setRunMsg(`Started ${kind} backfill (run ${j.runId}); ${est.estimatedCount.toLocaleString()} items, ~$${est.estimatedCostUsd.toFixed(2)}`);
      if (kind === "outlook-calendar") setCalEst(null); else setMailEst(null);
    } catch (e) {
      setRunMsg(e instanceof Error ? e.message : String(e));
    } finally {
      if (kind === "outlook-calendar") setCalBusy(false); else setMailBusy(false);
    }
  };

  if (err) {
    return (
      <ServiceCardShell
        icon={<MailIcon className="w-4 h-4" />}
        name="Outlook bridge"
        scope="work"
        tone="err"
        statusLabel="Offline"
        advanced={
          <div className="text-[11px] text-slate-500 dark:text-slate-400">
            Restart the VSTO add-in inside Parallels: <code>schtasks /Run /TN RestartOutlookBridge</code> or run <code>scripts\install-bridge.ps1</code>.
          </div>
        }
      >
        <div className="rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-200">
          <code>{err}</code>
        </div>
      </ServiceCardShell>
    );
  }

  if (!hello) {
    return (
      <ServiceCardShell
        icon={<MailIcon className="w-4 h-4" />}
        name="Outlook bridge"
        scope="work"
        tone="unknown"
        statusLabel="Probing…"
      />
    );
  }

  // Outlook is wahmed@opentext.com — strong work hint, but the user
  // still picks explicitly per Waqas's principle.
  const cardScope = (scope as "work" | "personal" | undefined) === "personal" ? "personal" : "work";
  const calRun = activeRuns.find((r) => r.kind === "outlook-calendar")
              ?? recentRuns.find((r) => r.kind === "outlook-calendar");
  const mailRun = activeRuns.find((r) => r.kind === "outlook-mail")
               ?? recentRuns.find((r) => r.kind === "outlook-mail");

  return (
    <ServiceCardShell
      icon={<MailIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
      name="Outlook bridge"
      scope={cardScope}
      tone="ok"
      statusLabel={`Connected as ${hello.current_user_smtp ?? "unknown"}`}
      stats={
        <>
          {(hello.mail_count_inbox ?? 0).toLocaleString()} inbox · {(hello.calendar_count_30d ?? 0).toLocaleString()} cal/30d
          {hello.outlook_version && <> · Outlook {hello.outlook_version}</>}
          {scope && <> · scope <span className="font-semibold">{scope}</span></>}
        </>
      }
      advanced={
        <div className="text-[11px] text-slate-500 dark:text-slate-400 space-y-3">
          <div>Last heartbeat <span className="font-mono">{hello.timestamp_utc ?? "—"}</span></div>
          {classifier && (
            <div className="space-y-1">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">In-place classifier</div>
              <div>
                Auto-classify {classifier.autoClassify ? "ON" : "off"} · backfill {classifier.backfillEnabled ? "ON" : "off"}
                {typeof classifier.estimatedUnclassified === "number" && classifier.estimatedUnclassified > 0
                  ? ` · ~${classifier.estimatedUnclassified.toLocaleString()} unclassified`
                  : ""}
                {typeof classifier.eta_seconds === "number" && classifier.eta_seconds > 0
                  ? ` · ETA ${formatEta(classifier.eta_seconds)}`
                  : ""}
              </div>
              {classifier.perFolder && Object.keys(classifier.perFolder).length > 0 && (
                <div className="text-[10px] text-slate-500 dark:text-slate-400 grid grid-cols-2 gap-x-3 gap-y-0.5">
                  {Object.entries(classifier.perFolder).map(([name, pf]) => (
                    <div key={name}>
                      <span className="font-mono">{name}</span>: {(pf.processed ?? 0).toLocaleString()}/{(pf.total ?? 0).toLocaleString()}
                    </div>
                  ))}
                </div>
              )}
              {classifier.lastTickAt && (
                <div className="text-[10px] text-slate-500">Last tick {formatRelative(classifier.lastTickAt)}</div>
              )}
            </div>
          )}
          {metrics && <BridgeMetricsBlock metrics={metrics} />}
          <div>Restart command (run on Parallels): <code>scripts\install-bridge.ps1</code></div>
          <ConnectorScopeSetup kind="outlook" current={scope} defaultHint="work" onSaved={onScopeChange} compact={false} />
        </div>
      }
    >
      {scope == null && (
        <ConnectorScopeSetup kind="outlook" current={null} defaultHint="work" onSaved={onScopeChange} compact />
      )}
      {/* ── Memso ingest section — calendar + email backlog ───────────── */}
      <div className="border-t border-slate-200 dark:border-slate-700 pt-3 space-y-3">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Backlog ingest → Memso packets</div>
        <BackfillRow
          kind="outlook-calendar"
          label="Calendar (1y back · 30d forward)"
          est={calEst}
          run={calRun ?? null}
          busy={calBusy}
          onEstimate={() => fetchEstimate("outlook-calendar")}
          onStart={() => startBackfill("outlook-calendar")}
        />
        <BackfillRow
          kind="outlook-mail"
          label="Email (30d back)"
          est={mailEst}
          run={mailRun ?? null}
          busy={mailBusy}
          onEstimate={() => fetchEstimate("outlook-mail")}
          onStart={() => startBackfill("outlook-mail")}
        />
        {runMsg && (
          <div className="text-[11px] text-slate-600 dark:text-slate-300">
            {runMsg}
          </div>
        )}
      </div>
    </ServiceCardShell>
  );
}

/** Last-hour proxy metrics block. The "are 502s actually gone?" view.
 *
 *  Cache-hit calls are NOT counted in the totals — those never reached
 *  the bridge, so including them would dilute the error rate. The
 *  health pill maps directly to s5xx/total: green <0.5%, amber 0.5-2%,
 *  red >2%. "idle" appears when no real bridge calls have happened in
 *  the rolling hour (e.g. fresh daemon restart or all reads served
 *  from cache).
 */
function BridgeMetricsBlock({ metrics }: { metrics: {
  windowMs: number;
  generatedAt: string;
  totals: { total: number; success: number; s4xx: number; s5xx: number; timeout: number; abort: number; errorRate: number; p50: number; p95: number; p99: number };
  endpoints: Array<{ endpoint: string; total: number; s5xx: number; errorRate: number; avgRetries: number; p50: number; p95: number; p99: number }>;
  health: "healthy" | "degraded" | "unstable" | "idle";
} }) {
  const t = metrics.totals;
  const pillByHealth: Record<typeof metrics.health, { bg: string; text: string; border: string; label: string }> = {
    healthy:  { bg: "bg-emerald-50 dark:bg-emerald-950/30", text: "text-emerald-700 dark:text-emerald-300", border: "border-emerald-300 dark:border-emerald-700", label: "Bridge healthy" },
    degraded: { bg: "bg-amber-50 dark:bg-amber-950/30",     text: "text-amber-700 dark:text-amber-300",     border: "border-amber-300 dark:border-amber-700",     label: "Bridge degraded" },
    unstable: { bg: "bg-rose-50 dark:bg-rose-950/30",       text: "text-rose-700 dark:text-rose-300",       border: "border-rose-300 dark:border-rose-700",       label: "Bridge unstable — investigate" },
    idle:     { bg: "bg-slate-50 dark:bg-slate-900/40",     text: "text-slate-600 dark:text-slate-400",     border: "border-slate-300 dark:border-slate-700",     label: "No traffic in window" },
  };
  const pill = pillByHealth[metrics.health];
  const pct = (n: number) => `${(n * 100).toFixed(2)}%`;
  // Top 5 by call volume (already sorted desc by total in snapshot()).
  const top = metrics.endpoints.slice(0, 5);
  return (
    <div className="space-y-1.5">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Bridge proxy (last hour)</div>
      <div className={`rounded-md border ${pill.border} ${pill.bg} px-2.5 py-1.5 text-[11px] ${pill.text} font-semibold flex items-center justify-between`}>
        <span>{pill.label}</span>
        {metrics.health !== "idle" && (
          <span className="font-mono">{pct(t.errorRate)} 5xx</span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-x-3 gap-y-0.5 text-[10px] text-slate-600 dark:text-slate-400">
        <div>Total: <span className="font-mono">{t.total.toLocaleString()}</span></div>
        <div>5xx: <span className="font-mono">{t.s5xx.toLocaleString()}</span></div>
        <div>4xx: <span className="font-mono">{t.s4xx.toLocaleString()}</span></div>
        <div>p50: <span className="font-mono">{Math.round(t.p50)}ms</span></div>
        <div>p95: <span className="font-mono">{Math.round(t.p95)}ms</span></div>
        <div>p99: <span className="font-mono">{Math.round(t.p99)}ms</span></div>
      </div>
      {top.length > 0 && (
        <div className="space-y-0.5">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 pt-1">Top endpoints</div>
          <div className="text-[10px] font-mono text-slate-600 dark:text-slate-400 space-y-0.5">
            {top.map((ep) => {
              const isHot = ep.errorRate >= 0.02;
              return (
                <div key={ep.endpoint} className="flex items-baseline gap-2">
                  <span className="truncate flex-1">{ep.endpoint}</span>
                  <span>{ep.total.toLocaleString()}</span>
                  <span className={isHot ? "text-rose-600 dark:text-rose-400 font-bold" : ""}>{pct(ep.errorRate)}</span>
                  <span>p95 {Math.round(ep.p95)}ms</span>
                  {ep.avgRetries > 0.1 && (
                    <span className="text-amber-600 dark:text-amber-400">×{ep.avgRetries.toFixed(1)}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function BackfillRow({
  label, est, run, busy, onEstimate, onStart,
}: {
  kind: "outlook-calendar" | "outlook-mail";
  label: string;
  est: { estimatedCount: number; estimatedCostUsd: number; provider: string; model: string } | null;
  run: {
    estimatedCount: number;
    progress: {
      eventsProcessed: number;
      eventsTotal: number;
      packetsInserted: number;
      skippedDuplicates: number;
      errored: number;
    };
    status: string;
    startedAt: string;
    finishedAt: string | null;
  } | null;
  busy: boolean;
  onEstimate: () => void;
  onStart: () => void;
}) {
  const pct =
    run && run.progress.eventsTotal > 0
      ? Math.min(100, Math.round((run.progress.eventsProcessed / run.progress.eventsTotal) * 100))
      : 0;
  const isRunning = run?.status === "running" || run?.status === "queued";

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="text-[12px] text-slate-700 dark:text-slate-200 flex-1 min-w-0 truncate">{label}</span>
        {!est && !isRunning && (
          <button
            type="button"
            disabled={busy}
            onClick={onEstimate}
            className="text-[11px] px-2 py-0.5 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-50"
          >
            {busy ? "…" : "Estimate"}
          </button>
        )}
        {est && !isRunning && (
          <button
            type="button"
            disabled={busy}
            onClick={onStart}
            className="text-[11px] px-2 py-0.5 rounded bg-sky-600 hover:bg-sky-700 text-white disabled:opacity-50"
          >
            {busy ? "…" : `Run (~$${est.estimatedCostUsd.toFixed(2)})`}
          </button>
        )}
      </div>

      {est && !isRunning && (
        <div className="text-[10px] text-slate-500 dark:text-slate-400">
          {est.estimatedCount.toLocaleString()} items · {est.provider}/{est.model} · ~${est.estimatedCostUsd.toFixed(2)} estimated
        </div>
      )}

      {run && (
        <>
          <div className="h-1.5 rounded bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div
              className={`h-full transition-all ${isRunning ? "bg-sky-500" : run.status === "finished" ? "bg-emerald-500" : "bg-rose-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400">
            {run.status === "running" ? "Running" : run.status === "finished" ? "Done" : run.status === "errored" ? "Error" : run.status}
            {" · "}
            {run.progress.eventsProcessed.toLocaleString()}/{run.progress.eventsTotal.toLocaleString()}
            {" · "}
            {run.progress.packetsInserted.toLocaleString()} new
            {run.progress.skippedDuplicates > 0 && <> · {run.progress.skippedDuplicates.toLocaleString()} dup</>}
            {run.progress.errored > 0 && <> · {run.progress.errored.toLocaleString()} err</>}
            {" · "}
            started {formatRelative(run.startedAt)}
          </div>
        </>
      )}
    </div>
  );
}

function formatEta(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

/** Teams 2.0 cache sidecar. Hits /api/teams/v1/snapshot/stats for the
 *  message / chat / contact counts and exposes a re-snapshot button
 *  via the sidecar's POST /v1/snapshot/refresh endpoint. */
function TeamsSidecarCard({ scope, onScopeChange }: { scope: string | null; onScopeChange: () => void }) {
  type Stats = {
    messages?: number;
    chats?: number;
    contacts?: number;
    lastSnapshotAt?: string;
    error?: string;
  };
  const [stats, setStats] = useState<Stats | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const reload = async () => {
    try {
      const r = await fetch("/api/teams/v1/snapshot/stats", { credentials: "same-origin" });
      const j = await r.json();
      if (r.ok && j) {
        const data = (j.data ?? j) as Stats;
        setStats(data);
        setErr(null);
      } else {
        setErr(j?.error?.message || `sidecar ${r.status}`);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void reload();
    const id = window.setInterval(reload, 30_000);
    return () => window.clearInterval(id);
  }, []);

  const refresh = async () => {
    if (busy) return;
    setBusy(true); setMsg(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch("/api/teams/v1/snapshot/refresh", {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRF-Token": csrf } : {}) },
      });
      const j = await r.json();
      if (!r.ok || j?.error) throw new Error(j?.error?.message || `failed (${r.status})`);
      setMsg("Snapshot refreshed");
      void reload();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <ServiceCardShell
        icon={<UsersIcon className="w-4 h-4" />}
        name="Teams sidecar"
        scope="work"
        tone="err"
        statusLabel="Offline"
      >
        <div className="rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-[11px] text-rose-700 dark:text-rose-200">
          <code>{err}</code>. Verify <code>WAKI_TEAMS_BRIDGE_URL</code> + the sidecar process on the VM.
        </div>
      </ServiceCardShell>
    );
  }

  if (!stats) {
    return (
      <ServiceCardShell
        icon={<UsersIcon className="w-4 h-4" />}
        name="Teams sidecar"
        scope="work"
        tone="unknown"
        statusLabel="Probing…"
      />
    );
  }

  const cardScope = (scope as "work" | "personal" | undefined) === "personal" ? "personal" : "work";
  return (
    <ServiceCardShell
      icon={<UsersIcon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />}
      name="Teams sidecar"
      scope={cardScope}
      tone="ok"
      statusLabel="Running"
      stats={
        <>
          {(stats.messages ?? 0).toLocaleString()} msgs · {(stats.chats ?? 0).toLocaleString()} chats · {(stats.contacts ?? 0).toLocaleString()} contacts
          {stats.lastSnapshotAt && <> · snapped {formatRelative(stats.lastSnapshotAt)}</>}
          {scope && <> · scope <span className="font-semibold">{scope}</span></>}
        </>
      }
      primary={
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          className="text-[11px] font-semibold rounded-md bg-sky-600 hover:bg-sky-700 text-white px-2.5 py-1 disabled:opacity-50 inline-flex items-center gap-1"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} />
          {busy ? "Snapshotting…" : "Re-snapshot now"}
        </button>
      }
      advanced={
        <ConnectorScopeSetup kind="teams" current={scope} defaultHint="work" onSaved={onScopeChange} compact={false} />
      }
    >
      {msg && <div className="text-[11px] text-emerald-700 dark:text-emerald-300">{msg}</div>}
      {scope == null && (
        <ConnectorScopeSetup kind="teams" current={null} defaultHint="work" onSaved={onScopeChange} compact />
      )}
    </ServiceCardShell>
  );
}

function ConnectorCard({
  connector: c,
  onChanged,
}: {
  connector: ConnectorsResponse["connectors"][number];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<"sync" | "disconnect" | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const isGoogle = c.kind === "gmail" || c.kind === "calendar";
  const isGithub = c.kind === "github";
  // Accept the legacy "mail-viewer" kind too so a pre-0149 row still
  // flows through the reconnect / WOV grant path.
  const isMailViewer = c.kind === "outlook-bridge" || c.kind === "mail-viewer";
  const isOAuth = isGoogle || isGithub;

  // Surface the reconnect prompt prominently when the existing
  // connector instance has an auth error. Google flags expired
  // refresh tokens as "invalid_grant"; GitHub surfaces revoked
  // tokens as 401. Reconnect re-runs the OAuth flow against the SAME
  // provider account and refreshes the tokens on the existing
  // instance row, packets / engagements / people stay intact.
  const status = (c.last_status || "").toLowerCase();
  // The outlook-bridge connector kind serves two topologies (see
  // backend/src/connectors/mail-viewer.ts sync() mode branch):
  //
  //   bridge-only — bridge URL + optional HMAC, no OAuth. Failures
  //                 read as "bridge_unreachable: ...". Reconnect is
  //                 N/A; the recovery action is Sync now (which
  //                 re-probes /healthz) or editing the URL via the
  //                 Configure flow.
  //   WOV-OAuth   — refresh_token present. The original WOV grant
  //                 path. Failures read as "refresh_replayed_needs_
  //                 reauth", "refresh failed", "401", etc., and the
  //                 fix IS a fresh OAuth grant.
  //
  // We tell the two apart by whether the status carries an OAuth-
  // specific marker. Bridge-only rows fall through to the
  // generic-error display below; the "Sync now" button is their
  // recovery affordance.
  const mailViewerOAuthFailure =
    isMailViewer &&
    (status.includes("needs_reauth") ||
      status.includes("replay") ||
      status.includes("invalid_token") ||
      status.includes("refresh failed") ||
      status.includes("unauthorized") ||
      status.includes("401"));
  const needsReconnect =
    (isOAuth &&
      (status.includes("invalid_grant") ||
        status.includes("token has been expired") ||
        status.includes("token has been revoked") ||
        status.includes("bad credentials") ||
        status.includes("unauthorized") ||
        status.includes("401"))) ||
    mailViewerOAuthFailure;
  // Distinct affordance for bridge-only rows where the bridge is
  // simply unreachable. No OAuth involved; Sync now will re-probe.
  const bridgeUnreachable =
    isMailViewer &&
    !mailViewerOAuthFailure &&
    (status.includes("bridge_unreachable") ||
      status.includes("missing config.base_url"));

  const reconnectHref = isGoogle
    ? `/api/connectors/google/start?provider=${encodeURIComponent(c.kind)}&scope=${encodeURIComponent(c.scope)}&returnTo=${encodeURIComponent("/settings/connectors")}`
    : isGithub
      ? `/api/connectors/github/start?scope=${encodeURIComponent(c.scope)}&returnTo=${encodeURIComponent("/settings/connectors")}`
      : mailViewerOAuthFailure
        ? `/api/connectors/outlook-bridge/start?connectorId=${encodeURIComponent(c.id)}&returnTo=${encodeURIComponent("/settings/connectors")}`
        : null;

  const lastSyncLabel = c.last_sync_at
    ? new Date(c.last_sync_at).toLocaleString(undefined, {
        month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
      })
    : "never";

  const sync = async () => {
    if (busy) return;
    setBusy("sync");
    setError(null);
    setMsg(null);
    try {
      const csrf = getCsrfToken();
      const res = await fetch(`/api/connectors/${c.id}/sync`, {
        method: "POST",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const body = (await res.json()) as { summary?: Record<string, { new: number; seen: number; error?: string }> };
      const stat = Object.values(body.summary || {})[0];
      if (stat?.error) throw new Error(stat.error.slice(0, 200));
      setMsg(stat ? `synced: ${stat.new} new / ${stat.seen} seen` : "synced");
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    if (busy) return;
    setBusy("disconnect");
    setError(null);
    try {
      const csrf = getCsrfToken();
      const headers = csrf ? { "X-CSRF-Token": csrf } : undefined;
      // For OAuth connectors, revoke the user_connections row first
      // (best-effort) so the next sync has no token to refresh; then
      // delete the connectors row. GitHub reuses the google-connections
      // route family since both write to the same user_connections
      // table; the provider key disambiguates inside.
      if (isGoogle) {
        await fetch(
          `/api/connectors/google/connections/${encodeURIComponent(c.kind)}/${encodeURIComponent(c.account)}`,
          { method: "DELETE", credentials: "same-origin", headers },
        ).catch(() => undefined);
      } else if (isGithub) {
        await fetch(
          `/api/connectors/google/connections/github/${encodeURIComponent(c.account)}`,
          { method: "DELETE", credentials: "same-origin", headers },
        ).catch(() => undefined);
      }
      const res = await fetch(`/api/connectors/${c.id}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers,
      });
      if (!res.ok) throw new Error(`${res.status}`);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
      setConfirming(false);
    }
  };

  return (
    <li className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/30 p-3 space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
          {c.kind}
        </span>
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate flex-1 min-w-[120px]">
          {c.account}
          {c.privacy_label ? (
            <span className="ml-1 text-[11px] font-normal text-slate-500 dark:text-slate-400">
              · {c.privacy_label}
            </span>
          ) : null}
        </span>
        <span
          className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${privacyBadgeClass(c.scope)}`}
          title={`Privacy scope: ${privacyScopeLabel(c.scope)}. Packets from this connector are tagged with this scope and respect downstream filters.`}
        >
          {privacyScopeLabel(c.scope)}
        </span>
        {c.enabled ? (
          <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
            on
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
            off
          </span>
        )}
      </div>
      <div className="text-[11px] text-slate-500 dark:text-slate-400">
        last sync: {lastSyncLabel}
        {c.last_status ? <> · {c.last_status}</> : null}
      </div>
      {(typeof c.packet_count_30d === "number" || typeof c.engagement_count === "number") && (
        <div className="flex flex-wrap gap-3 text-[11px] text-slate-500 dark:text-slate-400">
          {typeof c.packet_count_30d === "number" && (
            <span>
              <strong className="font-semibold text-slate-700 dark:text-slate-200">
                {c.packet_count_30d.toLocaleString()}
              </strong>{" "}
              packets · last 30d
            </span>
          )}
          {typeof c.engagement_count === "number" && (
            <span>
              <strong className="font-semibold text-slate-700 dark:text-slate-200">
                {c.engagement_count.toLocaleString()}
              </strong>{" "}
              engagement{c.engagement_count === 1 ? "" : "s"}
            </span>
          )}
        </div>
      )}
      {needsReconnect && (
        <div className="rounded-md border border-amber-300 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-2 text-[11px] text-amber-800 dark:text-amber-200">
          <strong className="font-semibold">Reconnect required.</strong>{" "}
          {mailViewerOAuthFailure
            ? "The WOV instance revoked this grant (refresh-token replay). Click Reconnect below to re-authenticate against the same instance."
            : "The OAuth token expired. Click Reconnect below to re-grant access."}{" "}
          Your existing engagements, packets, and people stay intact. The
          instance id is preserved, only the auth refreshes.
        </div>
      )}
      {bridgeUnreachable && (
        <div className="rounded-md border border-rose-300 dark:border-rose-700/50 bg-rose-50 dark:bg-rose-900/20 px-2.5 py-2 text-[11px] text-rose-800 dark:text-rose-200">
          <strong className="font-semibold">Bridge unreachable.</strong>{" "}
          The Outlook Bridge URL did not answer /healthz. The bridge uses an
          HMAC shared secret, not OAuth, so there is no token to refresh.
          Confirm Classic Outlook is running with the WakiOutlookBridge
          add-in enabled on the bridge machine, then click Sync now to
          re-probe. If the URL itself moved, edit it via the Configure
          Outlook Bridge flow.
        </div>
      )}
      {error && <div className="text-[11px] text-rose-600 dark:text-rose-300">{error}</div>}
      {msg && <div className="text-[11px] text-emerald-700 dark:text-emerald-300">{msg}</div>}
      <div className="flex flex-wrap items-center gap-2 pt-1">
        {reconnectHref && (
          <a
            href={reconnectHref}
            className={
              needsReconnect
                ? "rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-semibold px-3 py-1.5"
                : "rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5"
            }
            title={
              needsReconnect
                ? `Re-grant ${c.kind} access for ${c.account}. Instance id is preserved; only the OAuth token refreshes. No data is lost.`
                : `Re-run OAuth for ${c.account} to refresh credentials without disconnecting.`
            }
          >
            Reconnect
          </a>
        )}
        <button
          type="button"
          onClick={sync}
          disabled={busy !== null}
          className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
        >
          {busy === "sync" ? "Syncing…" : "Sync now"}
        </button>
        {confirming ? (
          <>
            <button
              type="button"
              onClick={disconnect}
              disabled={busy !== null}
              className="rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
            >
              {busy === "disconnect" ? "Disconnecting…" : "Confirm disconnect"}
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className="rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5"
            >
              Cancel
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => setConfirming(true)}
            className="rounded-lg bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5"
          >
            Disconnect
          </button>
        )}
      </div>

      {/* Per-instance settings disclosure. Connector-scoped settings
          live HERE rather than in a separate per-feature page so the
          user gets one canonical place to manage each instance. */}
      <ConnectorInstanceSettings connector={c} onChanged={onChanged} />
    </li>
  );
}

/** Per-connector settings panel rendered as a collapsible disclosure
 *  inside each ConnectorCard. Connector-specific knobs (which Gmail
 *  labels, which calendars, which Outlook folders, which WhatsApp
 *  contacts) live here; global app settings stay in their own
 *  Settings sections. Privacy scope + label edits ALSO surface here
 *  so the user can re-tag an instance without disconnecting. */
function ConnectorInstanceSettings({
  connector: c,
  onChanged,
}: {
  connector: ConnectorsResponse["connectors"][number];
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [scope, setScope] = useState(c.scope);
  const [privacyLabel, setPrivacyLabel] = useState(c.privacy_label ?? "");
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);
  const [savedErr, setSavedErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<EditPreviewResult | null>(null);

  // Connector-specific config fields. Persisted as JSON on the
  // connectors row. Today these are toggles + lookback hints; richer
  // per-kind UI grows here over time.
  const initialConfig = (() => {
    if (!c.config_json) return {} as Record<string, unknown>;
    try { return JSON.parse(c.config_json) as Record<string, unknown>; } catch { return {}; }
  })();
  const [config, setConfig] = useState<Record<string, unknown>>(initialConfig);

  const collectChanges = (): Array<{ field: string; oldValue: unknown; newValue: unknown }> => {
    const changes: Array<{ field: string; oldValue: unknown; newValue: unknown }> = [];
    const newLabel = privacyLabel.trim() || null;
    if (scope !== c.scope) changes.push({ field: "scope", oldValue: c.scope, newValue: scope });
    if (newLabel !== c.privacy_label) changes.push({ field: "privacy_label", oldValue: c.privacy_label, newValue: newLabel });
    // Config-field deltas. The four config keys we wire today match
    // the registered editable set in the backend classifier.
    for (const key of ["labelInclude", "labelExclude", "lookbackHours", "includeOrgs", "excludeRepos"] as const) {
      const oldVal = (initialConfig as Record<string, unknown>)[key];
      const newVal = (config as Record<string, unknown>)[key];
      const changed =
        JSON.stringify(oldVal ?? null) !== JSON.stringify(newVal ?? null);
      if (changed) changes.push({ field: `config.${key}`, oldValue: oldVal, newValue: newVal });
    }
    return changes;
  };

  const computePreview = async () => {
    const changes = collectChanges();
    if (changes.length === 0) {
      setPreview(null);
      return;
    }
    try {
      const csrf = getCsrfToken();
      const res = await fetch(`/api/connectors/${c.id}/preview-edit`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ changes }),
      });
      if (!res.ok) {
        setPreview(null);
        return;
      }
      const body = (await res.json()) as { ok: boolean; preview: EditPreviewResult };
      setPreview(body.preview);
    } catch {
      setPreview(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setSavedErr(null);
    setSavedMsg(null);
    try {
      const csrf = getCsrfToken();
      const res = await fetch(`/api/connectors/${c.id}/settings`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({
          scope,
          privacy_label: privacyLabel.trim() || null,
          config,
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      // After the PATCH, if the preview said Tier 2+, also fire the
      // apply-edit endpoint so the bulk retag actually happens.
      // Tier 1 needs nothing further; Tier 3+ queues a batch job.
      const changes = collectChanges();
      if (changes.length > 0) {
        try {
          await fetch(`/api/connectors/${c.id}/apply-edit`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              ...(csrf ? { "X-CSRF-Token": csrf } : {}),
            },
            body: JSON.stringify({ changes }),
          });
        } catch {
          /* preview-derived apply is best-effort; primary state is saved */
        }
      }
      setSavedMsg(
        preview && preview.tier >= 2
          ? `Saved + retagged ${preview.affected_rows.packets} packets.`
          : "Saved. Next tick will pick up the new settings.",
      );
      setPreview(null);
      onChanged();
    } catch (err) {
      setSavedErr((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <details
      className="mt-2 border-t border-slate-200/70 dark:border-slate-700/40 pt-2"
      open={open}
      onToggle={(e) => setOpen((e.target as HTMLDetailsElement).open)}
    >
      <summary className="text-[11px] font-semibold text-sky-600 dark:text-sky-300 cursor-pointer">
        {open ? "Hide settings" : "Settings"}
      </summary>
      <div className="mt-3 space-y-3">
        {/* Privacy controls */}
        <div className="space-y-1.5">
          <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-200">
            Privacy scope
          </label>
          <select
            value={scope}
            onChange={(e) => setScope(e.target.value)}
            className="w-full text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5"
          >
            {PRIVACY_SCOPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} — {opt.description}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder='Optional label, e.g. "Work — CGI" or "Personal — Wedding"'
            value={privacyLabel}
            onChange={(e) => setPrivacyLabel(e.target.value)}
            className="w-full text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5"
          />
        </div>

        {/* Per-kind settings — minimal MVP: each kind shows the
            toggles that matter for it. The list grows over time. */}
        <PerKindSettings
          kind={c.kind}
          config={config}
          onChange={setConfig}
        />

        <div className="flex items-center gap-2 pt-1">
          <button
            type="button"
            onClick={computePreview}
            disabled={saving}
            className="rounded-md border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
          >
            Preview impact
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save settings"}
          </button>
          {savedMsg && <span className="text-[11px] text-emerald-700 dark:text-emerald-300">{savedMsg}</span>}
          {savedErr && <span className="text-[11px] text-rose-600 dark:text-rose-300">{savedErr}</span>}
        </div>

        {preview && (
          <EditPreviewBanner preview={preview} />
        )}
      </div>
    </details>
  );
}

interface EditPreviewResult {
  tier: 1 | 2 | 3 | 4;
  affected_rows: {
    packets: number;
    threads: number;
    engagements: number;
    recommendations: number;
  };
  estimated_time_ms: number;
  estimated_llm_cost_usd: number;
  requires_reauth: boolean;
  requires_resync: boolean;
  reason: string;
  safe_to_auto_apply: boolean;
}

function EditPreviewBanner({ preview }: { preview: EditPreviewResult }) {
  const TIER_TONE = {
    1: { ring: "border-emerald-300 dark:border-emerald-800", tag: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300", label: "Tier 1 cosmetic" },
    2: { ring: "border-sky-300 dark:border-sky-800", tag: "bg-sky-100 text-sky-700 dark:bg-sky-950/40 dark:text-sky-300", label: "Tier 2 retag local data" },
    3: { ring: "border-amber-300 dark:border-amber-800", tag: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300", label: "Tier 3 reprocess existing" },
    4: { ring: "border-rose-300 dark:border-rose-800", tag: "bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300", label: "Tier 4 drop and re-fetch" },
  } as const;
  const tone = TIER_TONE[preview.tier];
  const timeLabel = preview.estimated_time_ms < 1000
    ? `${preview.estimated_time_ms} ms`
    : `${(preview.estimated_time_ms / 1000).toFixed(1)} s`;
  return (
    <div className={`rounded-md border px-3 py-2 text-[11px] ${tone.ring}`}>
      <div className="flex items-center gap-2 mb-1">
        <span className={`uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${tone.tag}`}>{tone.label}</span>
        {preview.safe_to_auto_apply ? (
          <span className="text-emerald-700 dark:text-emerald-300">safe to save</span>
        ) : (
          <span className="text-amber-700 dark:text-amber-300">review before saving</span>
        )}
      </div>
      <div className="text-slate-600 dark:text-slate-300">{preview.reason}</div>
      <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-slate-500 dark:text-slate-400">
        <div>Packets: <span className="font-mono">{preview.affected_rows.packets}</span></div>
        <div>Threads: <span className="font-mono">{preview.affected_rows.threads}</span></div>
        <div>Engagements: <span className="font-mono">{preview.affected_rows.engagements}</span></div>
        <div>Recommendations: <span className="font-mono">{preview.affected_rows.recommendations}</span></div>
        <div>Est time: <span className="font-mono">{timeLabel}</span></div>
        <div>Est LLM cost: <span className="font-mono">${preview.estimated_llm_cost_usd.toFixed(4)}</span></div>
        {preview.requires_reauth && <div className="col-span-2 text-rose-600 dark:text-rose-300">Requires re-auth.</div>}
        {preview.requires_resync && <div className="col-span-2 text-amber-600 dark:text-amber-300">Requires full resync. The batch queue picks it up after Save.</div>}
      </div>
    </div>
  );
}

/** Per-kind settings switch. Each kind contributes its own slim form;
 *  unknown kinds get a generic JSON editor as the escape hatch. The
 *  config object is owned by the parent so Save can persist the whole
 *  blob in one PATCH. */
function PerKindSettings({
  kind,
  config,
  onChange,
}: {
  kind: string;
  config: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
}) {
  const set = (k: string, v: unknown) => onChange({ ...config, [k]: v });
  const bool = (k: string, def = false): boolean => (config[k] === undefined ? def : !!config[k]);

  if (kind === "gmail") {
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">Gmail filters</p>
        <p className="text-[10px] text-slate-500 dark:text-slate-400">
          Default: PRIMARY + UPDATES only. Toggle to also pull
          promotions / social / forums (high noise; not recommended).
        </p>
        <SettingsToggle label="Include Promotions" checked={bool("includePromotions")} onChange={(v) => set("includePromotions", v)} />
        <SettingsToggle label="Include Social"     checked={bool("includeSocial")}     onChange={(v) => set("includeSocial", v)} />
        <SettingsToggle label="Include Forums"     checked={bool("includeForums")}     onChange={(v) => set("includeForums", v)} />
        <SettingsToggle label="Transactional only (overrides above)" checked={bool("transactionalOnly")} onChange={(v) => set("transactionalOnly", v)} />
      </div>
    );
  }
  if (kind === "calendar") {
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">Google Calendar</p>
        <SettingsToggle label="Include secondary calendars" checked={bool("includeSecondary", true)} onChange={(v) => set("includeSecondary", v)} />
        <SettingsToggle label="Include subscribed (read-only) calendars" checked={bool("includeSubscribed", false)} onChange={(v) => set("includeSubscribed", v)} />
        <SettingsNumber label="Past lookback (days)" value={(config.pastDays as number) ?? 30} onChange={(v) => set("pastDays", v)} hint="Overrides the global 30-day window only when set." />
      </div>
    );
  }
  if (kind === "outlook") {
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">Outlook (mail + calendar)</p>
        <SettingsToggle label="Ingest Inbox"         checked={bool("ingestInbox", true)} onChange={(v) => set("ingestInbox", v)} />
        <SettingsToggle label="Ingest Sent Items"    checked={bool("ingestSent", true)} onChange={(v) => set("ingestSent", v)} />
        <SettingsToggle label="Ingest all subfolders under Inbox" checked={bool("ingestAllSubfolders", false)} onChange={(v) => set("ingestAllSubfolders", v)} />
        <SettingsToggle label="Apply LLM classifier on arrival" checked={bool("applyClassifier", true)} onChange={(v) => set("applyClassifier", v)} />
        <p className="text-[10px] text-slate-500 dark:text-slate-400">
          Custom-folder rules + signature management live in <a href="/settings/mail" className="text-sky-600 dark:text-sky-300 underline">Mail settings</a>.
        </p>
      </div>
    );
  }
  if (kind === "teams") {
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">Microsoft Teams</p>
        <SettingsToggle label="Ingest channel messages" checked={bool("ingestChannels", true)}  onChange={(v) => set("ingestChannels", v)} />
        <SettingsToggle label="Ingest 1:1 DM threads"   checked={bool("ingestDms", true)}       onChange={(v) => set("ingestDms", v)} />
        <SettingsToggle label="Ingest group chats"      checked={bool("ingestGroupChats", true)} onChange={(v) => set("ingestGroupChats", v)} />
        <SettingsNumber label="Scrape cadence (minutes)" value={(config.scrapeCadenceMin as number) ?? 30} onChange={(v) => set("scrapeCadenceMin", v)} hint="How often the sidecar polls. Lower = fresher / heavier." />
      </div>
    );
  }
  if (kind === "whatsapp") {
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">WhatsApp</p>
        <SettingsNumber label="Top-N most-active contacts (full history)" value={(config.topNContacts as number) ?? 50} onChange={(v) => set("topNContacts", v)} />
        <SettingsNumber label="Long-tail per-contact history (days)"        value={(config.longTailDays as number) ?? 365} onChange={(v) => set("longTailDays", v)} />
        <SettingsNumber label="Long-tail per-contact history (max msgs)"   value={(config.longTailMaxMsgs as number) ?? 10000} onChange={(v) => set("longTailMaxMsgs", v)} />
        <SettingsToggle label="Include group chats" checked={bool("includeGroupChats", true)} onChange={(v) => set("includeGroupChats", v)} />
      </div>
    );
  }
  // Fallback — generic JSON editor for any kind not yet specialised.
  return (
    <div className="space-y-1">
      <p className="text-[11px] text-slate-500 dark:text-slate-400">No structured settings for this kind yet — config is a raw JSON object.</p>
      <textarea
        value={JSON.stringify(config, null, 2)}
        onChange={(e) => {
          try { onChange(JSON.parse(e.target.value) as Record<string, unknown>); } catch { /* ignore mid-edit parse */ }
        }}
        className="w-full text-[11px] font-mono rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5"
        rows={4}
      />
    </div>
  );
}

function SettingsToggle({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-[11px] text-slate-700 dark:text-slate-200 cursor-pointer">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="rounded" />
      {label}
    </label>
  );
}
function SettingsNumber({ label, value, onChange, hint }: { label: string; value: number; onChange: (v: number) => void; hint?: string }) {
  return (
    <label className="flex flex-col gap-0.5 text-[11px] text-slate-700 dark:text-slate-200">
      <span>{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-32 text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1"
      />
      {hint && <span className="text-[10px] text-slate-500 dark:text-slate-400">{hint}</span>}
    </label>
  );
}

/** Available tab body — renders three sections:
 *
 *  1. Currently-supported connectors (registry + canonical services).
 *     Always lists every kind. Button text adapts to "Connect" /
 *     "Connect another" / "Already connected" based on instance count
 *     and supportsMultipleInstances.
 *  2. Roadmap connectors — informational cards with a star toggle
 *     that writes to connector_interest so we can prioritise the
 *     V2.x build order.
 *  3. (when the user clicks Connect on a non-OAuth kind) An add-form
 *     or canonical-service wizard inline.                              */
function AvailableTab({
  registered,
  roadmap,
  byKind,
  connectedKinds,
  addKind,
  onAddKind,
  onAdded,
  onRoadmapChanged,
}: {
  registered: ConnectorsResponse["registered"];
  roadmap: NonNullable<ConnectorsResponse["roadmap"]>;
  byKind: Record<string, ConnectorsResponse["connectors"]>;
  connectedKinds: string[];
  addKind: string | null;
  onAddKind: (k: string | null) => void;
  onAdded: () => void;
  onRoadmapChanged: () => void;
}) {
  const [pickingFor, setPickingFor] = useState<{
    kind: string;
    label: string;
    oauthFlow: "google" | "github" | null;
    defaultScope:
      | "private"
      | "work"
      | "business"
      | "personal"
      | "family"
      | "social"
      | "financial"
      | "side_project";
    setupSteps?: Array<{ title: string; body: string; cta?: { label: string; href?: string; command?: string } }>;
    settingsHref?: string;
  } | null>(null);

  return (
    <section className="space-y-6">
      {addKind && (
        <ConnectorAddForm
          kind={addKind}
          registered={registered}
          onCancel={() => onAddKind(null)}
          onSaved={onAdded}
        />
      )}

      {pickingFor && (
        <PrivacyPickerDialog
          for={pickingFor}
          onCancel={() => setPickingFor(null)}
        />
      )}

      <div className="space-y-2">
        <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          Available now
        </h3>
        <ul className="space-y-2">
          {registered.map((c) => {
            const alreadyConnected = connectedKinds.includes(c.kind);
            const instanceCount = byKind[c.kind]?.length ?? 0;
            const canAddMore = !alreadyConnected || c.supportsMultipleInstances;
            const oauthLabel = c.oauthFlow === "google"
              ? "Google"
              : c.oauthFlow === "github"
                ? "GitHub"
                : null;
            const buttonText = !alreadyConnected
              ? (oauthLabel ? `Connect with ${oauthLabel}` : "Connect")
              : c.supportsMultipleInstances
                ? (oauthLabel ? `Connect another with ${oauthLabel}` : "Add another")
                : "Already connected";
            const onClick = () => {
              if (!canAddMore) return;
              if (c.kindSource === "canonical") {
                setPickingFor({
                  kind: c.kind,
                  label: c.label || c.kind,
                  oauthFlow: c.oauthFlow,
                  defaultScope: (c.defaultPrivacyScope as any) || "work",
                  setupSteps: c.setupSteps,
                  settingsHref: c.settingsHref,
                });
              } else if (c.oauthFlow === "google" || c.oauthFlow === "github") {
                setPickingFor({
                  kind: c.kind,
                  label: c.label || c.kind,
                  oauthFlow: c.oauthFlow,
                  defaultScope: "work",
                });
              } else {
                onAddKind(c.kind);
              }
            };
            return (
              <li
                key={c.kind}
                className="glass rounded-xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-3"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
                    {c.kind}
                  </span>
                  <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {c.label || c.description}
                  </span>
                  {c.supportsMultipleInstances && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200">
                      multi-instance
                    </span>
                  )}
                  {c.kindSource === "canonical" && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200">
                      wizard
                    </span>
                  )}
                  {alreadyConnected && (
                    <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-200">
                      {instanceCount} configured
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={onClick}
                    disabled={!canAddMore}
                    className={
                      canAddMore
                        ? "ml-auto text-[11px] font-semibold rounded-md bg-sky-600 hover:bg-sky-700 text-white px-2.5 py-1 transition-colors"
                        : "ml-auto text-[11px] font-semibold rounded-md bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 px-2.5 py-1 cursor-not-allowed"
                    }
                    title={
                      !canAddMore
                        ? "This connector type only supports one instance, and you already have one."
                        : undefined
                    }
                  >
                    {buttonText}
                  </button>
                </div>
                {c.kindSource === "canonical" && c.setupSteps && c.setupSteps.length > 0 && (
                  <details className="mt-2 w-full">
                    <summary className="text-[11px] text-sky-600 dark:text-sky-300 cursor-pointer">
                      Setup steps ({c.setupSteps.length})
                    </summary>
                    <ol className="mt-1 space-y-1 text-[11px] text-slate-600 dark:text-slate-300 list-decimal list-inside">
                      {c.setupSteps.map((s, i) => (
                        <li key={i}>
                          <span className="font-semibold">{s.title}.</span> {s.body}
                          {s.cta?.href && (
                            <a href={s.cta.href} className="ml-1 text-sky-600 dark:text-sky-300 underline">{s.cta.label}</a>
                          )}
                        </li>
                      ))}
                    </ol>
                  </details>
                )}
                {c.configFields.length > 0 && (
                  <details className="mt-2 w-full">
                    <summary className="text-[11px] text-sky-600 dark:text-sky-300 cursor-pointer">
                      Config fields ({c.configFields.length})
                    </summary>
                    <ul className="mt-1 space-y-0.5 text-[11px] text-slate-600 dark:text-slate-300">
                      {c.configFields.map((f) => (
                        <li key={f.key}>
                          <code className="text-slate-700 dark:text-slate-200">{f.key}</code> ({f.type}): {f.label}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {roadmap.length > 0 && (
        <div className="space-y-2 pt-4 border-t border-slate-200/70 dark:border-slate-700/50">
          <div>
            <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
              On the roadmap
            </h3>
            <p className="text-[11px] text-slate-500 dark:text-slate-400">
              Memso plans to support these. Star the ones you most want — your
              votes feed the V2.x build order.
            </p>
          </div>
          <RoadmapList roadmap={roadmap} onChanged={onRoadmapChanged} />
        </div>
      )}
    </section>
  );
}

/** Inline button rendered next to a Connected-tab kind header that
 *  opens the privacy picker before kicking off the OAuth flow. Gives
 *  Waqas a chance to pick "Personal" instead of the default "Work"
 *  when adding another Gmail account that's clearly not work. */
function ConnectAnotherButton({
  kind,
  defaultScope,
  oauthFlow,
}: {
  kind: string;
  defaultScope: "private" | "work" | "business" | "personal" | "family" | "social";
  oauthFlow: "google" | "github";
}) {
  const [picking, setPicking] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setPicking(true)}
        className="ml-auto text-[11px] font-semibold rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 px-2.5 py-1"
      >
        + Connect another {kind}
      </button>
      {picking && (
        <PrivacyPickerDialog
          for={{ kind, label: kind, oauthFlow, defaultScope }}
          onCancel={() => setPicking(false)}
        />
      )}
    </>
  );
}

/** Privacy picker dialog. Required step before any new connector
 *  instance is created — captures the privacy scope + an optional
 *  free-text label. For OAuth connectors, redirects to the start
 *  endpoint with scope + privacy_label as query params. For
 *  canonical-service wizards, displays the wizard steps and links
 *  to the configured wizard CTA / settingsHref. */
function PrivacyPickerDialog({
  for: target,
  onCancel,
}: {
  for: {
    kind: string;
    label: string;
    oauthFlow: "google" | "github" | null;
    defaultScope:
      | "private"
      | "work"
      | "business"
      | "personal"
      | "family"
      | "social"
      | "financial"
      | "side_project";
    setupSteps?: Array<{ title: string; body: string; cta?: { label: string; href?: string; command?: string } }>;
    settingsHref?: string;
  };
  onCancel: () => void;
}) {
  const [scope, setScope] = useState(target.defaultScope);
  const [label, setLabel] = useState("");

  const startOAuth = () => {
    const qs = new URLSearchParams({
      scope,
      returnTo: "/settings/connectors",
    });
    if (label.trim()) qs.set("privacy_label", label.trim());
    if (target.oauthFlow === "github") {
      window.location.href = `/api/connectors/github/start?${qs.toString()}`;
      return;
    }
    qs.set("provider", target.kind);
    window.location.href = `/api/connectors/google/start?${qs.toString()}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-5 space-y-4">
        <div>
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
            Connect {target.label}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            Pick the privacy scope this connector will serve. Packets get tagged with
            this scope so downstream views and AI calls respect it (private scope
            never leaves local models).
          </p>
        </div>
        <div className="space-y-2">
          <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-200">
            Privacy scope
          </label>
          <div className="space-y-1">
            {PRIVACY_SCOPE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-200 cursor-pointer rounded-md p-1.5 hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <input
                  type="radio"
                  name="privacy-scope"
                  value={opt.value}
                  checked={scope === opt.value}
                  onChange={() => setScope(opt.value)}
                  className="mt-0.5"
                />
                <span>
                  <span className="font-semibold">{opt.label}</span>
                  <span className="block text-[11px] text-slate-500 dark:text-slate-400">{opt.description}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          <label className="block text-[11px] font-semibold text-slate-700 dark:text-slate-200">
            Optional label
          </label>
          <input
            type="text"
            placeholder='e.g. "Work — CGI" or "Personal — Wedding"'
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="w-full text-xs rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1.5"
          />
          <p className="text-[10px] text-slate-500 dark:text-slate-400">
            Free-text disambiguator within the scope. Shown next to the account name on the connector card.
          </p>
        </div>

        {target.setupSteps && target.setupSteps.length > 0 && (
          <div className="space-y-1.5 pt-2 border-t border-slate-200 dark:border-slate-700">
            <p className="text-[11px] font-semibold text-slate-700 dark:text-slate-200">Setup steps</p>
            <ol className="text-[11px] text-slate-600 dark:text-slate-300 list-decimal list-inside space-y-1">
              {target.setupSteps.map((s, i) => (
                <li key={i}>
                  <span className="font-semibold">{s.title}.</span> {s.body}
                  {s.cta?.href && (
                    <a href={s.cta.href} className="ml-1 text-sky-600 dark:text-sky-300 underline">{s.cta.label}</a>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5"
          >
            Cancel
          </button>
          {target.oauthFlow === "google" || target.oauthFlow === "github" ? (
            <button
              type="button"
              onClick={startOAuth}
              className="rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5"
            >
              {target.oauthFlow === "github" ? "Continue with GitHub" : "Continue with Google"}
            </button>
          ) : target.settingsHref ? (
            <a
              href={target.settingsHref}
              className="rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5"
            >
              Open setup
            </a>
          ) : (
            <button
              type="button"
              onClick={onCancel}
              className="rounded-md bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Roadmap list — coming-soon connectors with a star toggle. */
function RoadmapList({
  roadmap,
  onChanged,
}: {
  roadmap: NonNullable<ConnectorsResponse["roadmap"]>;
  onChanged: () => void;
}) {
  const byCategory: Record<string, typeof roadmap> = {};
  for (const r of roadmap) {
    (byCategory[r.category] ||= []).push(r);
  }
  const order: Array<keyof typeof byCategory> = ["work", "personal", "family", "knowledge", "finance"];
  return (
    <div className="space-y-3">
      {order
        .filter((cat) => byCategory[cat] && byCategory[cat]!.length > 0)
        .map((cat) => (
          <div key={cat} className="space-y-1">
            <h4 className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 capitalize">
              {cat}
            </h4>
            <ul className="space-y-1.5">
              {byCategory[cat]!.map((r) => (
                <RoadmapCard key={r.slug} r={r} onChanged={onChanged} />
              ))}
            </ul>
          </div>
        ))}
    </div>
  );
}

function RoadmapCard({
  r,
  onChanged,
}: {
  r: NonNullable<ConnectorsResponse["roadmap"]>[number];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [starred, setStarred] = useState(r.starred);
  const toggle = async () => {
    setBusy(true);
    try {
      const csrf = getCsrfToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (csrf) headers["X-CSRF-Token"] = csrf;
      const res = await fetch(`/api/connector-interest/${encodeURIComponent(r.slug)}`, {
        method: starred ? "DELETE" : "POST",
        credentials: "same-origin",
        headers,
        body: starred ? undefined : JSON.stringify({}),
      });
      if (res.ok) {
        setStarred(!starred);
        onChanged();
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="rounded-lg border border-slate-200/70 dark:border-slate-700/40 bg-slate-50/60 dark:bg-slate-800/20 px-3 py-2">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{r.label}</span>
            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${privacyBadgeClass(r.privacyHint === "shared" ? "business" : r.privacyHint === "knowledge" || r.privacyHint === "finance" ? "business" : r.privacyHint)}`}>
              {r.privacyHint}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">
              {r.plannedFor}
            </span>
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">{r.description}</p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          className={
            starred
              ? "rounded-md bg-amber-500 hover:bg-amber-600 text-white text-[11px] font-semibold px-2.5 py-1 disabled:opacity-50"
              : "rounded-md bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-[11px] font-semibold px-2.5 py-1 disabled:opacity-50"
          }
          title={starred ? "Remove vote" : "Vote to prioritise this connector"}
        >
          {starred ? "★ Starred" : "☆ Star this"}
        </button>
      </div>
    </li>
  );
}

function ConnectorAddForm({
  kind,
  registered,
  onCancel,
  onSaved,
}: {
  kind: string;
  registered: ConnectorsResponse["registered"];
  onCancel: () => void;
  onSaved: () => void;
}) {
  const def = registered.find((r) => r.kind === kind);
  const [account, setAccount] = useState("");
  const [scope, setScope] = useState("work");
  const [config, setConfig] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!def) return null;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy || !account.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const parsed: Record<string, unknown> = {};
      for (const f of def.configFields) {
        const raw = config[f.key]?.trim() ?? "";
        if (!raw) continue;
        if (f.type === "list") parsed[f.key] = raw.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
        else if (f.type === "number") parsed[f.key] = Number(raw);
        else parsed[f.key] = raw;
      }
      const res = await fetch("/api/connectors", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": "" },
        credentials: "same-origin",
        body: JSON.stringify({ kind, account: account.trim(), scope, config: parsed }),
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body.slice(0, 200));
      }
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="glass rounded-xl border border-sky-300 dark:border-sky-700/60 backdrop-blur p-4 mb-3 space-y-3"
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-bold text-slate-900 dark:text-slate-100">
          New {kind} connector
        </h4>
        <button
          type="button"
          onClick={onCancel}
          className="text-[11px] text-slate-500 hover:text-slate-900 dark:hover:text-slate-100"
        >
          Cancel
        </button>
      </div>
      <FieldRow label="Account name (yours, any label)">
        <input
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          placeholder="e.g. work-rss"
          className="w-full rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs text-slate-900 dark:text-slate-100"
        />
      </FieldRow>
      <FieldRow label="Scope">
        <select
          value={scope}
          onChange={(e) => setScope(e.target.value)}
          className="w-full rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs text-slate-900 dark:text-slate-100"
        >
          <option value="work">work</option>
          <option value="business">business</option>
          <option value="personal">personal</option>
          <option value="family">family</option>
          <option value="social">social</option>
          <option value="private">private (forces local)</option>
        </select>
      </FieldRow>
      {def.configFields.map((f) => (
        <FieldRow key={f.key} label={f.label}>
          {f.type === "list" ? (
            <textarea
              value={config[f.key] ?? ""}
              onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              rows={3}
              className="w-full rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs text-slate-900 dark:text-slate-100 font-mono"
            />
          ) : (
            <input
              type={f.type === "password" ? "password" : "text"}
              value={config[f.key] ?? ""}
              onChange={(e) => setConfig({ ...config, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              className="w-full rounded-md bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs text-slate-900 dark:text-slate-100"
            />
          )}
        </FieldRow>
      ))}
      {error && <div className="text-[11px] text-rose-600 dark:text-rose-300">{error}</div>}
      <div className="flex justify-end">
        <button
          type="submit"
          disabled={busy || !account.trim()}
          className="rounded-md bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-xs font-semibold px-3 py-1.5"
        >
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

interface QueueStats {
  byStatus: Record<string, number>;
  byErrorClass: Record<string, number>;
  pending: number;
  inFlight: number;
  failedRetryable: number;
  failedTerminal: number;
  completed24h: number;
}
interface QueueRowDetail {
  id: string;
  capture_id: string;
  stage: string;
  status: string;
  attempts: number;
  max_attempts: number;
  last_error_class: string | null;
  last_error_message: string | null;
  enqueued_at: string;
  next_retry_at: string | null;
  finished_at: string | null;
}

function PipelineSection() {
  const [stats, setStats] = useState<QueueStats | null>(null);
  const [tab, setTab] = useState<"in_flight" | "queued" | "failed" | "completed">("queued");
  const [rows, setRows] = useState<QueueRowDetail[]>([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const loadStats = () => {
    fetch("/api/pipeline/queue/stats", { credentials: "same-origin" })
      .then((r) => r.json())
      .then(setStats)
      .catch(() => undefined);
  };
  const loadRows = (status: string) => {
    fetch(`/api/pipeline/queue?status=${status}&limit=50`, { credentials: "same-origin" })
      .then((r) => r.json())
      .then((d) => setRows(d.rows || []))
      .catch(() => setRows([]));
  };
  useEffect(() => {
    loadStats();
    const t = window.setInterval(loadStats, 5000);
    return () => window.clearInterval(t);
  }, []);
  useEffect(() => {
    loadRows(tab);
  }, [tab]);

  const retryRow = async (id: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fetch(`/api/pipeline/queue/retry/${encodeURIComponent(id)}`, {
        method: "POST",
        credentials: "same-origin",
      });
      loadRows(tab);
      loadStats();
    } finally {
      setBusy(false);
    }
  };
  const retryClass = async (klass: string) => {
    if (busy) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`/api/pipeline/queue/retry-class`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ errorClass: klass }),
      });
      const body = await res.json();
      setMsg(`Re-queued ${body.requeued ?? 0} ${klass} rows.`);
      loadRows(tab);
      loadStats();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Pipeline queue</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          One row per stage per capture. Failed rows wait for their failure mode to
          fix itself (env-change re-queues api_unauth, model recovery re-queues local_oom)
          or for you to retry them manually.
        </p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="Pending" value={stats.pending} tint="amber" />
          <Stat label="In flight" value={stats.inFlight} />
          <Stat label="Failed (retryable)" value={stats.failedRetryable} tint={stats.failedRetryable > 0 ? "amber" : "emerald"} />
          <Stat label="Failed (need attention)" value={stats.failedTerminal} tint={stats.failedTerminal > 0 ? "rose" : "emerald"} />
          <Stat label="Completed 24h" value={stats.completed24h} tint="emerald" />
        </div>
      )}

      {stats && Object.keys(stats.byErrorClass).length > 0 && (
        <div className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/40 p-3 text-xs space-y-2">
          <div className="font-bold text-slate-700 dark:text-slate-200">Failures by class</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.byErrorClass).map(([klass, count]) => (
              <button
                key={klass}
                type="button"
                onClick={() => retryClass(klass)}
                disabled={busy}
                className="inline-flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-sky-100 dark:hover:bg-sky-900/30 px-2.5 py-1 transition-colors"
                title={`Re-queue all ${klass} failures`}
              >
                <span className="font-bold">{klass}</span>
                <span className="text-slate-500 tabular-nums">{count}</span>
              </button>
            ))}
          </div>
          {msg && <div className="text-emerald-600 dark:text-emerald-300">{msg}</div>}
        </div>
      )}

      <nav className="flex gap-1.5 flex-wrap">
        {(["queued", "in_flight", "failed", "completed"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setTab(s)}
            aria-pressed={tab === s}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors capitalize ${
              tab === s ? "bg-sky-600 text-white" : "bg-slate-100 dark:bg-slate-800/40 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
            }`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </nav>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400 italic">No {tab.replace("_", " ")} rows.</p>
      ) : (
        <ul className="space-y-1.5 max-h-[60vh] overflow-y-auto">
          {rows.map((r) => (
            <li
              key={r.id}
              className="rounded-lg bg-slate-100 dark:bg-slate-800/40 px-3 py-2 text-xs space-y-1"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200">{r.stage}</span>
                <code className="text-[10px] text-slate-500">{r.capture_id}</code>
                <span className="text-[10px] text-slate-500">attempts: {r.attempts}/{r.max_attempts}</span>
                {r.last_error_class && (
                  <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-rose-200 dark:bg-rose-700/40 text-rose-800 dark:text-rose-200">
                    {r.last_error_class}
                  </span>
                )}
                {r.next_retry_at && (
                  <span className="text-[10px] text-amber-600 dark:text-amber-400">
                    retry: {new Date(r.next_retry_at).toLocaleTimeString()}
                  </span>
                )}
                <span className="ml-auto text-[10px] text-slate-400">{new Date(r.enqueued_at).toLocaleString()}</span>
                {(r.status === "failed" || r.status === "stalled") && (
                  <button
                    type="button"
                    onClick={() => retryRow(r.id)}
                    disabled={busy}
                    className="ml-2 text-[10px] font-semibold rounded bg-sky-600 hover:bg-sky-700 text-white px-2 py-0.5"
                  >
                    Retry
                  </button>
                )}
              </div>
              {r.last_error_message && (
                <div className="text-[10px] text-rose-600 dark:text-rose-300 break-words">{r.last_error_message.slice(0, 240)}</div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ViewsSection() {
  const view = useView();
  const [views, setViews] = useState<ViewDef[]>([]);
  const [scopes, setScopes] = useState<ScopeDef[]>([]);
  const [activeId, setActiveId] = useState<string>("");
  const [draft, setDraft] = useState<Record<string, { name: string; scopes: string[] }>>({});
  const [newName, setNewName] = useState("");
  const [newScopes, setNewScopes] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.views();
      setViews(r.views);
      setScopes(r.scopes);
      setActiveId(r.activeViewId);
      const d: Record<string, { name: string; scopes: string[] }> = {};
      for (const v of r.views) d[v.viewId] = { name: v.name, scopes: [...v.scopes] };
      setDraft(d);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load views");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setErr(null);
    try {
      await fn();
      await load();
      await view.refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Action failed");
    } finally {
      setBusy(false);
    }
  }

  function toggleDraftScope(viewId: string, scope: string) {
    setDraft((prev) => {
      const cur = prev[viewId] ?? { name: "", scopes: [] };
      const has = cur.scopes.includes(scope);
      return {
        ...prev,
        [viewId]: {
          ...cur,
          scopes: has ? cur.scopes.filter((s) => s !== scope) : [...cur.scopes, scope],
        },
      };
    });
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Views</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          A view bundles one or more privacy scopes into a single
          selector at the top of the app. Switching a view filters every
          surface (Today, Engagements, People, Recall, Actions) to that
          view's scopes. Built-in views can be cloned but not edited.
        </p>
      </div>

      {err && (
        <div className="rounded-lg border border-rose-300 bg-rose-50 dark:bg-rose-900/20 px-3 py-2 text-sm text-rose-700 dark:text-rose-300">
          {err}
        </div>
      )}

      <ul className="space-y-3">
        {views.map((v) => {
          const d = draft[v.viewId] ?? { name: v.name, scopes: v.scopes };
          const dirty =
            !v.isBuiltin &&
            (d.name !== v.name ||
              d.scopes.slice().sort().join(",") !== v.scopes.slice().sort().join(","));
          const isActive = v.viewId === activeId;
          return (
            <li
              key={v.viewId}
              className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 space-y-3"
            >
              <div className="flex items-center gap-2 flex-wrap">
                {v.isBuiltin ? (
                  <span className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {v.name}
                  </span>
                ) : (
                  <input
                    value={d.name}
                    disabled={busy}
                    onChange={(e) =>
                      setDraft((p) => ({
                        ...p,
                        [v.viewId]: { ...d, name: e.target.value },
                      }))
                    }
                    className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1 text-sm"
                  />
                )}
                {v.isBuiltin && (
                  <span className="rounded-full bg-slate-100 dark:bg-slate-700 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Built-in
                  </span>
                )}
                {isActive && (
                  <span className="rounded-full bg-emerald-100 dark:bg-emerald-900/40 px-2 py-0.5 text-[10px] uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                    Active
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2">
                  {!isActive && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => run(() => api.setActiveView(v.viewId))}
                      className="rounded-md border border-slate-300 dark:border-slate-600 px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                      Set active
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={busy}
                    title="Clone"
                    onClick={() => run(() => api.cloneView(v.viewId))}
                    className="rounded-md border border-slate-300 dark:border-slate-600 p-1.5 hover:bg-slate-50 dark:hover:bg-slate-700"
                  >
                    <Copy size={13} />
                  </button>
                  {!v.isBuiltin && (
                    <button
                      type="button"
                      disabled={busy}
                      title="Delete"
                      onClick={() => run(() => api.deleteView(v.viewId))}
                      className="rounded-md border border-rose-300 text-rose-600 dark:text-rose-400 p-1.5 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                    >
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {v.isEverything ? (
                  <span className="text-xs text-slate-400">
                    All scopes (no filter)
                  </span>
                ) : (
                  scopes.map((s) => {
                    const checked = d.scopes.includes(s.scope);
                    return (
                      <label
                        key={s.scope}
                        title={s.description ?? ""}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${
                          checked
                            ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                            : "border-slate-300 dark:border-slate-600 text-slate-500"
                        } ${v.isBuiltin ? "opacity-60 cursor-not-allowed" : ""}`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          disabled={v.isBuiltin || busy}
                          checked={checked}
                          onChange={() => toggleDraftScope(v.viewId, s.scope)}
                        />
                        {s.display_name}
                      </label>
                    );
                  })
                )}
              </div>

              {dirty && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={busy || d.scopes.length === 0 || !d.name.trim()}
                    onClick={() =>
                      run(() =>
                        api.updateView(v.viewId, { name: d.name.trim(), scopes: d.scopes }),
                      )
                    }
                    className="rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-3 py-1.5 text-xs font-medium disabled:opacity-50"
                  >
                    Save changes
                  </button>
                  {d.scopes.length === 0 && (
                    <span className="text-xs text-rose-600">
                      Pick at least one scope
                    </span>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>

      <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-600 p-4 space-y-3">
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
          New view
        </h3>
        <input
          value={newName}
          disabled={busy}
          placeholder="View name"
          onChange={(e) => setNewName(e.target.value)}
          className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5 text-sm"
        />
        <div className="flex flex-wrap gap-2">
          {scopes.map((s) => {
            const checked = newScopes.includes(s.scope);
            return (
              <label
                key={s.scope}
                title={s.description ?? ""}
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs cursor-pointer ${
                  checked
                    ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
                    : "border-slate-300 dark:border-slate-600 text-slate-500"
                }`}
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  disabled={busy}
                  checked={checked}
                  onChange={() =>
                    setNewScopes((p) =>
                      p.includes(s.scope)
                        ? p.filter((x) => x !== s.scope)
                        : [...p, s.scope],
                    )
                  }
                />
                {s.display_name}
              </label>
            );
          })}
        </div>
        <button
          type="button"
          disabled={busy || !newName.trim() || newScopes.length === 0}
          onClick={() =>
            run(async () => {
              await api.createView(newName.trim(), newScopes);
              setNewName("");
              setNewScopes([]);
            })
          }
          className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 px-3 py-1.5 text-xs font-medium disabled:opacity-50"
        >
          <Plus size={13} /> Create view
        </button>
      </div>
    </div>
  );
}

function PrivacySection() {
  const [stats, setStats] = useState<{ totalCaptures: number; privateCaptures: number; nonPrivateCaptures: number; agentRunsTotal: number; cloudRunsOnPrivate: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetch("/api/privacy/stats", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((d) => setStats(d))
      .catch((e) => setError(String(e)));
  }, []);
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Privacy &amp; scopes</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Captures get one of six scopes. Anything tagged "private" only ever talks to
          local models. The pipeline runner asserts this on every stage; the smoke
          harness audits a private capture end-to-end on every run.
        </p>
      </div>

      <div className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 bg-slate-50 dark:bg-slate-800/40 p-3 text-xs space-y-1">
        <div className="flex items-center gap-2 font-bold text-emerald-700 dark:text-emerald-300">
          <ShieldCheck className="w-3.5 h-3.5" />
          Privacy gate is active.
        </div>
        <div className="text-slate-600 dark:text-slate-300">
          <code>resolveAgentCall()</code> in <code>backend/src/agents.config.ts</code> is the
          single chokepoint. Any capture with scope=private coerces every agent to its
          localFallback. If the runner detects a non-local route on private content it
          refuses to proceed.
        </div>
      </div>

      <ScopeRouting />

      {error && <div className="text-xs text-rose-600 dark:text-rose-300">{error}</div>}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat label="Captures total" value={stats.totalCaptures} />
          <Stat label="Private" value={stats.privateCaptures} tint="amber" />
          <Stat label="Non-private" value={stats.nonPrivateCaptures} tint="emerald" />
          <Stat label="Agent runs total" value={stats.agentRunsTotal} />
          <Stat label="Cloud calls on private (must be 0)" value={stats.cloudRunsOnPrivate} tint={stats.cloudRunsOnPrivate === 0 ? "emerald" : "rose"} />
        </div>
      )}
    </div>
  );
}

const SCOPES = ["private", "work", "business", "personal", "family", "social"] as const;
type ScopeName = typeof SCOPES[number];

interface ScopeConfig {
  patterns: Array<{ scope: ScopeName; pattern: string }>;
  default_scope: ScopeName;
}

const EMPTY_SCOPE_CONFIG: ScopeConfig = { patterns: [], default_scope: "personal" };

/** Maps email-address patterns to capture scopes. The Ingestion
 *  Specialist (and the Gmail / Calendar connectors) consult these to
 *  pre-tag inbound captures before the classifier sees them. The
 *  pattern is matched against From / attendee email; first match wins;
 *  unmatched falls through to default_scope. Patterns can be:
 *    - "@example.com" - any address at that domain
 *    - "alice@" - any local-part (rare, escape hatch)
 *    - "alice@example.com" - exact match
 *  Persists into `user_settings.privacy_scopes`. */
function ScopeRouting() {
  const [cfg, setCfg] = useState<ScopeConfig>(EMPTY_SCOPE_CONFIG);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [draftPattern, setDraftPattern] = useState("");
  const [draftScope, setDraftScope] = useState<ScopeName>("work");

  useEffect(() => {
    fetch("/api/user/settings/privacy_scopes", { credentials: "same-origin" })
      .then((r) => (r.status === 404 ? null : r.json()))
      .then((b: { value?: Partial<ScopeConfig> } | null) => {
        if (b?.value) {
          setCfg({
            patterns: Array.isArray(b.value.patterns) ? b.value.patterns : [],
            default_scope: (b.value.default_scope as ScopeName) || "personal",
          });
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const persist = async (next: ScopeConfig, label: string) => {
    setBusy(true);
    setMsg(null);
    try {
      const csrf = getCsrfToken();
      const res = await fetch("/api/user/settings", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ key: "privacy_scopes", value: next }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setCfg(next);
      setMsg(`${label} saved.`);
      window.setTimeout(() => setMsg(null), 1800);
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const addPattern = () => {
    const p = draftPattern.trim().toLowerCase();
    if (!p) return;
    const next: ScopeConfig = {
      ...cfg,
      patterns: [...cfg.patterns.filter((x) => x.pattern !== p), { scope: draftScope, pattern: p }],
    };
    setDraftPattern("");
    persist(next, "Pattern added");
  };

  const removePattern = (pattern: string) => {
    persist(
      { ...cfg, patterns: cfg.patterns.filter((p) => p.pattern !== pattern) },
      "Pattern removed",
    );
  };

  const setDefaultScope = (s: ScopeName) => {
    persist({ ...cfg, default_scope: s }, "Default scope updated");
  };

  if (!loaded) {
    return <div className="text-xs text-slate-500 dark:text-slate-400">Loading scope routing…</div>;
  }

  return (
    <section className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-4 space-y-3">
      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">Email-to-scope routing</h3>
        <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
          Map sender / attendee patterns to scopes so Memso pre-tags inbound captures
          before the classifier sees them. First match wins; unmatched falls through to
          the default below.
        </p>
      </div>

      <div className="space-y-1.5">
        {cfg.patterns.length === 0 ? (
          <p className="text-[11px] italic text-slate-500 dark:text-slate-400">
            No rules yet. Try <code>@yourcompany.com</code> -&gt; work,
            {" "}<code>@gmail.com</code> -&gt; personal.
          </p>
        ) : (
          <ul className="space-y-1">
            {cfg.patterns.map((p) => (
              <li
                key={p.pattern}
                className="flex items-center gap-2 text-xs bg-slate-50 dark:bg-slate-800/40 rounded-lg px-2.5 py-1.5"
              >
                <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-1.5 py-0.5 ${scopeTint(p.scope)}`}>
                  {p.scope}
                </span>
                <code className="flex-1 text-slate-700 dark:text-slate-200">{p.pattern}</code>
                <button
                  type="button"
                  onClick={() => removePattern(p.pattern)}
                  disabled={busy}
                  className="text-[11px] text-rose-600 dark:text-rose-300 hover:underline disabled:opacity-50"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-1">
        <input
          type="text"
          value={draftPattern}
          onChange={(e) => setDraftPattern(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addPattern();
            }
          }}
          placeholder="@example.com or alice@example.com"
          className="flex-1 min-w-[200px] rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2.5 py-1.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-sky-400"
        />
        <select
          value={draftScope}
          onChange={(e) => setDraftScope(e.target.value as ScopeName)}
          className="rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-1.5 text-xs"
        >
          {SCOPES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={addPattern}
          disabled={busy || !draftPattern.trim()}
          className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
        >
          Add
        </button>
      </div>

      <div className="pt-2 border-t border-slate-200/70 dark:border-slate-700/50">
        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-1.5">
          Default scope (when no pattern matches)
        </div>
        <div className="flex flex-wrap gap-1.5">
          {SCOPES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setDefaultScope(s)}
              disabled={busy}
              className={
                "text-[11px] uppercase tracking-wider font-bold rounded-full px-2.5 py-1 transition-colors disabled:opacity-50 " +
                (cfg.default_scope === s
                  ? "bg-sky-600 text-white shadow"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700")
              }
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {msg && <div className="text-[11px] text-emerald-700 dark:text-emerald-300">{msg}</div>}
    </section>
  );
}

function scopeTint(scope: ScopeName): string {
  switch (scope) {
    case "private": return "bg-rose-500/20 text-rose-700 dark:text-rose-200";
    case "work": return "bg-sky-500/20 text-sky-700 dark:text-sky-200";
    case "business": return "bg-violet-500/20 text-violet-700 dark:text-violet-200";
    case "personal": return "bg-emerald-500/20 text-emerald-700 dark:text-emerald-200";
    case "family": return "bg-amber-500/20 text-amber-700 dark:text-amber-200";
    case "social": return "bg-pink-500/20 text-pink-700 dark:text-pink-200";
  }
}

function Stat({ label, value, tint }: { label: string; value: number; tint?: "emerald" | "rose" | "amber" }) {
  const tints: Record<string, string> = {
    emerald: "text-emerald-600 dark:text-emerald-300",
    rose: "text-rose-600 dark:text-rose-300",
    amber: "text-amber-600 dark:text-amber-300",
  };
  const colour = tint ? tints[tint] : "text-slate-900 dark:text-slate-100";
  return (
    <div className="rounded-lg bg-slate-100 dark:bg-slate-800/40 p-3">
      <div className={`text-2xl font-bold tabular-nums ${colour}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
        {label}
      </div>
    </div>
  );
}

function RouteBadge({ model, fallback }: { model: string; fallback: string }) {
  let route: "local" | "api" | "session" = "local";
  if (model.startsWith("api:")) route = "api";
  else if (model.startsWith("claude-session:")) route = "session";
  const tints = {
    local: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
    api: "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
    session: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
  };
  return (
    <div className="flex flex-col items-end gap-1 text-[10px]">
      <span className={`uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${tints[route]}`}>
        {route}
      </span>
      <code className="text-slate-500 dark:text-slate-400">{model}</code>
      {model !== fallback && (
        <code className="text-slate-400">fallback: {fallback}</code>
      )}
    </div>
  );
}

function SpendCard({
  Icon,
  label,
  tokens,
  tint,
  hint,
}: {
  Icon: LucideIcon;
  label: string;
  tokens: number;
  tint: "emerald" | "sky" | "violet";
  hint: string;
}) {
  const tints: Record<typeof tint, string> = {
    emerald: "from-emerald-400 to-teal-500",
    sky: "from-sky-400 to-indigo-500",
    violet: "from-violet-400 to-fuchsia-500",
  };
  return (
    <div className="glass rounded-xl p-3 border border-slate-200/70 dark:border-slate-700/50 backdrop-blur flex flex-col gap-2">
      <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${tints[tint]} flex items-center justify-center text-white shadow-md`}>
        <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
      </div>
      <div>
        <div className="text-xl font-bold tabular-nums text-slate-900 dark:text-slate-100">
          {tokens.toLocaleString()}
        </div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          {label} tokens today
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>
      </div>
    </div>
  );
}

function SpendCell({
  tint,
  label,
  runs,
  tokens,
}: {
  tint: "emerald" | "sky" | "violet";
  label: string;
  runs: number;
  tokens: number;
}) {
  const tints: Record<typeof tint, string> = {
    emerald: "text-emerald-600 dark:text-emerald-300",
    sky: "text-sky-600 dark:text-sky-300",
    violet: "text-violet-600 dark:text-violet-300",
  };
  return (
    <div className="text-slate-700 dark:text-slate-200">
      <span className={`font-bold ${tints[tint]}`}>{label}</span>:{" "}
      <span className="tabular-nums">{runs}</span> runs ·{" "}
      <span className="tabular-nums">{tokens.toLocaleString()}</span> tok
    </div>
  );
}

interface ApiTokenRow {
  id: string;
  label: string;
  prefix: string;
  scopes: string;
  last_used_at: string | null;
  created_at: string;
  expires_at: string | null;
}
interface IssuedTokenResp {
  ok: boolean;
  token: { id: string; label: string; prefix: string; secret: string; scopes: string; created_at: string };
  hint?: string;
}

/** Per-user API tokens panel. Issues + lists + revokes mem_<handle>_*
 *  tokens used by external capture surfaces (memso-desktop wake-word
 *  agent, iOS Shortcuts, scripts). The freshly-issued secret is shown
 *  once with a copy affordance; subsequent reads only see the prefix. */
function ApiTokensSection() {
  const [rows, setRows] = useState<ApiTokenRow[] | null>(null);
  const [label, setLabel] = useState("");
  const [issued, setIssued] = useState<IssuedTokenResp["token"] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = () => {
    fetch("/api/tokens", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((b: { tokens: ApiTokenRow[] }) => setRows(b.tokens || []))
      .catch(() => setRows([]));
  };
  useEffect(load, []);

  const issue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const csrf = (await import("../lib/auth")).getCsrfToken();
      const res = await fetch("/api/tokens", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ label: label.trim(), scopes: "capture" }),
      });
      const body = (await res.json()) as IssuedTokenResp;
      if (!res.ok || !body.ok) throw new Error((body as unknown as { error?: string }).error || `${res.status}`);
      setIssued(body.token);
      setLabel("");
      load();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    if (!confirm("Revoke this token? The desktop / shortcut using it will stop working immediately.")) return;
    const csrf = (await import("../lib/auth")).getCsrfToken();
    await fetch(`/api/tokens/${id}`, {
      method: "DELETE",
      credentials: "same-origin",
      headers: csrf ? { "X-CSRF-Token": csrf } : {},
    });
    load();
  };

  const copySecret = async (secret: string) => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* user can copy manually */
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">API tokens</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Bearer tokens for external capture sources (memso-desktop wake-word agent,
          iOS Shortcuts, scripts). Each token is shown <strong>exactly once</strong>;
          copy it now, store it in your password manager.
        </p>
      </div>

      <form onSubmit={issue} className="flex items-end gap-2">
        <div className="flex-1">
          <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
            New token label
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            maxLength={64}
            placeholder="memso-desktop on MacBook"
            className="w-full rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"
          />
        </div>
        <button
          type="submit"
          disabled={!label.trim() || busy}
          className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
        >
          {busy ? "Generating…" : "Generate token"}
        </button>
      </form>
      {error && (
        <div className="text-xs text-rose-700 bg-rose-50 dark:bg-rose-900/20 dark:text-rose-300 border border-rose-200 dark:border-rose-700/50 rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {issued && (
        <div className="rounded-xl border border-emerald-300 dark:border-emerald-700/50 bg-emerald-50 dark:bg-emerald-900/20 p-4 space-y-2">
          <div className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
            Token issued: {issued.label}
          </div>
          <div className="font-mono text-xs break-all bg-white dark:bg-slate-900 border border-emerald-200 dark:border-emerald-800 rounded px-2 py-1.5">
            {issued.secret}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => copySecret(issued.secret)}
              className="text-xs font-semibold rounded bg-emerald-600 hover:bg-emerald-700 text-white px-2 py-1"
            >
              {copied ? "Copied!" : "Copy to clipboard"}
            </button>
            <button
              type="button"
              onClick={() => setIssued(null)}
              className="text-xs text-emerald-800 dark:text-emerald-300 underline"
            >
              I have it
            </button>
          </div>
          <p className="text-[11px] text-emerald-800 dark:text-emerald-300">
            Server only stores the hash. Once you dismiss this banner, the secret cannot be recovered.
          </p>
        </div>
      )}

      <div>
        <h3 className="text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-2">
          Active tokens
        </h3>
        {rows === null ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 italic">
            No tokens issued yet.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {rows.map((t) => (
              <li
                key={t.id}
                className="rounded-lg bg-slate-100 dark:bg-slate-800/40 px-3 py-2 flex items-center gap-3 text-sm"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-900 dark:text-slate-100 truncate">
                    {t.label}
                  </div>
                  <div className="font-mono text-[11px] text-slate-500 dark:text-slate-400">
                    {t.prefix}…
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 text-right whitespace-nowrap">
                  {t.last_used_at ? `last used ${t.last_used_at.slice(0, 10)}` : "never used"}
                </div>
                <button
                  type="button"
                  onClick={() => revoke(t.id)}
                  className="text-[11px] font-semibold text-rose-600 hover:text-rose-700"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">
        <strong>How to use:</strong> external tools include the token as
        a bearer header on POST <code>/api/capture/voice</code>:
        <pre className="bg-slate-100 dark:bg-slate-800 rounded px-2 py-1.5 mt-1 overflow-auto text-[11px]">{`curl -X POST ${typeof window !== "undefined" ? window.location.origin : "https://app.memso.ai"}/api/capture/voice \\
  -H "Authorization: Bearer mem_<your-token>" \\
  -H "Content-Type: application/json" \\
  -d '{"transcript":"meeting at 3pm with kapil","source":"desktop","scope":"work"}'`}</pre>
      </div>
    </div>
  );
}

const MCP_RESOURCES = [
  "people",
  "orgs",
  "places",
  "trips",
  "conversations",
  "engagements",
  "today",
  "tasks",
  "recommendations",
  "intents",
  "insights",
  "observations",
  "notes",
  "subscriptions",
  "monitors",
  "captures",
  "claims",
  "attention",
  "composer",
] as const;

const MCP_VIEW_SCOPES = [
  "personal",
  "work",
  "business",
  "family",
  "social",
  "private",
] as const;

const MCP_SENSITIVITY_TIERS = [
  "default",
  "sensitive",
  "restricted",
  "secret",
] as const;

/** Curated MCP clients. One tap names + pairs; the local model then
 *  proposes a starter grant set from the name. */
const MCP_GALLERY: Array<{ name: string; blurb: string }> = [
  { name: "Claude Desktop", blurb: "Anthropic desktop app — add Memso as a custom MCP server." },
  { name: "Claude Code", blurb: "The Claude CLI — claude mcp add memso with the SSE URL + token." },
  { name: "ChatGPT", blurb: "Connect Memso as a custom connector for recall in chats." },
  { name: "Cursor", blurb: "IDE agent — give it your people / orgs context while you code." },
  { name: "Cowork", blurb: "Multi-agent workspace — read-only Memso recall." },
  { name: "Custom script", blurb: "Your own automation hitting the MCP SSE transport." },
];

/** Prompt library: copy-paste starters that exercise Memso MCP. */
const MCP_PROMPTS: Array<{ title: string; prompt: string }> = [
  {
    title: "Pre-meeting brief",
    prompt:
      "Using Memso, who am I meeting today and what's the recent context with each attendee?",
  },
  {
    title: "Relationship catch-up",
    prompt:
      "From Memso, which strong relationships have gone quiet and what was the last open thread?",
  },
  {
    title: "Org pulse",
    prompt:
      "Summarize what Memso knows about <org> — key people, recent conversations, open commitments.",
  },
  {
    title: "Daily synthesis",
    prompt:
      "Pull today's Memso engagements and draft the three things I should not drop.",
  },
];

function McpSection() {
  type McpClient = {
    id: string;
    client_name: string;
    paired_at: string;
    last_seen_at: string | null;
    sensitivity_default: string;
    paused: number;
    revoked_at: string | null;
    granted_scopes: string[];
    granted_view_scopes: string[];
    granted_sensitivity_tiers: string[];
    grants: Array<{
      resource: string;
      allowed: number;
      can_write: number;
      sensitivity_max: string;
    }>;
  };
  const [clients, setClients] = useState<McpClient[] | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paired, setPaired] = useState<{
    client_name: string;
    token: string;
    proposed_grants: Array<{ resource: string; allowed: number }>;
  } | null>(null);
  const [copied, setCopied] = useState(false);
  const [audit, setAudit] = useState<
    Record<
      string,
      {
        summary: string;
        events: Array<{
          method: string;
          resource: string | null;
          kind: string;
          allowed: number;
          at: string;
        }>;
      }
    >
  >({});

  const loadAudit = async (id: string) => {
    try {
      const a = await api.mcpClientAudit(id);
      setAudit((p) => ({
        ...p,
        [id]: { summary: a.summary, events: a.events },
      }));
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const load = () => {
    api
      .mcpClients()
      .then((r) => setClients(r.clients))
      .catch((e: Error) => setError(e.message));
  };
  useEffect(load, []);

  const pairNamed = async (clientName: string) => {
    if (busy || !clientName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.mcpCreateClient(clientName.trim());
      setPaired({
        client_name: r.client_name,
        token: r.token,
        proposed_grants: r.proposed_grants,
      });
      setName("");
      load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const pair = () => pairNamed(name);

  const revoke = async (c: McpClient) => {
    setError(null);
    if (
      typeof window !== "undefined" &&
      !window.confirm(`Revoke ${c.client_name}? Its token stops working immediately.`)
    ) {
      return;
    }
    setClients((prev) => (prev ?? []).filter((x) => x.id !== c.id));
    try {
      await api.mcpRevokeClient(c.id);
      load();
    } catch (e) {
      setError((e as Error).message);
      load();
    }
  };

  const togglePause = async (c: McpClient) => {
    setError(null);
    const newPaused = c.paused === 1 ? false : true;
    setClients((prev) =>
      (prev ?? []).map((x) =>
        x.id === c.id ? { ...x, paused: newPaused ? 1 : 0 } : x,
      ),
    );
    try {
      await api.mcpPauseClient(c.id, newPaused);
      load();
    } catch (e) {
      setError((e as Error).message);
      load();
    }
  };

  const setBulkScopes = async (
    c: McpClient,
    patch: {
      scopes?: string[];
      view_scopes?: string[];
      sensitivity_tiers?: string[];
      can_write_scopes?: string[];
    },
  ) => {
    setError(null);
    const body = {
      scopes: patch.scopes ?? c.granted_scopes,
      view_scopes: patch.view_scopes ?? c.granted_view_scopes,
      sensitivity_tiers: patch.sensitivity_tiers ?? c.granted_sensitivity_tiers,
      can_write_scopes:
        patch.can_write_scopes ??
        c.grants.filter((g) => g.can_write === 1).map((g) => g.resource),
    };
    setClients((prev) =>
      (prev ?? []).map((x) =>
        x.id === c.id
          ? {
              ...x,
              granted_scopes: body.scopes,
              granted_view_scopes: body.view_scopes,
              granted_sensitivity_tiers: body.sensitivity_tiers,
            }
          : x,
      ),
    );
    try {
      await api.mcpSetScopes(c.id, body);
      load();
    } catch (e) {
      setError((e as Error).message);
      load();
    }
  };

  const toggleListItem = (list: string[], item: string): string[] =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

  const setGrant = async (
    c: McpClient,
    resource: string,
    patch: { allowed?: boolean; can_write?: boolean },
  ) => {
    setError(null);
    const cur = c.grants.find((g) => g.resource === resource);
    const allowed = patch.allowed ?? (cur?.allowed ?? 0) === 1;
    const can_write = patch.can_write ?? (cur?.can_write ?? 0) === 1;
    setClients((prev) =>
      (prev ?? []).map((x) =>
        x.id === c.id
          ? {
              ...x,
              grants: MCP_RESOURCES.map((res) => {
                const g = x.grants.find((gg) => gg.resource === res);
                if (res !== resource)
                  return {
                    resource: res,
                    allowed: g?.allowed ?? 0,
                    can_write: g?.can_write ?? 0,
                    sensitivity_max: g?.sensitivity_max ?? "default",
                  };
                return {
                  resource: res,
                  allowed: allowed ? 1 : 0,
                  can_write: can_write ? 1 : 0,
                  sensitivity_max: g?.sensitivity_max ?? "default",
                };
              }),
            }
          : x,
      ),
    );
    try {
      await api.mcpSetGrants(c.id, [{ resource, allowed, can_write }]);
      load();
    } catch (e) {
      setError((e as Error).message);
      load();
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
          MCP clients
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Pair an external agent (Claude, ChatGPT, Cursor, a custom
          script) over MCP. The token is shown <strong>exactly once</strong>.
          A freshly paired client sees <strong>nothing</strong> until you
          allow a resource below; on pairing the local model proposes a
          starter set from the client name.
        </p>
      </div>

      <details className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-3.5 text-sm">
        <summary className="cursor-pointer font-semibold text-slate-900 dark:text-slate-100">
          How to connect a client
        </summary>
        <div className="mt-2 space-y-2 text-slate-600 dark:text-slate-300 text-[13px]">
          <ol className="list-decimal pl-5 space-y-1">
            <li>Pair below; copy the token (shown once).</li>
            <li>
              Point the client at the MCP endpoint with a bearer header:
              <code className="ml-1 rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px]">
                Authorization: Bearer &lt;token&gt;
              </code>
            </li>
            <li>
              Transport:{" "}
              <code className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px]">
                GET /api/mcp/sse
              </code>{" "}
              +{" "}
              <code className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px]">
                POST /api/mcp/message
              </code>
              . Unauth discovery:{" "}
              <code className="rounded bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 text-[11px]">
                GET /api/mcp/docs
              </code>
              .
            </li>
            <li>
              Allow resources for the client below. Until you do, every
              call returns an empty result with{" "}
              <code className="text-[11px]">grants_not_configured</code>.
              Writes also need the per-resource <strong>W</strong> toggle.
            </li>
          </ol>
          <p className="font-mono text-[11px] bg-slate-100 dark:bg-slate-800 rounded p-2 overflow-auto">
            {`POST /api/mcp/message
{"method":"people.search","params":{"q":"alice"}}
-> {"ok":true,"resource":"people","grants_not_configured":false,"result":{"items":[...]}}`}
          </p>
          <p>
            Resources: people, orgs, conversations, engagements, today,
            tasks, recommendations, intents, insights, observations,
            notes, subscriptions, monitors, captures. Sensitive rows are
            filtered above each grant's ceiling; all access is audited
            (see a client's activity below).
          </p>
        </div>
      </details>

      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
          Quick pair
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {MCP_GALLERY.map((g) => (
            <button
              key={g.name}
              type="button"
              disabled={busy}
              onClick={() => pairNamed(g.name)}
              className="text-left rounded-xl border border-slate-200/70 dark:border-slate-700/50 hover:border-sky-300 dark:hover:border-sky-700 p-3 transition-colors disabled:opacity-50"
            >
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {g.name}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                {g.blurb}
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Or name a custom client"
          className="flex-1 rounded-lg border border-slate-300 dark:border-slate-600 bg-transparent px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={pair}
          disabled={busy || !name.trim()}
          className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-40"
        >
          {busy ? "Pairing..." : "Pair client"}
        </button>
      </div>

      {error && (
        <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>
      )}

      {paired && (
        <div className="rounded-xl border border-amber-300/70 dark:border-amber-700/50 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-2">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
            {paired.client_name} paired. Copy this token now, it is not
            shown again:
          </p>
          <div className="flex gap-2">
            <code className="flex-1 rounded bg-white dark:bg-slate-900 border border-amber-300/60 px-2 py-1.5 text-xs font-mono break-all">
              {paired.token}
            </code>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(paired.token);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 2000);
                } catch {
                  /* manual copy */
                }
              }}
              className="rounded-lg border border-amber-400 px-3 py-1.5 text-xs font-semibold"
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="text-xs text-amber-700 dark:text-amber-300">
            Proposed starter grants:{" "}
            {paired.proposed_grants
              .filter((g) => g.allowed)
              .map((g) => g.resource)
              .join(", ") || "none (ask per use)"}
          </p>
        </div>
      )}

      <div className="space-y-3">
        {clients === null ? (
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Loading...
          </p>
        ) : clients.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 rounded-xl border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center">
            No MCP clients paired yet.
          </p>
        ) : (
          clients.map((c) => (
            <div
              key={c.id}
              className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-3.5 space-y-2.5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-sm text-slate-900 dark:text-slate-100 inline-flex items-center gap-2">
                  {c.client_name}
                  {c.paused === 1 && (
                    <span className="rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-semibold px-2 py-0.5">
                      Paused
                    </span>
                  )}
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400">
                    {c.last_seen_at
                      ? `last seen ${c.last_seen_at}`
                      : "never used"}
                  </span>
                  <button
                    type="button"
                    onClick={() => togglePause(c)}
                    className="text-[11px] font-semibold text-amber-600 dark:text-amber-400 hover:underline"
                  >
                    {c.paused === 1 ? "Resume" : "Pause"}
                  </button>
                  <button
                    type="button"
                    onClick={() => revoke(c)}
                    className="text-[11px] font-semibold text-rose-600 dark:text-rose-400 hover:underline"
                  >
                    Revoke
                  </button>
                </span>
              </div>

              <div className="rounded-lg bg-slate-50 dark:bg-slate-900/40 border border-slate-200/60 dark:border-slate-700/40 p-2.5 space-y-2 text-[11px]">
                <p className="text-slate-600 dark:text-slate-300">
                  Bulk scope. Teach Memso which slices of your life this
                  client may see. Per-resource toggles below override.
                </p>
                <div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    View scopes (what privacy bucket)
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {MCP_VIEW_SCOPES.map((s) => {
                      const on = c.granted_view_scopes.includes(s);
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() =>
                            setBulkScopes(c, {
                              view_scopes: toggleListItem(
                                c.granted_view_scopes,
                                s,
                              ),
                            })
                          }
                          className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold capitalize ${
                            on
                              ? "bg-sky-600 text-white border-sky-600"
                              : "text-slate-500 border-slate-300 dark:border-slate-600"
                          }`}
                        >
                          {s}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1">
                    Sensitivity tiers (cap)
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {MCP_SENSITIVITY_TIERS.map((t) => {
                      const on = c.granted_sensitivity_tiers.includes(t);
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() =>
                            setBulkScopes(c, {
                              sensitivity_tiers: toggleListItem(
                                c.granted_sensitivity_tiers,
                                t,
                              ),
                            })
                          }
                          className={`px-2 py-0.5 rounded-full border text-[10px] font-semibold ${
                            on
                              ? "bg-purple-600 text-white border-purple-600"
                              : "text-slate-500 border-slate-300 dark:border-slate-600"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {MCP_RESOURCES.map((res) => {
                  const g = c.grants.find((x) => x.resource === res);
                  const on = (g?.allowed ?? 0) === 1;
                  const w = (g?.can_write ?? 0) === 1;
                  return (
                    <span
                      key={res}
                      className="inline-flex items-center rounded-full border overflow-hidden"
                      style={{ borderColor: on ? "#059669" : undefined }}
                    >
                      <button
                        type="button"
                        onClick={() => setGrant(c, res, { allowed: !on })}
                        className={`px-2.5 py-1 text-[11px] font-semibold capitalize ${
                          on
                            ? "bg-emerald-600 text-white"
                            : "text-slate-500"
                        }`}
                      >
                        {res} {on ? "on" : "off"}
                      </button>
                      <button
                        type="button"
                        disabled={!on}
                        title={
                          on
                            ? "Toggle write access for this resource"
                            : "Allow the resource first"
                        }
                        onClick={() => setGrant(c, res, { can_write: !w })}
                        className={`px-2 py-1 text-[10px] font-bold border-l ${
                          w && on
                            ? "bg-amber-600 text-white"
                            : "text-slate-400 disabled:opacity-40"
                        }`}
                      >
                        {w && on ? "W" : "w"}
                      </button>
                    </span>
                  );
                })}
              </div>
              <div className="pt-1">
                {audit[c.id] ? (
                  <div className="space-y-1.5">
                    <p className="text-[11px] text-slate-600 dark:text-slate-300">
                      {audit[c.id].summary}
                    </p>
                    <details className="text-[11px] text-slate-500">
                      <summary className="cursor-pointer">
                        Recent events ({audit[c.id].events.length})
                      </summary>
                      <ul className="mt-1 space-y-0.5 font-mono">
                        {audit[c.id].events.slice(0, 12).map((ev, i) => (
                          <li key={i}>
                            {ev.at} {ev.kind} {ev.resource ?? "-"}{" "}
                            {ev.allowed ? "ok" : "blocked"} {ev.method}
                          </li>
                        ))}
                      </ul>
                    </details>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => loadAudit(c.id)}
                    className="text-[11px] font-semibold text-sky-600 dark:text-sky-400 hover:underline"
                  >
                    Show activity
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100 mb-2">
          Prompt library
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mb-2.5">
          Starters that exercise Memso recall from a paired client.
        </p>
        <div className="space-y-2">
          {MCP_PROMPTS.map((p) => (
            <div
              key={p.title}
              className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 p-3"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {p.title}
                </span>
                <button
                  type="button"
                  onClick={() => {
                    navigator.clipboard?.writeText(p.prompt).catch(() => {});
                  }}
                  className="text-[11px] font-semibold text-sky-600 dark:text-sky-400 hover:underline flex-shrink-0"
                >
                  Copy
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {p.prompt}
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/** AI settings: providers + feature bindings.
 *
 *  Two sub-tabs:
 *    Providers — list of supported providers (Anthropic, Gemini,
 *      OpenAI, Groq, local Ollama). Save / rotate / test API key per
 *      provider. Keys are encrypted at rest via lib/ai-crypto and
 *      never echoed back; UI shows only the last 4 chars.
 *    Bindings — per-feature mapping of which provider+model handles
 *      a given AI feature (mail classifier, compose AI, Teams scan,
 *      etc.). Click a row to edit; reset to default to clear.
 *
 *  All routes are /api/settings/ai/* (mounted in routes/ai-settings).
 *  CSRF + cookie-auth via the shared middleware. */

type AiProviderName =
  | "anthropic"
  | "google_gemini"
  | "openai"
  | "groq"
  | "local_ollama";

type ProviderRowDto = {
  /** Legacy short vocab from the DB column: anthropic|gemini|openai|groq|local. */
  provider: string;
  keyTail: string | null;
  baseUrl: string | null;
  encrypted: boolean;
  legacyPlaintext: boolean;
  updatedAt: string;
};

type FeatureDto = {
  key: string;
  name: string;
  category: string;
  defaultProvider: AiProviderName;
  defaultModel: string;
  description?: string;
};

type BindingDto = {
  feature: FeatureDto;
  provider: AiProviderName;
  model: string;
  isDefault: boolean;
  fallback: { provider: AiProviderName; model: string } | null;
};

const PROVIDER_META: Record<
  AiProviderName,
  {
    label: string;
    /** Legacy short vocab the backend's provider_credentials column uses. */
    shortKey: string;
    help: string;
    consoleUrl: string;
    hasKey: boolean;
    models: string[];
  }
> = {
  anthropic: {
    label: "Anthropic",
    shortKey: "anthropic",
    help: "Claude Opus / Sonnet / Haiku. Best at long-context reasoning + coding.",
    consoleUrl: "https://console.anthropic.com/settings/keys",
    hasKey: true,
    models: ["claude-opus-4-7", "claude-sonnet-4-6", "claude-haiku-4-5-20251001"],
  },
  google_gemini: {
    label: "Google Gemini",
    shortKey: "gemini",
    help: "Flash is the cost-quality sweet spot for high-volume scanning. $0.075/$0.30 per Mtok in/out.",
    consoleUrl: "https://aistudio.google.com/apikey",
    hasKey: true,
    models: ["gemini-2.5-pro", "gemini-2.5-flash", "gemini-2.5-flash-lite"],
  },
  openai: {
    label: "OpenAI",
    shortKey: "openai",
    help: "GPT-4o / o1. Cloud fallback option.",
    consoleUrl: "https://platform.openai.com/api-keys",
    hasKey: true,
    models: ["gpt-4o", "gpt-4o-mini", "o1", "o1-mini"],
  },
  groq: {
    label: "Groq",
    shortKey: "groq",
    help: "Ultra-fast Llama / Mixtral inference. Good for batch + classifier roles.",
    consoleUrl: "https://console.groq.com/keys",
    hasKey: true,
    models: ["llama-3.3-70b-versatile", "llama-3.1-8b-instant", "mixtral-8x7b-32768"],
  },
  local_ollama: {
    label: "Local Ollama",
    shortKey: "local",
    help: "Routes to the WakiAIService Ollama daemon at :8400. No API key, privacy by default.",
    consoleUrl: "https://ollama.com/library",
    hasKey: false,
    models: ["qwen3:8b", "qwen3:14b", "qwen3:4b", "llama3.2:latest", "gemma3:4b"],
  },
};

const PROVIDER_ORDER: AiProviderName[] = [
  "anthropic",
  "google_gemini",
  "openai",
  "groq",
  "local_ollama",
];

function shortToProvider(s: string): AiProviderName | null {
  for (const p of PROVIDER_ORDER) {
    if (PROVIDER_META[p].shortKey === s) return p;
  }
  return null;
}

function AiProvidersSection() {
  const [tab, setTab] = useState<"providers" | "bindings">("providers");
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">AI</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Configure your providers and pick which model handles each feature. Keys are encrypted
          at rest and never echoed back.
        </p>
      </div>

      <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-700/60 bg-slate-50 dark:bg-slate-900/40 p-1">
        {(["providers", "bindings"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-xs font-semibold rounded-md transition ${
              tab === t
                ? "bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 shadow-sm"
                : "text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            }`}
          >
            {t === "providers" ? "Providers" : "Bindings"}
          </button>
        ))}
      </div>

      {tab === "providers" ? <ProvidersTab /> : <BindingsTab />}
    </div>
  );
}

function ProvidersTab() {
  const [rows, setRows] = useState<ProviderRowDto[] | null>(null);
  const [encOk, setEncOk] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await fetch("/api/settings/ai/providers", { credentials: "same-origin" });
      const j = await r.json();
      if (!r.ok || !j?.ok) {
        setErr(j?.error || `failed (${r.status})`);
        return;
      }
      setRows((j.providers ?? []) as ProviderRowDto[]);
      setEncOk(!!j.encryptionAvailable);
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  const byProvider = new Map<AiProviderName, ProviderRowDto>();
  for (const r of rows ?? []) {
    const p = shortToProvider(r.provider);
    if (p) byProvider.set(p, r);
  }

  return (
    <div className="space-y-3">
      {!encOk && (
        <div className="rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          Encryption is not available. Set <code className="font-mono">MEMSO_AI_KEY_MASTER_SECRET</code> in
          the daemon environment to enable encrypted credential storage.
        </div>
      )}
      {err && (
        <div className="rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
          {err}
        </div>
      )}
      {PROVIDER_ORDER.map((p) => (
        <ProviderCard
          key={p}
          provider={p}
          row={byProvider.get(p)}
          loading={loading}
          encryptionAvailable={encOk}
          onChange={reload}
        />
      ))}
    </div>
  );
}

function ProviderCard({
  provider,
  row,
  loading,
  encryptionAvailable,
  onChange,
}: {
  provider: AiProviderName;
  row: ProviderRowDto | undefined;
  loading: boolean;
  encryptionAvailable: boolean;
  onChange: () => void;
}) {
  const meta = PROVIDER_META[provider];
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const save = async () => {
    if (busy || draft.trim().length < 8) {
      setErr("Key looks too short.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch(`/api/settings/ai/providers/${provider}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ apiKey: draft.trim() }),
      });
      const j = await r.json();
      if (!r.ok || !j?.ok) {
        setErr(j?.message || j?.error || `failed (${r.status})`);
        return;
      }
      setDraft("");
      setEdit(false);
      onChange();
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    if (!window.confirm(`Remove your ${meta.label} key?`)) return;
    setBusy(true);
    setErr(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch(`/api/settings/ai/providers/${provider}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j?.error || `failed (${r.status})`);
        return;
      }
      onChange();
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch(`/api/settings/ai/providers/${provider}/test`, {
        method: "POST",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
      });
      const j = await r.json();
      if (!r.ok || !j?.ok) {
        setTestResult({ ok: false, msg: j?.message || j?.error || `failed (${r.status})` });
      } else {
        setTestResult({ ok: true, msg: `${j.latencyMs}ms · "${(j.sample ?? "").slice(0, 40)}"` });
      }
    } catch (e) {
      setTestResult({ ok: false, msg: String((e as Error).message || e) });
    } finally {
      setTesting(false);
    }
  };

  const badge = (() => {
    if (loading) return <span className="text-[11px] text-slate-500 dark:text-slate-400">loading...</span>;
    if (!meta.hasKey) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-slate-200/70 dark:bg-slate-700/40 text-slate-700 dark:text-slate-200">
          No key required
        </span>
      );
    }
    if (row?.encrypted) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-200">
          Encrypted · ...{row.keyTail}
        </span>
      );
    }
    if (row?.legacyPlaintext) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300">
          Plaintext · ...{row.keyTail} · rotate to encrypt
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200">
        Not configured
      </span>
    );
  })();

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white/50 dark:bg-slate-900/30 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">{meta.label}</div>
            {badge}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{meta.help}</div>
          {row?.updatedAt && (
            <div className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
              Last set {formatRelative(row.updatedAt)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {meta.hasKey && (
            <a
              href={meta.consoleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-semibold text-sky-600 dark:text-sky-400 hover:underline"
            >
              Get key ↗
            </a>
          )}
          {meta.hasKey && !edit && (
            <button
              type="button"
              onClick={() => setEdit(true)}
              className="rounded-md bg-slate-900 dark:bg-slate-200 text-white dark:text-slate-900 text-xs font-semibold px-2.5 py-1"
            >
              {row ? "Replace" : "Add key"}
            </button>
          )}
          <button
            type="button"
            onClick={test}
            disabled={testing || (meta.hasKey && !row)}
            className="rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold px-2.5 py-1 disabled:opacity-40"
          >
            {testing ? "Testing..." : "Test"}
          </button>
          {row && meta.hasKey && !edit && (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="rounded-md border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 text-xs font-semibold px-2.5 py-1"
            >
              Remove
            </button>
          )}
        </div>
      </div>

      {edit && (
        <div className="mt-3 space-y-2">
          <input
            type="password"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Paste ${meta.label} API key`}
            autoComplete="off"
            spellCheck={false}
            className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono"
          />
          {err && <div className="text-[11px] text-rose-600 dark:text-rose-300">{err}</div>}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={busy || draft.trim().length < 8 || !encryptionAvailable}
              className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
            >
              {busy ? "Saving..." : "Save"}
            </button>
            <button
              type="button"
              onClick={() => {
                setEdit(false);
                setDraft("");
                setErr(null);
              }}
              className="rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5"
            >
              Cancel
            </button>
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              Encrypted with AES-256-GCM. Never echoed back.
            </span>
          </div>
        </div>
      )}

      {testResult && (
        <div
          className={`mt-3 rounded-md px-3 py-1.5 text-[11px] ${
            testResult.ok
              ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
              : "bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800"
          }`}
        >
          {testResult.ok ? "✓ " : "✗ "}
          {testResult.msg}
        </div>
      )}
    </div>
  );
}

function BindingsTab() {
  const [rows, setRows] = useState<BindingDto[] | null>(null);
  const [providers, setProviders] = useState<Set<AiProviderName>>(new Set(["local_ollama"]));
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [edit, setEdit] = useState<BindingDto | null>(null);

  const reload = async () => {
    setLoading(true);
    setErr(null);
    try {
      const [bRes, pRes] = await Promise.all([
        fetch("/api/settings/ai/bindings", { credentials: "same-origin" }).then((r) => r.json()),
        fetch("/api/settings/ai/providers", { credentials: "same-origin" }).then((r) => r.json()),
      ]);
      if (!bRes?.ok) {
        setErr(bRes?.error || "load_failed");
        return;
      }
      setRows((bRes.bindings ?? []) as BindingDto[]);
      const configured = new Set<AiProviderName>(["local_ollama"]);
      for (const r of (pRes?.providers ?? []) as ProviderRowDto[]) {
        const p = shortToProvider(r.provider);
        if (p && (r.encrypted || r.legacyPlaintext)) configured.add(p);
      }
      setProviders(configured);
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    reload();
  }, []);

  if (loading && !rows) {
    return <div className="text-xs text-slate-500 dark:text-slate-400">Loading bindings...</div>;
  }
  if (err) {
    return (
      <div className="rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
        {err}
      </div>
    );
  }

  const grouped = new Map<string, BindingDto[]>();
  for (const r of rows ?? []) {
    const arr = grouped.get(r.feature.category) ?? [];
    arr.push(r);
    grouped.set(r.feature.category, arr);
  }

  return (
    <>
      <div className="space-y-4">
        {[...grouped.entries()].map(([category, items]) => (
          <div key={category}>
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1.5">
              {category}
            </div>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700/60 bg-white/50 dark:bg-slate-900/30 overflow-hidden">
              {items.map((b, idx) => {
                const configured = providers.has(b.provider);
                return (
                  <button
                    type="button"
                    key={b.feature.key}
                    onClick={() => setEdit(b)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                      idx > 0 ? "border-t border-slate-200 dark:border-slate-700/60" : ""
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {b.feature.name}
                      </div>
                      {b.feature.description && (
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">
                          {b.feature.description}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <div className="text-right">
                        <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                          {PROVIDER_META[b.provider]?.label ?? b.provider}
                        </div>
                        <div className="text-[11px] text-slate-500 dark:text-slate-400 font-mono">
                          {b.model}
                        </div>
                      </div>
                      {b.isDefault && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                          default
                        </span>
                      )}
                      {!configured && (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300"
                          title="Provider has no key configured"
                        >
                          ⚠ no key
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {edit && (
        <BindingEditModal
          binding={edit}
          configuredProviders={providers}
          onClose={() => setEdit(null)}
          onSaved={() => {
            setEdit(null);
            reload();
          }}
        />
      )}
    </>
  );
}

function BindingEditModal({
  binding,
  configuredProviders,
  onClose,
  onSaved,
}: {
  binding: BindingDto;
  configuredProviders: Set<AiProviderName>;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [provider, setProvider] = useState<AiProviderName>(binding.provider);
  const [model, setModel] = useState(binding.model);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);
  const meta = PROVIDER_META[provider];
  const models = meta?.models ?? [];

  const test = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch(`/api/settings/ai/bindings/${binding.feature.key}/test`, {
        method: "POST",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
      });
      const j = await r.json();
      if (!r.ok || !j?.ok) {
        setTestResult({ ok: false, msg: j?.message || j?.error || `failed (${r.status})` });
      } else {
        const tag = `${PROVIDER_META[j.binding?.provider as AiProviderName]?.label ?? j.binding?.provider} · ${j.binding?.model}`;
        setTestResult({ ok: true, msg: `${j.latencyMs}ms · ${tag} · "${(j.sample ?? "").slice(0, 40)}"` });
      }
    } catch (e) {
      setTestResult({ ok: false, msg: String((e as Error).message || e) });
    } finally {
      setTesting(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch(`/api/settings/ai/bindings/${binding.feature.key}`, {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ provider, model: model.trim() }),
      });
      const j = await r.json();
      if (!r.ok || !j?.ok) {
        setErr(j?.message || j?.error || `failed (${r.status})`);
        return;
      }
      onSaved();
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    const def = `${PROVIDER_META[binding.feature.defaultProvider]?.label} · ${binding.feature.defaultModel}`;
    if (!window.confirm(`Reset ${binding.feature.name} to default (${def})?`)) return;
    setBusy(true);
    setErr(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch(`/api/settings/ai/bindings/${binding.feature.key}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setErr(j?.error || `failed (${r.status})`);
        return;
      }
      onSaved();
    } catch (e) {
      setErr(String((e as Error).message || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-xl p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            {binding.feature.category}
          </div>
          <h3 className="text-base font-bold text-slate-900 dark:text-slate-100 mt-0.5">
            {binding.feature.name}
          </h3>
          {binding.feature.description && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {binding.feature.description}
            </p>
          )}
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Provider
          </label>
          <select
            value={provider}
            onChange={(e) => {
              const next = e.target.value as AiProviderName;
              setProvider(next);
              const m = PROVIDER_META[next].models[0];
              if (m) setModel(m);
            }}
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
          >
            {PROVIDER_ORDER.map((p) => {
              const m = PROVIDER_META[p];
              const ok = configuredProviders.has(p);
              return (
                <option key={p} value={p}>
                  {m.label}
                  {ok ? "" : " (no key)"}
                </option>
              );
            })}
          </select>
          {!configuredProviders.has(provider) && (
            <div className="text-[11px] text-amber-700 dark:text-amber-300 mt-1">
              ⚠ This provider has no key configured. Calls to this feature will fail until you add a
              key in the Providers tab.
            </div>
          )}
        </div>

        <div>
          <label className="text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Model
          </label>
          <input
            list={`models-${binding.feature.key}`}
            value={model}
            onChange={(e) => setModel(e.target.value)}
            placeholder="e.g. claude-sonnet-4-6"
            className="mt-1 w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono"
          />
          <datalist id={`models-${binding.feature.key}`}>
            {models.map((m) => (
              <option key={m} value={m} />
            ))}
          </datalist>
        </div>

        {err && (
          <div className="rounded-md border border-rose-300 dark:border-rose-700 bg-rose-50 dark:bg-rose-950/30 px-3 py-2 text-xs text-rose-700 dark:text-rose-200">
            {err}
          </div>
        )}

        {testResult && (
          <div
            className={`rounded-md px-3 py-1.5 text-[11px] ${
              testResult.ok
                ? "bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800"
                : "bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 border border-rose-200 dark:border-rose-800"
            }`}
          >
            {testResult.ok ? "✓ " : "✗ "}
            {testResult.msg}
          </div>
        )}

        <div className="flex items-center justify-between gap-2 pt-2">
          {!binding.isDefault ? (
            <button
              type="button"
              onClick={reset}
              disabled={busy}
              className="text-xs font-semibold text-rose-600 dark:text-rose-400 hover:underline"
            >
              Reset to default
            </button>
          ) : (
            <span className="text-[11px] text-slate-400">Currently using catalog default</span>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={test}
              disabled={testing || busy}
              className="rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5 disabled:opacity-40"
            >
              {testing ? "Testing..." : "Test"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 text-xs font-semibold px-3 py-1.5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={busy || !model.trim()}
              className="rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
            >
              {busy ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Per-user vault path + scope policy. The vault is the on-disk
 *  Obsidian-style folder Memso writes daily briefs + curated notes
 *  to. Default is `~/memso-vault` (or the legacy `~/brain-v2-vault`
 *  for users who landed before the rename). User can override via
 *  this setting; takes effect on the next vault write. */
function DataAndVaultSection() {
  const [vaultPath, setVaultPath] = useState("");
  const [defaultScope, setDefaultScope] = useState("personal");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/user/settings", { credentials: "same-origin" })
      .then((r) => r.json())
      .then((b: { settings?: Record<string, unknown> }) => {
        const s = b.settings || {};
        if (typeof s.vault_path === "string") setVaultPath(s.vault_path);
        const ps = s.privacy_scopes as { default_scope?: string } | undefined;
        if (ps?.default_scope) setDefaultScope(ps.default_scope);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const save = async (key: string, value: unknown, label: string) => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const csrf = (await import("../lib/auth")).getCsrfToken();
      const res = await fetch("/api/user/settings", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf } : {}),
        },
        body: JSON.stringify({ key, value }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      setMsg(`${label} saved.`);
      window.setTimeout(() => setMsg(null), 2000);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) {
    return <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Data &amp; vault</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Where Memso writes your daily briefs + curated notes on disk, and the default
          scope new captures get when the classifier can't decide.
        </p>
      </div>

      <section className="space-y-2">
        <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          Vault path
        </label>
        <input
          type="text"
          value={vaultPath}
          onChange={(e) => setVaultPath(e.target.value)}
          placeholder="~/Documents/Obsidian/MemsoVault"
          className="w-full rounded-lg bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-sky-400"
        />
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Leave blank to use the platform default <code>~/memso-vault</code>. Path resolves
          on the daemon host (your local Mac for now).
        </p>
        <button
          type="button"
          onClick={() => save("vault_path", vaultPath.trim(), "Vault path")}
          disabled={busy}
          className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
        >
          {busy ? "Saving…" : "Save vault path"}
        </button>
      </section>

      <section className="space-y-2">
        <label className="block text-xs uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
          Default capture scope
        </label>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          When the classifier can't pick a scope, captures land here. <strong>Private</strong>{" "}
          forces local-only routing for everything.
        </p>
        <div className="flex flex-wrap gap-1.5">
          {["private", "work", "business", "personal", "family", "social"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => {
                setDefaultScope(s);
                save("privacy_scopes", { patterns: [], default_scope: s }, "Default scope");
              }}
              className={
                "text-[11px] uppercase tracking-wider font-bold rounded-full px-2.5 py-1 transition-colors " +
                (defaultScope === s
                  ? "bg-sky-600 text-white shadow"
                  : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700")
              }
            >
              {s}
            </button>
          ))}
        </div>
      </section>

      {msg && <div className="text-xs text-emerald-700 dark:text-emerald-300">{msg}</div>}
      {error && <div className="text-xs text-rose-700 dark:text-rose-300">{error}</div>}
    </div>
  );
}

/** Push notifications panel.
 *
 *  Shows three things:
 *    1. Master toggle: subscribe / unsubscribe THIS device.
 *       The browser-side dance (permission, SW, pushManager) is in
 *       lib/push.ts; this component handles loading + error state.
 *    2. Per-kind opt-out checkboxes (brief, monitor matches,
 *       recommendation digests, test). Default is brief +
 *       monitor-match.
 *    3. Device list: every active subscription the user has across
 *       browsers, with last-sent timestamp + a "Send test
 *       notification" button that fans out to all of them.
 *
 *  Brand: NO em dashes anywhere in this component or in the strings
 *  it sends to /api/push/test. Memso lexicon throughout.
 */
function NotificationsSection() {
  type State = "loading" | "ready" | "error";
  const [state, setState] = useState<State>("loading");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [serverConfigured, setServerConfigured] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>("default");
  const [enabledKinds, setEnabledKinds] = useState<string[]>([
    "brief",
    "monitor-match",
    "test",
  ]);
  const [devices, setDevices] = useState<
    {
      id: string;
      endpoint_short: string;
      user_agent: string | null;
      created_at: string;
      last_sent_at: string | null;
      last_error: string | null;
    }[]
  >([]);
  const [busy, setBusy] = useState<"subscribe" | "unsubscribe" | "test" | "prefs" | null>(
    null,
  );

  const supported =
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window;

  const refresh = async () => {
    try {
      const lib = await import("../lib/push");
      const data = await lib.listPushDevices();
      setServerConfigured(data.enabled);
      setEnabledKinds(
        Array.isArray(data.prefs?.enabled_kinds) && data.prefs.enabled_kinds.length > 0
          ? data.prefs.enabled_kinds
          : ["brief", "monitor-match", "test"],
      );
      setDevices(data.devices || []);
      const sub = await lib.getCurrentSubscription();
      setEnabled(!!sub);
      if ("Notification" in window) setPermission(Notification.permission);
      setState("ready");
    } catch (e) {
      setState("error");
      setErrorMsg((e as Error).message);
    }
  };

  useEffect(() => {
    if (!supported) {
      setState("ready");
      return;
    }
    void refresh();
  }, [supported]);

  const onSubscribe = async () => {
    setStatusMsg(null);
    setErrorMsg(null);
    setBusy("subscribe");
    try {
      const lib = await import("../lib/push");
      await lib.subscribeThisDevice();
      setStatusMsg("This device is now wired up for push.");
      await refresh();
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onUnsubscribe = async () => {
    setStatusMsg(null);
    setErrorMsg(null);
    setBusy("unsubscribe");
    try {
      const lib = await import("../lib/push");
      await lib.unsubscribeThisDevice();
      setStatusMsg("This device will no longer receive push.");
      await refresh();
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onTest = async () => {
    setStatusMsg(null);
    setErrorMsg(null);
    setBusy("test");
    try {
      const lib = await import("../lib/push");
      const r = await lib.sendTestPush();
      if (r.delivered === 0 && r.attempted === 0) {
        setStatusMsg("No devices subscribed yet. Toggle on first.");
      } else {
        setStatusMsg(`Test sent: ${r.delivered} of ${r.attempted} device(s) delivered.`);
      }
      await refresh();
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const onToggleKind = async (kind: string) => {
    const next = enabledKinds.includes(kind)
      ? enabledKinds.filter((k) => k !== kind)
      : [...enabledKinds, kind];
    setEnabledKinds(next);
    setBusy("prefs");
    try {
      const lib = await import("../lib/push");
      await lib.setPushPrefs(next);
    } catch (e) {
      setErrorMsg((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  if (!supported) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Notifications</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            This browser does not support push notifications. Try Chrome, Edge, Firefox, or Safari 16.4+.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Notifications</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Push notifications for the morning brief, monitor matches, and recommendation digests.
        </p>
      </div>

      {!serverConfigured && (
        <div className="rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700/40 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
          Server-side VAPID keys are not configured. Push transport is disabled until VAPID_PUBLIC_KEY and VAPID_PRIVATE_KEY are set.
        </div>
      )}

      <Row
        label="Enable push on this device"
        hint={
          permission === "denied"
            ? "Browser permission is blocked. Reset it from the site settings."
            : enabled
              ? "This browser is subscribed and will receive push."
              : "Tap to grant permission and register this browser."
        }
      >
        {enabled ? (
          <button
            type="button"
            onClick={onUnsubscribe}
            disabled={busy !== null}
            className="inline-flex items-center gap-2 rounded-lg bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-sm font-semibold px-3 py-2 hover:bg-slate-300 dark:hover:bg-slate-600 disabled:opacity-50 transition-colors"
          >
            {busy === "unsubscribe" ? "Unsubscribing..." : "Disable on this device"}
          </button>
        ) : (
          <button
            type="button"
            onClick={onSubscribe}
            disabled={busy !== null || !serverConfigured || permission === "denied"}
            className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-3 py-2 disabled:opacity-50 transition-colors"
          >
            <Bell className="w-4 h-4" />
            {busy === "subscribe" ? "Subscribing..." : "Enable push notifications"}
          </button>
        )}
      </Row>

      <div className="space-y-2">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">What to send</h3>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Pick the kinds you want. Brief and monitor matches are on by default.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[
            { id: "brief", label: "Morning brief", hint: "Daily summary lands by 7am." },
            { id: "monitor-match", label: "Monitor matches", hint: "Mentions, deadlines, milestones." },
            {
              id: "recommendation-digest",
              label: "Recommendation digests",
              hint: "Merge candidates, queue piles, evolution prompts.",
            },
            { id: "test", label: "Test pushes", hint: "Allow the test button to ping you." },
          ].map((k) => {
            const on = enabledKinds.includes(k.id);
            return (
              <label
                key={k.id}
                className={`flex items-start gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  on
                    ? "border-orange-300 bg-orange-50 dark:bg-orange-900/15 dark:border-orange-700/40"
                    : "border-slate-200 dark:border-slate-700/60 bg-white/40 dark:bg-slate-800/30"
                }`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  disabled={busy !== null}
                  onChange={() => onToggleKind(k.id)}
                  className="mt-1"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                    {k.label}
                  </div>
                  <div className="text-xs text-slate-500 dark:text-slate-400">{k.hint}</div>
                </div>
              </label>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Devices ({devices.length})
          </h3>
          <button
            type="button"
            onClick={onTest}
            disabled={busy !== null || devices.length === 0}
            className="inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50 transition-colors"
          >
            {busy === "test" ? "Sending..." : "Send test notification"}
          </button>
        </div>
        {devices.length === 0 ? (
          <p className="text-xs text-slate-500 dark:text-slate-400">
            No devices subscribed yet. Enable push above to register this browser.
          </p>
        ) : (
          <ul className="space-y-1.5">
            {devices.map((d) => (
              <li
                key={d.id}
                className="flex items-start justify-between gap-3 px-3 py-2 rounded-lg border border-slate-200/70 dark:border-slate-700/50 bg-white/40 dark:bg-slate-800/30"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-mono text-slate-700 dark:text-slate-300 truncate">
                    {d.endpoint_short}
                  </div>
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                    {d.user_agent || "Unknown device"}
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Added {formatRelative(d.created_at)}
                    {d.last_sent_at ? ` , last sent ${formatRelative(d.last_sent_at)}` : ""}
                    {d.last_error ? ` , error: ${d.last_error.slice(0, 60)}` : ""}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ObservationPreferences />

      <SourceFreshnessPreferences />

      {statusMsg && (
        <div className="rounded-lg border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700/40 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200">
          {statusMsg}
        </div>
      )}
      {errorMsg && (
        <div className="rounded-lg border border-rose-300/60 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-700/40 px-3 py-2 text-xs text-rose-800 dark:text-rose-200">
          {errorMsg}
        </div>
      )}
      {state === "loading" && (
        <p className="text-xs text-slate-500 dark:text-slate-400">Loading...</p>
      )}
    </div>
  );
}

const OBSERVATION_KIND_LABELS: Record<string, { label: string; hint: string }> = {
  tone_shift: { label: "Tone shifts", hint: "When a contact's tone toward you shifts noticeably." },
  relationship_warming: { label: "Relationships warming", hint: "When cadence or warmth with a contact rises." },
  pattern_anomaly: { label: "Pattern anomalies", hint: "When a usual pattern breaks (skipped meeting, etc.)." },
  topic_emergence: { label: "Topics emerging", hint: "When a new topic starts recurring across captures." },
  commitment_overdue: { label: "Commitments overdue", hint: "When a past commitment of yours is still unactioned." },
  decision_revisit: { label: "Decisions to revisit", hint: "When a past decision is worth a second look." },
  question_unresolved: { label: "Open questions", hint: "When a question was raised but never answered." },
  frequency_unusual: { label: "Cadence unusual", hint: "When your cadence with someone is off your baseline." },
  birthday_upcoming: { label: "Birthdays upcoming", hint: "When a contact's birthday lands in the next 7 days." },
  deadline_approaching: { label: "Deadlines approaching", hint: "When a tracked deadline is closing in." },
};

/** Per-kind opt-out for the Today page Observations panel. Wave 1
 *  W1.2: an observation is "Memso noticed X". Some users want every
 *  signal; others want only deadlines + birthdays. The toggle row
 *  here lets each user mute kinds they don't want. */
function ObservationPreferences() {
  const [prefs, setPrefs] = useState<{ kind: string; muted: boolean }[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .observationPreferences()
      .then((d) => {
        setPrefs(d.preferences.map((p) => ({ kind: p.kind, muted: p.muted })));
        setLoaded(true);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoaded(true);
      });
  }, []);

  const toggle = async (kind: string, nextMuted: boolean) => {
    if (busy) return;
    setBusy(kind);
    setError(null);
    setPrefs((prior) => prior.map((p) => (p.kind === kind ? { ...p, muted: nextMuted } : p)));
    try {
      await api.setObservationPreference(kind as never, nextMuted);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPrefs((prior) => prior.map((p) => (p.kind === kind ? { ...p, muted: !nextMuted } : p)));
    } finally {
      setBusy(null);
    }
  };

  if (!loaded) return null;
  return (
    <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 space-y-3">
      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
          Observations on Today
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Pick which ambient patterns Memso surfaces in your "Memso noticed" panel. Turning a kind off hides new and existing rows for that kind.
        </p>
      </div>
      {error && (
        <div className="rounded-lg border border-rose-300/60 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-700/40 px-3 py-2 text-xs text-rose-800 dark:text-rose-200">
          {error}
        </div>
      )}
      <ul className="space-y-1.5">
        {prefs.map((p) => {
          const meta = OBSERVATION_KIND_LABELS[p.kind] ?? { label: p.kind, hint: "" };
          return (
            <li
              key={p.kind}
              className="flex items-start justify-between gap-3 px-3 py-2 rounded-lg border border-slate-200/70 dark:border-slate-700/50 bg-white/40 dark:bg-slate-800/30"
            >
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">{meta.label}</div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400">{meta.hint}</div>
              </div>
              <label className="inline-flex items-center cursor-pointer flex-shrink-0">
                <span className="sr-only">{`Mute ${meta.label}`}</span>
                <input
                  type="checkbox"
                  checked={!p.muted}
                  disabled={busy === p.kind}
                  onChange={(e) => toggle(p.kind, !e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                />
                <span className="ml-2 text-[11px] text-slate-600 dark:text-slate-300">
                  {p.muted ? "Muted" : "On"}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Per-source stale-alert thresholds + active-hours window. C8 on
 *  the tracker. Each row in the list is one entry in the backend
 *  SOURCE_FRESHNESS_REGISTRY (connectors + selected workers); the
 *  threshold input is the number of hours of silence before the
 *  top-bar StaleSourceBanner pops. Active hours pause accumulation
 *  outside the user's working window so a quiet 3am Gmail mailbox
 *  does not page them.
 *
 *  Brand: no em dashes anywhere. Memso lexicon throughout. */
interface SourceFreshnessRow {
  key: string;
  label: string;
  kind: "connector" | "worker";
  default_threshold_hours: number;
  default_alert_enabled: boolean;
  threshold_hours: number;
  alert_enabled: boolean;
  ignore_active_hours: boolean;
}

function SourceFreshnessPreferences() {
  const [rows, setRows] = useState<SourceFreshnessRow[]>([]);
  const [activeStart, setActiveStart] = useState("08:00");
  const [activeEnd, setActiveEnd] = useState("23:00");
  const [timezone, setTimezone] = useState<string>("America/Toronto");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetch("/api/notifications/source-freshness/config", { credentials: "same-origin" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => {
        setRows((d.sources ?? []) as SourceFreshnessRow[]);
        setActiveStart(d.active_hours_start ?? "08:00");
        setActiveEnd(d.active_hours_end ?? "23:00");
        setTimezone(d.timezone ?? "America/Toronto");
        setLoaded(true);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : String(e));
        setLoaded(true);
      });
  }, []);

  const updateRow = (key: string, patch: Partial<SourceFreshnessRow>) => {
    setRows((prior) => prior.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const save = async () => {
    setBusy(true);
    setStatus(null);
    setError(null);
    try {
      const csrf = getCsrfToken();
      const r = await fetch("/api/notifications/source-freshness/config", {
        method: "PUT",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "x-csrf-token": csrf } : {}),
        },
        body: JSON.stringify({
          sources: rows.map((row) => ({
            key: row.key,
            threshold_hours: row.threshold_hours,
            alert_enabled: row.alert_enabled,
          })),
          active_hours_start: activeStart,
          active_hours_end: activeEnd,
          timezone,
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setStatus("Saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;
  return (
    <div className="rounded-2xl border border-slate-200/70 dark:border-slate-700/50 backdrop-blur p-4 space-y-3">
      <div>
        <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
          Source freshness
        </h3>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Memso watches every connected source and pops a banner when one goes silent past your threshold. Pick the cadence per source. Set 0h or turn the alert off to skip the source entirely.
        </p>
      </div>
      {error && (
        <div className="rounded-lg border border-rose-300/60 bg-rose-50 dark:bg-rose-900/20 dark:border-rose-700/40 px-3 py-2 text-xs text-rose-800 dark:text-rose-200">
          {error}
        </div>
      )}
      {status && (
        <div className="rounded-lg border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700/40 px-3 py-2 text-xs text-emerald-800 dark:text-emerald-200">
          {status}
        </div>
      )}
      <ul className="space-y-1.5">
        {rows.map((row) => (
          <li
            key={row.key}
            className="flex items-start justify-between gap-3 px-3 py-2 rounded-lg border border-slate-200/70 dark:border-slate-700/50 bg-white/40 dark:bg-slate-800/30"
          >
            <div className="min-w-0 flex-1">
              <div className="text-xs font-semibold text-slate-700 dark:text-slate-200">
                {row.label}
              </div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400">
                {row.kind === "worker" ? "Worker liveness" : "Connector cadence"}
                {row.ignore_active_hours ? ", monitored 24/7" : ", paused outside active hours"}
                . Default {row.default_threshold_hours}h.
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <input
                type="number"
                min={0}
                max={720}
                step={1}
                value={row.threshold_hours}
                onChange={(e) =>
                  updateRow(row.key, {
                    threshold_hours: Math.max(0, Math.min(720, Number(e.target.value) || 0)),
                  })
                }
                disabled={!row.alert_enabled}
                className="w-16 rounded-md border border-slate-300 dark:border-slate-600 bg-white/70 dark:bg-slate-800/70 px-2 py-1 text-xs text-right disabled:opacity-50"
              />
              <span className="text-[11px] text-slate-500 dark:text-slate-400">h</span>
              <label className="inline-flex items-center cursor-pointer ml-2">
                <span className="sr-only">{`Alert on ${row.label}`}</span>
                <input
                  type="checkbox"
                  checked={row.alert_enabled}
                  onChange={(e) => updateRow(row.key, { alert_enabled: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 dark:border-slate-600"
                />
                <span className="ml-1.5 text-[11px] text-slate-600 dark:text-slate-300">
                  {row.alert_enabled ? "On" : "Off"}
                </span>
              </label>
            </div>
          </li>
        ))}
      </ul>

      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-slate-700 dark:text-slate-200">
          Active hours
        </h4>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Stale-time only accumulates inside this window. Sources that catch up while you sleep do not page you the next morning. Worker rows ignore this and watch 24/7.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="time"
            value={activeStart}
            onChange={(e) => setActiveStart(e.target.value)}
            className="rounded-md border border-slate-300 dark:border-slate-600 bg-white/70 dark:bg-slate-800/70 px-2 py-1 text-xs"
          />
          <span className="text-xs text-slate-500 dark:text-slate-400">to</span>
          <input
            type="time"
            value={activeEnd}
            onChange={(e) => setActiveEnd(e.target.value)}
            className="rounded-md border border-slate-300 dark:border-slate-600 bg-white/70 dark:bg-slate-800/70 px-2 py-1 text-xs"
          />
          <input
            type="text"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Toronto"
            className="rounded-md border border-slate-300 dark:border-slate-600 bg-white/70 dark:bg-slate-800/70 px-2 py-1 text-xs min-w-[10rem]"
          />
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold px-3 py-1.5 disabled:opacity-50 transition-colors"
        >
          {busy ? "Saving..." : "Save source freshness"}
        </button>
      </div>
    </div>
  );
}

function UnknownSection({ label }: { label: string }) {
  return (
    <div className="text-center py-12">
      <div className="inline-flex w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-400 to-slate-500 items-center justify-center text-white shadow-md mb-3">
        <Cog className="w-6 h-6" strokeWidth={2.5} />
      </div>
      <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">
        {label || "Section"}
      </h2>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
        This settings section is not available.
      </p>
    </div>
  );
}
